import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  BarChart3,
  Archive,
  Bell,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  Download,
  Edit,
  ExternalLink,
  Eye,
  FileText,
  Mail,
  MapPin,
  Megaphone,
  MessageCircle,
  MoreHorizontal,
  Navigation,
  Phone,
  Plus,
  Search,
  Send,
  Settings,
  Trash2,
  Upload,
  Users,
  X,
} from "lucide-react";
import { api, formatErr } from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";

const sections = [
  { id: "dashboard", label: "Dashboard", Icon: BarChart3 },
  { id: "finder", label: "Lead Finder", Icon: Search },
  { id: "leads", label: "Leads", Icon: Users },
  { id: "lists", label: "Contact Lists", Icon: Mail },
  { id: "whatsapp", label: "WhatsApp", Icon: MessageCircle },
  { id: "templates", label: "WhatsApp Templates", Icon: FileText },
  { id: "campaigns", label: "Campaigns", Icon: Megaphone },
  { id: "more", label: "More", Icon: MoreHorizontal },
];

const sectionIds = new Set(sections.map((item) => item.id));

function marketingSectionPath(sectionId) {
  return sectionId && sectionId !== "dashboard" ? `/admin/marketing/${sectionId}` : "/admin/marketing";
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function crmPhoneKey(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 10) return `91${digits}`;
  return digits;
}

function whatsappActivityText(item) {
  const raw = item.raw || {};
  const type = String(item.messageType || raw.type || "message").toLowerCase();
  const media = raw[type] && typeof raw[type] === "object" ? raw[type] : {};
  const text = item.text || item.caption || media.caption || media.filename || item.templateName || "";
  if (text) return text;
  if (type === "image") return "Image message";
  if (type === "video") return "Video message";
  if (type === "document") return "Document message";
  if (item.direction === "out") return `Sent ${item.templateName || "WhatsApp template"}`;
  return "WhatsApp message";
}

function whatsappStatusTone(status = "") {
  const value = String(status).toLowerCase();
  if (["delivered", "read", "replied", "sent"].includes(value)) return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (["failed", "undelivered"].includes(value)) return "border-red-200 bg-red-50 text-red-700";
  return "border-blue-200 bg-blue-50 text-blue-700";
}

const defaultSearch = {
  category: "",
  keyword: "Restaurant",
  country: "India",
  state: "Maharashtra",
  city: "Vasai",
  area: "",
  pincode: "",
  radius_km: 10,
  max_results: 60,
  minimum_rating: "",
  has_phone: false,
  has_website: false,
  source: "google_places",
};

const RADIUS_OPTIONS = [1, 3, 5, 10, 15, 25, 50];
const ENV_GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const defaultTargetRegion = {
  presetName: "",
  country: "India",
  state: "Maharashtra",
  city: "",
  includedLocations: [],
  excludedLocations: [],
  defaultRadiusKm: 10,
  notes: "",
  isActive: true,
};

let googleMapsLoaderPromise;
let googleMapsLoaderKey = "";

function loadGoogleMapsScript(apiKey) {
  if (!apiKey) return Promise.reject(new Error("Google Maps API key missing. Please add key in settings or .env."));
  if (window.google?.maps?.places && googleMapsLoaderKey === apiKey) return Promise.resolve(window.google);
  if (googleMapsLoaderPromise && googleMapsLoaderKey === apiKey) return googleMapsLoaderPromise;
  googleMapsLoaderKey = apiKey;
  googleMapsLoaderPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector("script[data-rp-google-maps='true']");
    if (existing) {
      existing.remove();
    }
    window.gm_authFailure = () => {
      const error = new Error("Google Maps rejected this browser key. Enable Maps JavaScript API and Places API, turn on billing, and allow this website domain in HTTP referrers.");
      window.dispatchEvent(new CustomEvent("rp-google-maps-auth-error", { detail: error.message }));
      reject(error);
    };
    if (window.google?.maps && googleMapsLoaderKey !== apiKey) {
      delete window.google.maps;
    }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&v=weekly`;
    script.async = true;
    script.defer = true;
    script.dataset.rpGoogleMaps = "true";
    script.onload = () => {
      if (window.google?.maps?.places) resolve(window.google);
      else reject(new Error("Google Maps loaded without Places support. Enable Places API for this browser key."));
    };
    script.onerror = () => reject(new Error("Google Maps failed to load."));
    document.head.appendChild(script);
  });
  return googleMapsLoaderPromise;
}

function addressPartsFromGoogle(components = []) {
  const parts = { city: "", state: "", country: "", pincode: "" };
  components.forEach((component) => {
    const types = component.types || [];
    if (!parts.city && (types.includes("locality") || types.includes("postal_town") || types.includes("administrative_area_level_2"))) parts.city = component.long_name || "";
    if (!parts.state && types.includes("administrative_area_level_1")) parts.state = component.long_name || "";
    if (!parts.country && types.includes("country")) parts.country = component.long_name || "";
    if (!parts.pincode && types.includes("postal_code")) parts.pincode = component.long_name || "";
  });
  return parts;
}

function placeTypeFromGoogle(types = []) {
  if (types.includes("postal_code")) return "pincode";
  if (types.includes("locality") || types.includes("postal_town")) return "city";
  if (types.includes("sublocality") || types.includes("neighborhood")) return "area";
  if (types.includes("administrative_area_level_1")) return "state";
  if (types.includes("country")) return "country";
  if (types.includes("point_of_interest") || types.includes("establishment")) return "landmark";
  return "location";
}

function placeSuggestionRank(types = []) {
  const type = placeTypeFromGoogle(types);
  const ranks = { city: 0, area: 1, pincode: 2, landmark: 3, state: 4, country: 5 };
  return ranks[type] ?? 6;
}

function normalizeGooglePlace(place, fallbackRadius = 10) {
  const geometry = place.geometry || {};
  const location = geometry.location || {};
  const viewport = geometry.viewport;
  const latitude = typeof location.lat === "function" ? location.lat() : location.lat;
  const longitude = typeof location.lng === "function" ? location.lng() : location.lng;
  const viewportBounds = viewport
    ? {
        north: typeof viewport.getNorthEast === "function" ? viewport.getNorthEast().lat() : viewport.north,
        east: typeof viewport.getNorthEast === "function" ? viewport.getNorthEast().lng() : viewport.east,
        south: typeof viewport.getSouthWest === "function" ? viewport.getSouthWest().lat() : viewport.south,
        west: typeof viewport.getSouthWest === "function" ? viewport.getSouthWest().lng() : viewport.west,
      }
    : null;
  const parts = addressPartsFromGoogle(place.address_components || []);
  return {
    placeId: place.place_id || place.placeId || "",
    name: place.name || place.formatted_address || place.description || "",
    formattedAddress: place.formatted_address || place.description || "",
    latitude: latitude ?? null,
    longitude: longitude ?? null,
    city: parts.city,
    state: parts.state,
    country: parts.country,
    pincode: parts.pincode,
    type: placeTypeFromGoogle(place.types || []),
    radiusKm: Number(place.radiusKm || fallbackRadius || 10),
    viewportBounds,
  };
}

function locationCoverageAreaSqKm(item, fallbackRadius = 10) {
  const expanded = expandedViewportBounds(item);
  if (expanded) {
    const midLat = (Number(expanded.north) + Number(expanded.south)) / 2;
    const heightKm = Math.abs(Number(expanded.north) - Number(expanded.south)) * 111;
    const widthKm = Math.abs(Number(expanded.east) - Number(expanded.west)) * 111 * Math.cos((midLat * Math.PI) / 180);
    return Math.max(0, heightKm * widthKm);
  }
  const radius = Number(item.radiusKm || fallbackRadius || 10);
  return Math.PI * radius ** 2;
}

function leadFinderResultKey(row, index = 0) {
  const stable = row.googlePlaceId || row.placeId || row.phone || row.mobile || row.whatsapp || "";
  const nameAddress = [row.businessName, row.fullAddress || row.address, row.city].filter(Boolean).join("|");
  return `${stable || nameAddress || "lead"}::${index}`;
}

function uniqueParts(parts = []) {
  const seen = new Set();
  return parts
    .map((part) => String(part || "").trim())
    .filter((part) => {
      if (!part) return false;
      const key = part.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function backendKeyRejected(message = "") {
  const value = String(message || "").toLowerCase();
  return (
    value.includes("not authorized to use this api key") ||
    value.includes("empty referer") ||
    value.includes("referer restrictions cannot be used") ||
    value.includes("referrer restrictions cannot be used")
  );
}

function backendSearchCanFallback(message = "") {
  const value = String(message || "").toLowerCase();
  return backendKeyRejected(value) || value.includes("request timed out") || value.includes("backend service is not responding");
}

function googlePlacesStatusOk(status) {
  return status === window.google?.maps?.places?.PlacesServiceStatus?.OK;
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function browserTextSearch(service, request, maxRows = 20) {
  return new Promise((resolve, reject) => {
    const rows = [];
    let lastPagination = null;
    let nextPageRetries = 0;
    const handlePage = (results, status, pagination) => {
      if (googlePlacesStatusOk(status) || status === window.google?.maps?.places?.PlacesServiceStatus?.ZERO_RESULTS) {
        nextPageRetries = 0;
        rows.push(...(results || []));
        lastPagination = pagination;
        if (rows.length < maxRows && pagination?.hasNextPage) {
          setTimeout(() => pagination.nextPage(), 3200);
          return;
        }
        resolve(rows.slice(0, maxRows));
        return;
      }
      if (status === window.google?.maps?.places?.PlacesServiceStatus?.INVALID_REQUEST && rows.length && lastPagination?.hasNextPage && nextPageRetries < 4) {
        nextPageRetries += 1;
        setTimeout(() => lastPagination.nextPage(), 1800 + nextPageRetries * 1200);
        return;
      }
      if (status === window.google?.maps?.places?.PlacesServiceStatus?.INVALID_REQUEST && rows.length) {
        resolve(rows.slice(0, maxRows));
        return;
      }
      reject(new Error(`Google browser search failed: ${status || "unknown status"}`));
    };
    service.textSearch(request, handlePage);
  });
}

function browserPlaceDetails(service, placeId) {
  return new Promise((resolve) => {
    if (!placeId) return resolve({});
    service.getDetails(
      {
        placeId,
        fields: ["place_id", "name", "formatted_address", "formatted_phone_number", "international_phone_number", "website", "rating", "geometry", "types", "url", "address_components"],
      },
      (place, status) => {
        resolve(googlePlacesStatusOk(status) && place ? place : {});
      }
    );
  });
}

function browserPlaceToLead(row, details, fallbackCategory = "") {
  const place = details?.place_id ? details : row;
  const normalized = normalizeGooglePlace(place, 10);
  const phone = details?.international_phone_number || details?.formatted_phone_number || "";
  const types = details?.types || row?.types || [];
  return {
    businessName: details?.name || row?.name || "",
    category: fallbackCategory || types.slice(0, 2).join(", "),
    categoryName: fallbackCategory || types.slice(0, 2).join(", "),
    fullAddress: details?.formatted_address || row?.formatted_address || "",
    address: details?.formatted_address || row?.formatted_address || "",
    phone,
    mobile: phone,
    whatsapp: phone,
    whatsappNumber: phone,
    website: details?.website || "",
    googleRating: details?.rating || row?.rating || null,
    rating: details?.rating || row?.rating || null,
    mapLink: details?.url || (normalized.placeId ? `https://www.google.com/maps/place/?q=place_id:${normalized.placeId}` : ""),
    googlePlaceId: normalized.placeId,
    placeId: normalized.placeId,
    latitude: normalized.latitude,
    longitude: normalized.longitude,
    city: normalized.city,
    state: normalized.state,
    pincode: normalized.pincode,
    country: normalized.country,
    source: "google_places",
  };
}

function distanceKmBetween(lat1, lng1, lat2, lng2) {
  const nums = [lat1, lng1, lat2, lng2].map(Number);
  if (nums.some((value) => !Number.isFinite(value))) return null;
  const [aLat, aLng, bLat, bLng] = nums;
  const toRad = (value) => (value * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const s1 = Math.sin(dLat / 2) ** 2;
  const s2 = Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(s1 + s2), Math.sqrt(1 - s1 - s2));
}

const INDIA_BROWSER_SEARCH_CITIES = [
  "Mumbai",
  "Delhi",
  "Bengaluru",
  "Hyderabad",
  "Chennai",
  "Kolkata",
  "Pune",
  "Ahmedabad",
  "Jaipur",
  "Surat",
  "Lucknow",
  "Indore",
];

const STATE_BROWSER_SEARCH_CITIES = {
  maharashtra: ["Mumbai", "Pune", "Nagpur", "Nashik", "Thane", "Navi Mumbai", "Vasai-Virar", "Aurangabad"],
  gujarat: ["Ahmedabad", "Surat", "Vadodara", "Rajkot", "Bhavnagar", "Jamnagar"],
  delhi: ["New Delhi", "Connaught Place", "Karol Bagh", "Rohini", "Dwarka", "Saket"],
  karnataka: ["Bengaluru", "Mysuru", "Mangaluru", "Hubballi", "Belagavi"],
  "tamil nadu": ["Chennai", "Coimbatore", "Madurai", "Tiruchirappalli", "Salem"],
  telangana: ["Hyderabad", "Secunderabad", "Warangal", "Karimnagar"],
  rajasthan: ["Jaipur", "Jodhpur", "Udaipur", "Kota", "Ajmer"],
  "uttar pradesh": ["Lucknow", "Kanpur", "Noida", "Ghaziabad", "Varanasi", "Agra"],
  "west bengal": ["Kolkata", "Howrah", "Siliguri", "Durgapur", "Asansol"],
};

function browserSearchPoints(loc, maxResults) {
  const lat = Number(loc?.latitude);
  const lng = Number(loc?.longitude);
  const radiusKm = Number(loc?.radiusKm || 10);
  if (![lat, lng, radiusKm].every(Number.isFinite) || radiusKm <= 0) return [];
  const count = maxResults >= 60 ? 8 : maxResults >= 40 ? 4 : 0;
  if (!count) return [];
  const stepKm = Math.max(1, radiusKm * 0.55);
  const latDelta = stepKm / 111;
  const lngDelta = stepKm / Math.max(20, 111 * Math.cos((lat * Math.PI) / 180));
  const offsets = [
    [latDelta, 0],
    [-latDelta, 0],
    [0, lngDelta],
    [0, -lngDelta],
    [latDelta, lngDelta],
    [latDelta, -lngDelta],
    [-latDelta, lngDelta],
    [-latDelta, -lngDelta],
  ];
  return offsets.slice(0, count).map(([dLat, dLng]) => ({ lat: lat + dLat, lng: lng + dLng }));
}

function browserSearchTargets(payload, included, maxResults = 20) {
  const keyword = payload.businessKeyword || payload.keyword || payload.category;
  if (!included.length) {
    return [{
      query: uniqueParts([keyword, payload.city, payload.state, payload.country]).join(" "),
      location: null,
      point: null,
    }];
  }
  return included.flatMap((loc) => {
    const locType = String(loc.type || "").toLowerCase();
    if (locType === "country" && String(loc.name || loc.country || "").toLowerCase().includes("india")) {
      const count = maxResults >= 60 ? 12 : maxResults >= 40 ? 8 : 4;
      return INDIA_BROWSER_SEARCH_CITIES.slice(0, count).map((city) => ({
        query: uniqueParts([keyword, city, "India"]).join(" "),
        location: { ...loc, type: "country" },
        point: null,
      }));
    }
    if (locType === "state") {
      const stateName = String(loc.name || loc.state || "").trim();
      const cities = STATE_BROWSER_SEARCH_CITIES[stateName.toLowerCase()] || [];
      if (cities.length) {
        const count = maxResults >= 60 ? Math.min(cities.length, 8) : maxResults >= 40 ? Math.min(cities.length, 6) : Math.min(cities.length, 3);
        return cities.slice(0, count).map((city) => ({
          query: uniqueParts([keyword, city, stateName, loc.country || "India"]).join(" "),
          location: { ...loc, type: "state" },
          point: null,
        }));
      }
    }
    const query = uniqueParts([
      keyword,
      loc.type === "pincode" ? "" : loc.name,
      loc.city,
      loc.state,
      loc.country,
    ]).join(" ");
    if (!query) return [];
    return [
      { query, location: loc, point: null },
      ...browserSearchPoints(loc, maxResults).map((point) => ({ query, location: loc, point })),
    ];
  });
}

function browserLeadMatchesLocation(lead, loc) {
  if (!loc) return true;
  const locType = String(loc.type || "").toLowerCase();
  const address = [lead.address, lead.fullAddress, lead.city, lead.state, lead.country].filter(Boolean).join(" ").toLowerCase();
  if (locType === "country") {
    const country = String(loc.name || loc.country || "").toLowerCase();
    return !country || address.includes(country);
  }
  if (locType === "state") {
    const state = String(loc.name || loc.state || "").toLowerCase();
    return !state || address.includes(state);
  }
  if (locType === "city") {
    const city = String(loc.name || loc.city || "").toLowerCase();
    return !city || address.includes(city);
  }
  const locLat = Number(loc.latitude);
  const locLng = Number(loc.longitude);
  const leadLat = Number(lead.latitude);
  const leadLng = Number(lead.longitude);
  if ([locLat, locLng, leadLat, leadLng].every(Number.isFinite)) {
    const radius = Number(loc.radiusKm || 10);
    const distance = distanceKmBetween(locLat, locLng, leadLat, leadLng);
    if (distance !== null) return distance <= radius;
  }
  const names = [loc.name, loc.city, loc.state, loc.country].filter(Boolean).map((item) => String(item).toLowerCase());
  return names.some((name) => address.includes(name));
}

async function browserLeadFinderSearch(payload) {
  if (!window.google?.maps?.places) throw new Error("Google Maps browser search is not ready yet.");
  const node = document.createElement("div");
  const service = new window.google.maps.places.PlacesService(node);
  const included = payload.targetRegion?.includedLocations?.length ? payload.targetRegion.includedLocations : payload.includedLocations || [];
  const keyword = String(payload.businessKeyword || payload.keyword || payload.category || "").trim();
  const maxResults = Math.max(1, Math.min(Number(payload.max_results || 20), 60));
  const targets = browserSearchTargets(payload, included, maxResults);
  const seen = new Set();
  const leads = [];
  let rawCandidates = 0;
  const perTargetLimit = targets.length > 1 ? Math.max(5, Math.ceil(maxResults / targets.length) + 3) : maxResults;
  for (const target of targets) {
    if (leads.length >= maxResults) break;
    const loc = target.location;
    const locLat = Number(target.point?.lat ?? loc?.latitude);
    const locLng = Number(target.point?.lng ?? loc?.longitude);
    const request = { query: target.query };
    if (Number.isFinite(locLat) && Number.isFinite(locLng)) {
      request.location = new window.google.maps.LatLng(locLat, locLng);
      request.radius = Math.max(100, Math.min(Number(loc.radiusKm || payload.radius_km || 10) * 1000, 50000));
    }
    const rows = await browserTextSearch(service, request, target.point ? 20 : perTargetLimit);
    rawCandidates += rows.length;
    for (const row of rows) {
      if (leads.length >= maxResults) break;
      const key = row.place_id || row.name;
      if (!key || seen.has(key)) continue;
      seen.add(key);
      const details = await browserPlaceDetails(service, row.place_id);
      const lead = browserPlaceToLead(row, details, keyword);
      if (payload.minimum_rating && lead.rating && Number(lead.rating) < Number(payload.minimum_rating)) continue;
      if (payload.has_phone && !lead.phone) continue;
      if (payload.has_website && !lead.website) continue;
      if (!browserLeadMatchesLocation(lead, loc)) continue;
      leads.push(lead);
    }
  }
  return { results: leads, total: leads.length, rawCandidates, duplicatesSkipped: 0, queries: targets.map((item) => item.query), source: "google_places_browser", manualSaveRequired: true };
}

const defaultCategoryDraft = {
  categoryName: "",
  categoryType: "Custom",
  country: "India",
  state: "",
  city: "",
  area: "",
  pincode: "",
  notes: "",
  isActive: true,
};

const defaultTemplateDraft = {
  templateName: "",
  category: "Marketing",
  languageCode: "en_US",
  messageType: "Text",
  headerType: "None",
  headerText: "",
  mediaUrl: "",
  metaHeaderHandle: "",
  metaHeaderError: "",
  bodyText: "",
  bodyPreview: "",
  footerText: "",
  buttons: [{ type: "QUICK_REPLY", text: "Request Demo", phoneNumber: "", url: "" }],
  variables: ["{{1}}", "{{2}}", "{{3}}"],
  variableExamples: [],
  status: "DRAFT",
};

// Detects {{1}}, {{2}}, ... in the body text, in order - used to render one
// example-value input per placeholder so Meta gets a realistic sample of
// each variable's real shape (a date, an amount, a name, etc.) instead of
// generic filler text. Meta appears to lightly validate that a template's
// real send-time values aren't wildly different in format from the example
// given at approval time, so a mismatched example (e.g. "Item Six" for what
// is actually always a currency amount) can cause sends to fail even after
// approval.
function detectTemplateVariableCount(bodyText) {
  const indexes = new Set();
  const matches = String(bodyText || "").matchAll(/\{\{\s*(\d+)\s*\}\}/g);
  for (const match of matches) indexes.add(Number(match[1]));
  return indexes.size ? Math.max(...indexes) : 0;
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6B7280]">{label}</span>
      {children}
    </label>
  );
}

function StatCard({ label, value, Icon }) {
  return (
    <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6B7280]">{label}</div>
          <div className="mt-2 text-2xl font-extrabold text-[#111827]">{value ?? 0}</div>
        </div>
        <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-[#111827] text-white">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

function Pill({ children, tone = "gray" }) {
  const tones = {
    gray: "border-[#E5E7EB] bg-[#F9FAFB] text-[#374151]",
    green: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    red: "border-red-200 bg-red-50 text-red-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
  };
  return <span className={`inline-flex items-center rounded-sm border px-2 py-1 text-xs font-semibold ${tones[tone]}`}>{children}</span>;
}

function campaignErrorMessage(err) {
  const message = formatErr(err);
  if (message.includes("132001") || message.toLowerCase().includes("template name does not exist")) {
    return `${message} This template is not available under the active WhatsApp Business Account/language. Go to WhatsApp Templates, click Sync Templates, then recreate or submit this template under RP Infotech WABA.`;
  }
  return message;
}

export default function AdminMarketingCRM() {
  const navigate = useNavigate();
  const { tab } = useParams();
  const [searchParams] = useSearchParams();
  const routeSection = sectionIds.has(tab) ? tab : sectionIds.has(searchParams.get("tab")) ? searchParams.get("tab") : "dashboard";
  const [section, setSectionState] = useState(routeSection);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("");
  const [searchForm, setSearchForm] = useState(defaultSearch);
  const [searchResults, setSearchResults] = useState([]);
  const [searchSummary, setSearchSummary] = useState(null);
  const [saveListId, setSaveListId] = useState("");
  const [saveNewListName, setSaveNewListName] = useState("");
  const [selectedPlaces, setSelectedPlaces] = useState({});
  const [leads, setLeads] = useState([]);
  const [leadCategories, setLeadCategories] = useState([]);
  const [targetRegions, setTargetRegions] = useState([]);
  const [targetRegion, setTargetRegion] = useState(defaultTargetRegion);
  const [selectedTargetRegionId, setSelectedTargetRegionId] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [locationMode, setLocationMode] = useState("include");
  const [lists, setLists] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignActionMessage, setCampaignActionMessage] = useState("");
  const [templates, setTemplates] = useState([]);
  const [media, setMedia] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [deliveryLogs, setDeliveryLogs] = useState([]);
  const [reports, setReports] = useState({});
  const [settings, setSettings] = useState({});
  const [whatsappVerify, setWhatsappVerify] = useState(null);
  const [leadDraft, setLeadDraft] = useState({ businessName: "", mobile: "", whatsappNumber: "", category: "", categoryId: "", city: "", whatsappConsent: "YES", status: "New" });
  const [categoryDraft, setCategoryDraft] = useState(defaultCategoryDraft);
  const [editingCategoryId, setEditingCategoryId] = useState("");
  const [listDraft, setListDraft] = useState({ name: "", description: "", leadIds: [] });
  const [editingListId, setEditingListId] = useState("");
  const [campaignDraft, setCampaignDraft] = useState({
    campaignName: "Daily offer campaign",
    contactListId: "",
    contactListIds: [],
    messageCategory: "Marketing",
    messageType: "Image",
    templateName: "",
    languageCode: "en_US",
    mediaUrl: "",
    caption: "",
    ctaButton: "Call Now",
    testNumber: "",
    templateVariables: {
      customerName: "",
      businessName: "RP Signage",
      productName: "",
      offer: "",
      websiteLink: "https://rpsignage.com",
      phoneNumber: "9225140406",
    },
    scheduleMode: "Send Now",
    scheduledDateTime: "",
    frequency: "One time",
    status: "Draft",
  });
  const [templateDraft, setTemplateDraft] = useState(defaultTemplateDraft);
  const [templateSubmitting, setTemplateSubmitting] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState({ phoneNumberId: "", wabaId: "", metaAppId: "", accessToken: "", apiVersion: "v23.0", webhookVerifyToken: "", webhookUrl: "", googlePlacesApiKey: "", googleMapsBrowserApiKey: "" });
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [leadFilters, setLeadFilters] = useState({ search: "", category: "", city: "", status: "", consent: "", rating: "", source: "" });
  const [leadSort, setLeadSort] = useState("createdAt_desc");
  const [leadPage, setLeadPage] = useState(1);
  const [leadPageSize, setLeadPageSize] = useState(25);
  const [detailLead, setDetailLead] = useState(null);
  const [listModalOpen, setListModalOpen] = useState(false);
  const [bulkListId, setBulkListId] = useState("");
  const [bulkNewListName, setBulkNewListName] = useState("");
  const [bulkStatus, setBulkStatus] = useState("Contacted");
  const [bulkConsent, setBulkConsent] = useState("YES");
  const [bulkCategoryId, setBulkCategoryId] = useState("");
  const [activeWhatsappChat, setActiveWhatsappChat] = useState("");
  const [whatsappReplyText, setWhatsappReplyText] = useState("");

  useEffect(() => {
    setSectionState(routeSection);
  }, [routeSection]);

  function setSection(nextSection) {
    const safeSection = sectionIds.has(nextSection) ? nextSection : "dashboard";
    setSectionState(safeSection);
    navigate(marketingSectionPath(safeSection), { replace: false });
  }

  const selectedSearchResults = useMemo(
    () => searchResults.filter((row, index) => selectedPlaces[leadFinderResultKey(row, index)]),
    [searchResults, selectedPlaces],
  );
  const searchResultKeys = useMemo(() => searchResults.map((row, index) => leadFinderResultKey(row, index)), [searchResults]);
  const allSearchResultsSelected = searchResultKeys.length > 0 && searchResultKeys.every((key) => selectedPlaces[key]);
  const toggleAllSearchResults = (checked) => {
    setSelectedPlaces((current) => {
      const next = { ...current };
      searchResultKeys.forEach((key) => {
        if (checked) next[key] = true;
        else delete next[key];
      });
      return next;
    });
  };

  const cityOptions = useMemo(() => [...new Set(leads.map((lead) => lead.city).filter(Boolean))].sort(), [leads]);
  const sourceOptions = useMemo(() => [...new Set(leads.map((lead) => lead.source).filter(Boolean))].sort(), [leads]);

  const filteredLeads = useMemo(() => {
    const q = leadFilters.search.trim().toLowerCase();
    return leads.filter((lead) => {
      const rating = Number(lead.rating || lead.googleRating || 0);
      const haystack = [lead.businessName, lead.mobile, lead.whatsappNumber, lead.categoryName, lead.category, lead.city, lead.state, lead.source].join(" ").toLowerCase();
      if (q && !haystack.includes(q)) return false;
      if (lead.archived) return false;
      if (leadFilters.city && lead.city !== leadFilters.city) return false;
      if (leadFilters.status && lead.status !== leadFilters.status) return false;
      if (leadFilters.consent && lead.whatsappConsent !== leadFilters.consent) return false;
      if (leadFilters.source && lead.source !== leadFilters.source) return false;
      if (leadFilters.rating && rating < Number(leadFilters.rating)) return false;
      return true;
    });
  }, [leads, leadFilters]);

  const sortedLeads = useMemo(() => {
    const [key, dir] = leadSort.split("_");
    const sign = dir === "asc" ? 1 : -1;
    return [...filteredLeads].sort((a, b) => {
      const av = key === "googleRating" ? Number(a.googleRating || 0) : String(a[key] || "").toLowerCase();
      const bv = key === "googleRating" ? Number(b.googleRating || 0) : String(b[key] || "").toLowerCase();
      if (av > bv) return sign;
      if (av < bv) return -sign;
      return 0;
    });
  }, [filteredLeads, leadSort]);

  const totalLeadPages = Math.max(1, Math.ceil(sortedLeads.length / leadPageSize));
  const visibleLeads = useMemo(() => {
    const safePage = Math.min(leadPage, totalLeadPages);
    const start = (safePage - 1) * leadPageSize;
    return sortedLeads.slice(start, start + leadPageSize);
  }, [sortedLeads, leadPage, leadPageSize, totalLeadPages]);
  const selectedLeadRows = useMemo(() => leads.filter((lead) => selectedLeadIds.includes(lead.id)), [leads, selectedLeadIds]);
  const campaignRecipientSummary = useMemo(() => {
    const selectedIds = [...new Set([...(campaignDraft.contactListIds || []), campaignDraft.contactListId].filter(Boolean))];
    const selectedLists = lists.filter((item) => selectedIds.includes(item.id));
    const listLeadIds = [...new Set(selectedLists.flatMap((list) => list.leadIds || []))];
    const rows = leads.filter((lead) => listLeadIds.includes(lead.id));
    const optedIn = rows.filter((lead) => lead.whatsappConsent === "YES" && (lead.whatsappNumber || lead.mobile)).length;
    const missingWhatsapp = rows.filter((lead) => !(lead.whatsappNumber || lead.mobile)).length;
    const noConsent = rows.filter((lead) => (lead.whatsappNumber || lead.mobile) && lead.whatsappConsent !== "YES").length;
    const skipped = rows.length - optedIn;
    return { list: selectedLists[0] || null, lists: selectedLists, total: rows.length, optedIn, skipped, noConsent, missingWhatsapp, estimated: optedIn };
  }, [campaignDraft.contactListId, campaignDraft.contactListIds, lists, leads]);

  const whatsappConversations = useMemo(() => {
    const leadByPhone = new Map();
    leads.forEach((lead) => {
      const key = lead.phone_key || crmPhoneKey(lead.whatsappNumber || lead.mobile);
      if (key) leadByPhone.set(key, lead);
    });
    const grouped = new Map();
    const addItem = (item) => {
      const key = item.phone_key || crmPhoneKey(item.phone);
      if (!key) return;
      const lead = leadByPhone.get(key);
      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          phone: item.phone || key,
          title: lead?.businessName || item.phone || key,
          lead,
          messages: [],
          latestAt: item.createdAt || item.updatedAt || "",
        });
      }
      const group = grouped.get(key);
      group.messages.push(item);
      const stamp = item.updatedAt || item.createdAt || "";
      if (!group.latestAt || new Date(stamp).getTime() > new Date(group.latestAt).getTime()) group.latestAt = stamp;
    };
    inbox.forEach((msg) => {
      addItem({
        ...msg,
        direction: "in",
        status: "received",
        createdAt: msg.createdAt,
        phone: msg.phone || msg.phone_key,
      });
    });
    deliveryLogs.forEach((log) => {
      addItem({
        ...log,
        direction: "out",
        phone: log.phone || log.phone_key,
        createdAt: log.createdAt || log.updatedAt,
      });
    });
    return [...grouped.values()]
      .map((group) => ({
        ...group,
        messages: group.messages.sort((a, b) => new Date(a.createdAt || a.updatedAt || 0).getTime() - new Date(b.createdAt || b.updatedAt || 0).getTime()),
      }))
      .sort((a, b) => new Date(b.latestAt || 0).getTime() - new Date(a.latestAt || 0).getTime());
  }, [inbox, deliveryLogs, leads]);

  useEffect(() => {
    if (!whatsappConversations.length) {
      setActiveWhatsappChat("");
      return;
    }
    if (!activeWhatsappChat || !whatsappConversations.some((chat) => chat.key === activeWhatsappChat)) {
      setActiveWhatsappChat(whatsappConversations[0].key);
    }
  }, [activeWhatsappChat, whatsappConversations]);

  async function loadAll() {
    setLoading(true);
    try {
      const [leadRes, categoryRes, regionRes, listRes, campRes, tmplRes, mediaRes, inboxRes, reportRes, settingsRes, deliveryRes] = await Promise.all([
        api.get("/marketing-crm/leads"),
        api.get("/marketing-crm/lead-categories"),
        api.get("/marketing-crm/target-regions"),
        api.get("/marketing-crm/contact-lists"),
        api.get("/marketing-crm/focused-campaigns"),
        api.get("/marketing-crm/message-templates"),
        api.get("/marketing-crm/media-library"),
        api.get("/marketing-crm/inbox"),
        api.get("/marketing-crm/reports"),
        api.get("/marketing-crm/settings"),
        api.get("/marketing-crm/delivery-logs"),
      ]);
      setLeads(leadRes.data || []);
      setLeadCategories(categoryRes.data || []);
      setTargetRegions(regionRes.data || []);
      setLists(listRes.data || []);
      setCampaigns(campRes.data || []);
      setTemplates(tmplRes.data || []);
      setMedia(mediaRes.data || []);
      setInbox(inboxRes.data || []);
      setDeliveryLogs(deliveryRes.data || []);
      setReports(reportRes.data || {});
      setSettings(settingsRes.data || {});
      setSettingsDraft((cur) => ({ ...cur, ...(settingsRes.data || {}), accessToken: "", googlePlacesApiKey: "", googleMapsBrowserApiKey: "" }));
    } catch (err) {
      setNotice(formatErr(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
  }, []);

  async function refreshDeliveryLogs() {
    try {
      const { data } = await api.get("/marketing-crm/delivery-logs");
      setDeliveryLogs(data || []);
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function refreshWhatsappActivity() {
    try {
      const [inboxRes, deliveryRes, settingsRes] = await Promise.all([
        api.get("/marketing-crm/inbox"),
        api.get("/marketing-crm/delivery-logs"),
        api.get("/marketing-crm/settings"),
      ]);
      setInbox(inboxRes.data || []);
      setDeliveryLogs(deliveryRes.data || []);
      setSettings(settingsRes.data || {});
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  useEffect(() => {
    if (section !== "campaigns") return undefined;
    const timer = setInterval(refreshDeliveryLogs, 15000);
    return () => clearInterval(timer);
  }, [section]);

  useEffect(() => {
    if (section !== "whatsapp") return undefined;
    const timer = setInterval(refreshWhatsappActivity, 15000);
    return () => clearInterval(timer);
  }, [section]);

  function addTargetLocation(location, mode = locationMode) {
    const radiusKm = Number(location.radiusKm || targetRegion.defaultRadiusKm || searchForm.radius_km || 10);
    const item = {
      name: location.name || location.formattedAddress,
      type: location.type || "custom",
      latitude: location.latitude ?? null,
      longitude: location.longitude ?? null,
      radiusKm,
      viewportBounds: location.viewportBounds || null,
      placeId: location.placeId || "",
      formattedAddress: location.formattedAddress || "",
      city: location.city || "",
      state: location.state || "",
      country: location.country || "",
      pincode: location.pincode || "",
    };
    if (!item.name) return;
    const key = mode === "exclude" ? "excludedLocations" : "includedLocations";
    setTargetRegion((cur) => ({
      ...cur,
      country: location.country || cur.country,
      state: location.state || cur.state,
      city: location.city || (location.type === "city" ? location.name : cur.city),
      [key]: [...cur[key].filter((row) => (row.placeId || row.name).toLowerCase() !== (item.placeId || item.name).toLowerCase()), item],
    }));
    setLocationSearch("");
  }

  function removeTargetLocation(name, mode) {
    const key = mode === "exclude" ? "excludedLocations" : "includedLocations";
    setTargetRegion((cur) => ({ ...cur, [key]: cur[key].filter((item) => (item.placeId || item.name) !== name) }));
  }

  function updateTargetLocation(name, mode, patch) {
    const key = mode === "exclude" ? "excludedLocations" : "includedLocations";
    setTargetRegion((cur) => ({ ...cur, [key]: cur[key].map((item) => (item.placeId || item.name) === name ? { ...item, ...patch } : item) }));
  }

  function applyTargetPreset(regionId) {
    setSelectedTargetRegionId(regionId);
    const region = targetRegions.find((item) => item.id === regionId);
    if (!region) return;
    setTargetRegion({
      presetName: region.presetName || "",
      country: region.country || "India",
      state: region.state || "",
      city: region.city || "",
      includedLocations: region.includedLocations || [],
      excludedLocations: region.excludedLocations || [],
      defaultRadiusKm: region.defaultRadiusKm || 10,
      notes: region.notes || "",
      isActive: region.isActive !== false,
    });
    setSearchForm((cur) => ({ ...cur, country: region.country || cur.country, state: region.state || cur.state, city: region.city || cur.city, radius_km: region.defaultRadiusKm || cur.radius_km }));
  }

  async function saveTargetRegionPreset() {
    if (!targetRegion.presetName.trim()) return setNotice("Enter a preset name first.");
    if (!targetRegion.includedLocations.length) return setNotice("Add at least one included location.");
    try {
      const payload = { ...targetRegion, defaultRadiusKm: Number(targetRegion.defaultRadiusKm || searchForm.radius_km || 10) };
      if (selectedTargetRegionId) {
        await api.put(`/marketing-crm/target-regions/${selectedTargetRegionId}`, payload);
        setNotice("Target region preset updated.");
      } else {
        const { data } = await api.post("/marketing-crm/target-regions", payload);
        setSelectedTargetRegionId(data.id);
        setNotice("Target region preset saved.");
      }
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function deleteTargetRegionPreset() {
    if (!selectedTargetRegionId) return setNotice("Select a preset first.");
    const region = targetRegions.find((item) => item.id === selectedTargetRegionId);
    if (!window.confirm(`Delete target region preset "${region?.presetName || "selected preset"}"?`)) return;
    try {
      await api.delete(`/marketing-crm/target-regions/${selectedTargetRegionId}`);
      setSelectedTargetRegionId("");
      setTargetRegion(defaultTargetRegion);
      setNotice("Target region preset deleted.");
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function searchPlaces() {
    setNotice("");
    setLoading(true);
    const payload = {
      ...searchForm,
      targetRegionId: selectedTargetRegionId,
      targetRegion: selectedTargetRegionId ? null : {
        country: targetRegion.country,
        state: targetRegion.state,
        city: targetRegion.city,
        includedLocations: targetRegion.includedLocations,
        excludedLocations: targetRegion.excludedLocations,
        defaultRadiusKm: Number(targetRegion.defaultRadiusKm || searchForm.radius_km || 10),
      },
      businessKeyword: searchForm.keyword,
      includedLocations: targetRegion.includedLocations,
      excludedLocations: targetRegion.excludedLocations,
      radius_km: Number(searchForm.radius_km || 10),
      max_results: Number(searchForm.max_results || 60),
      minimum_rating: searchForm.minimum_rating ? Number(searchForm.minimum_rating) : null,
    };
    try {
      if (!payload.targetRegionId && !payload.targetRegion.includedLocations.length) {
        return setNotice("Add at least one included target location or choose a saved preset.");
      }
      const { data } = await api.post("/marketing-crm/finder/search-by-region", payload, { timeout: 60000 });
      setSearchResults(data.results || []);
      setSelectedPlaces({});
      setSearchSummary(data);
      const searched = Array.isArray(data.queries) && data.queries.length ? ` Query: ${data.queries[0]}` : "";
      const rawCount = Number(data.rawCandidates || 0);
      const filterHint = !data.total && rawCount ? " Google returned places, but filters/details removed them. Try unchecking phone/website filters or use a wider area." : "";
      const noGoogleHint = !data.total && !rawCount ? " Google returned no places for this keyword and region. Try a wider area or a simpler keyword." : "";
      setNotice(`${data.total || 0} results found. ${rawCount} Google candidates checked. ${data.duplicatesSkipped || 0} duplicates skipped.${filterHint}${noGoogleHint}${searched}`);
    } catch (err) {
      const message = formatErr(err);
      if (backendSearchCanFallback(message) && window.google?.maps?.places) {
        try {
          const data = await browserLeadFinderSearch(payload);
          setSearchResults(data.results || []);
          setSelectedPlaces({});
          setSearchSummary(data);
          const searched = Array.isArray(data.queries) && data.queries.length ? ` Query: ${data.queries[0]}` : "";
          setNotice(`${data.total || 0} results found using browser Google key. Backend search did not complete in time or the server key is not authorized.${searched}`);
          return;
        } catch (fallbackErr) {
          setNotice(`${message}. Browser fallback also failed: ${formatErr(fallbackErr)}`);
          return;
        }
      }
      setNotice(message);
    } finally {
      setLoading(false);
    }
  }

  async function saveSelectedLeads() {
    if (!selectedSearchResults.length) return setNotice("Select at least one lead first.");
    if (!saveListId && !saveNewListName.trim()) return setNotice("Select a contact list or enter a new contact list name.");
    setLoading(true);
    try {
      const leadsToSave = selectedSearchResults.map((row) => ({
        businessName: row.businessName,
        mobile: row.mobile || row.phone || "",
        whatsappNumber: row.whatsappNumber || row.whatsapp || row.phone || "",
        whatsapp: row.whatsapp || row.whatsappNumber || row.phone || "",
        website: row.website || "",
        category: row.categoryName || row.category || searchForm.keyword,
        categoryName: row.categoryName || row.category || searchForm.keyword,
        categoryId: "",
        country: row.country || targetRegion.country || searchForm.country,
        area: row.area || targetRegion.includedLocations[0]?.name || searchForm.area,
        address: row.fullAddress || row.address || "",
        fullAddress: row.fullAddress || row.address || "",
        city: row.city || targetRegion.city || searchForm.city,
        state: row.state || targetRegion.state || searchForm.state,
        pincode: row.pincode || searchForm.pincode,
        latitude: row.latitude,
        longitude: row.longitude,
        source: "google_places",
        googlePlaceId: row.googlePlaceId || row.placeId || "",
        placeId: row.placeId || row.googlePlaceId || "",
        googleRating: row.googleRating || row.rating || null,
        rating: row.rating || row.googleRating || null,
        whatsappConsent: "YES",
        consent: "Opt-in",
        consentSource: "default_opt_in",
        status: "New",
        tags: [searchForm.keyword, searchForm.category, targetRegion.city, targetRegion.includedLocations[0]?.name].filter(Boolean),
      }));
      const { data } = await api.post("/marketing-crm/leads/save-selected", {
        leads: leadsToSave,
        contactListId: saveListId,
        contactListName: saveNewListName.trim(),
      });
      const reusedNames = (data.skippedDuplicates || []).filter((item) => item.reason === "already_saved").map((item) => item.businessName).filter(Boolean).slice(0, 3);
      const skippedNames = (data.skippedDuplicates || []).filter((item) => item.reason !== "already_saved").map((item) => item.businessName).filter(Boolean).slice(0, 3);
      const reusedHint = reusedNames.length ? ` Already saved and added: ${reusedNames.join(", ")}${(data.reusedCount || 0) > reusedNames.length ? "..." : ""}.` : "";
      const skippedHint = skippedNames.length ? ` Could not add duplicates: ${skippedNames.join(", ")}${(data.skippedCount || 0) > skippedNames.length ? "..." : ""}.` : "";
      const listHint = data.contactList?.name ? ` Added to contact list: ${data.contactList.name}.` : "";
      setNotice(`Selected ${data.requestedCount || leadsToSave.length}. New ${data.savedCount || 0}. Already saved added ${data.reusedCount || 0}. Skipped ${data.skippedCount || 0}.${listHint}${reusedHint}${skippedHint}`);
      setSaveNewListName("");
      await loadAll();
      setSection("leads");
    } catch (err) {
      setNotice(formatErr(err));
    } finally {
      setLoading(false);
    }
  }

  async function createLead() {
    try {
      await api.post("/marketing-crm/leads", {
        ...leadDraft,
        whatsappConsent: "YES",
        consent: "Opt-in",
        consentSource: "default_opt_in",
      });
      setLeadDraft({ businessName: "", mobile: "", whatsappNumber: "", category: "", categoryId: "", city: "", whatsappConsent: "YES", status: "New" });
      setNotice("Lead added.");
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function updateLead(id, patch) {
    try {
      await api.put(`/marketing-crm/leads/${id}`, patch);
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function deleteLead(id) {
    if (!window.confirm("Are you sure you want to delete this lead? This cannot be undone.")) return;
    try {
      await api.delete(`/marketing-crm/leads/${id}`);
      setSelectedLeadIds((ids) => ids.filter((leadId) => leadId !== id));
      setNotice("Lead deleted.");
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function saveContactList() {
    if (!listDraft.name.trim()) return setNotice("Contact list name is required.");
    try {
      if (editingListId) {
        await api.put(`/marketing-crm/contact-lists/${editingListId}`, listDraft);
        setNotice("Contact list updated.");
      } else {
        await api.post("/marketing-crm/contact-lists", listDraft);
        setNotice("Contact list created.");
      }
      setListDraft({ name: "", description: "", leadIds: [] });
      setEditingListId("");
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function deleteList(list) {
    if (!list?.id) return setNotice("Contact list id missing.");
    if (!window.confirm(`Delete contact list "${list.name}"? Leads will stay saved.`)) return;
    try {
      await api.delete(`/marketing-crm/contact-lists/${list.id}`);
      if (bulkListId === list.id) setBulkListId("");
      if (campaignDraft.contactListId === list.id || (campaignDraft.contactListIds || []).includes(list.id)) {
        setCampaignDraft((cur) => ({ ...cur, contactListId: cur.contactListId === list.id ? "" : cur.contactListId, contactListIds: (cur.contactListIds || []).filter((id) => id !== list.id) }));
      }
      setNotice("Contact list deleted.");
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function deleteContactListsBulk(listIds = []) {
    const ids = [...new Set(listIds.filter(Boolean))];
    if (!ids.length) return setNotice("Select at least one contact list.");
    if (!window.confirm(`Delete ${ids.length} selected contact list(s)? Leads will stay saved.`)) return;
    try {
      await Promise.all(ids.map((id) => api.delete(`/marketing-crm/contact-lists/${id}`)));
      if (ids.includes(bulkListId)) setBulkListId("");
      if (ids.includes(campaignDraft.contactListId) || (campaignDraft.contactListIds || []).some((id) => ids.includes(id))) {
        setCampaignDraft((cur) => ({ ...cur, contactListId: ids.includes(cur.contactListId) ? "" : cur.contactListId, contactListIds: (cur.contactListIds || []).filter((id) => !ids.includes(id)) }));
      }
      if (ids.includes(editingListId)) {
        setEditingListId("");
        setListDraft({ name: "", description: "", leadIds: [] });
      }
      setNotice(`${ids.length} contact list(s) deleted.`);
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function removeContactsFromList(listId, leadIds = []) {
    const ids = [...new Set(leadIds.filter(Boolean))];
    const list = lists.find((item) => item.id === listId);
    if (!list) return setNotice("Open a contact list first.");
    if (!ids.length) return setNotice("Select at least one contact.");
    if (!window.confirm(`Remove ${ids.length} selected contact(s) from "${list.name}"? Leads will stay saved.`)) return;
    try {
      const nextLeadIds = (list.leadIds || []).filter((id) => !ids.includes(id));
      await api.put(`/marketing-crm/contact-lists/${list.id}`, { leadIds: nextLeadIds });
      setNotice(`${ids.length} contact(s) removed from ${list.name}.`);
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function moveContactsToList(fromListId, toListId, leadIds = []) {
    const ids = [...new Set(leadIds.filter(Boolean))];
    const fromList = lists.find((item) => item.id === fromListId);
    const toList = lists.find((item) => item.id === toListId);
    if (!fromList) return setNotice("Open a contact list first.");
    if (!toList) return setNotice("Select target contact list.");
    if (fromList.id === toList.id) return setNotice("Choose a different contact list to move into.");
    if (!ids.length) return setNotice("Select at least one contact.");
    try {
      const sourceLeadIds = (fromList.leadIds || []).filter((id) => !ids.includes(id));
      const targetLeadIds = [...new Set([...(toList.leadIds || []), ...ids])];
      await Promise.all([
        api.put(`/marketing-crm/contact-lists/${fromList.id}`, { leadIds: sourceLeadIds }),
        api.put(`/marketing-crm/contact-lists/${toList.id}`, { leadIds: targetLeadIds }),
      ]);
      setNotice(`${ids.length} contact(s) moved to ${toList.name}.`);
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function addSelectedToContactList() {
    if (!selectedLeadIds.length) return setNotice("Select leads first.");
    try {
      let targetId = bulkListId;
      let targetName = lists.find((list) => list.id === bulkListId)?.name || bulkNewListName.trim();
      if (!targetId) {
        if (!bulkNewListName.trim()) return setNotice("Select a list or enter a new list name.");
        const { data } = await api.post("/marketing-crm/contact-lists", { name: bulkNewListName.trim(), leadIds: [] });
        targetId = data.id;
        targetName = data.name;
      }
      const target = lists.find((list) => list.id === targetId);
      const merged = [...new Set([...(target?.leadIds || []), ...selectedLeadIds])];
      await api.put(`/marketing-crm/contact-lists/${targetId}`, { leadIds: merged });
      setNotice(`${selectedLeadIds.length} leads added to ${targetName} list.`);
      setListModalOpen(false);
      setBulkListId("");
      setBulkNewListName("");
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function changeSelectedStatus() {
    if (!selectedLeadIds.length) return setNotice("Select leads first.");
    try {
      await api.post("/marketing-crm/leads/bulk-update", { leadIds: selectedLeadIds, status: bulkStatus });
      setNotice(`${selectedLeadIds.length} leads updated to ${bulkStatus}.`);
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function deleteSelectedLeads() {
    if (!selectedLeadIds.length) return setNotice("Select leads first.");
    if (!window.confirm(`Delete ${selectedLeadIds.length} selected leads? This cannot be undone.`)) return;
    try {
      await api.post("/marketing-crm/leads/bulk-delete", { leadIds: selectedLeadIds });
      setNotice(`${selectedLeadIds.length} leads deleted.`);
      setSelectedLeadIds([]);
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function changeSelectedConsent() {
    if (!selectedLeadIds.length) return setNotice("Select leads first.");
    try {
      await api.post("/marketing-crm/leads/bulk-update", { leadIds: selectedLeadIds, consent: bulkConsent });
      setNotice(`${selectedLeadIds.length} leads consent updated.`);
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function markCampaignListOptIn() {
    const selectedLists = campaignRecipientSummary.lists || [];
    const leadIds = [...new Set(selectedLists.flatMap((list) => list.leadIds || []))].filter(Boolean);
    if (!selectedLists.length || !leadIds.length) {
      const message = "Select a recipient list with contacts first.";
      setCampaignActionMessage(message);
      return setNotice(message);
    }
    const ok = window.confirm(`Mark ${leadIds.length} contact(s) from ${selectedLists.length} list(s) as WhatsApp opted-in? Only continue if these contacts have given permission.`);
    if (!ok) return;
    try {
      await api.post("/marketing-crm/leads/bulk-update", { leadIds, consent: "YES" });
      const message = `${leadIds.length} contact(s) marked as WhatsApp opted-in.`;
      setCampaignActionMessage(message);
      setNotice(message);
      await loadAll();
    } catch (err) {
      const message = formatErr(err);
      setCampaignActionMessage(message);
      setNotice(message);
    }
  }

  async function archiveSelectedLeads() {
    if (!selectedLeadIds.length) return setNotice("Select leads first.");
    if (!window.confirm(`Archive ${selectedLeadIds.length} selected leads?`)) return;
    try {
      await api.post("/marketing-crm/leads/bulk-update", { leadIds: selectedLeadIds, archived: true });
      setSelectedLeadIds([]);
      setNotice("Selected leads archived.");
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function assignSelectedCategory() {
    if (!selectedLeadIds.length) return setNotice("Select leads first.");
    if (!bulkCategoryId) return setNotice("Select a category first.");
    try {
      await api.post("/marketing-crm/leads/bulk-assign-category", { leadIds: selectedLeadIds, categoryId: bulkCategoryId });
      setNotice(`${selectedLeadIds.length} leads assigned to category.`);
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function saveLeadCategory(draft = categoryDraft) {
    try {
      if (editingCategoryId) {
        await api.put(`/marketing-crm/lead-categories/${editingCategoryId}`, draft);
        setNotice("Category updated.");
      } else {
        await api.post("/marketing-crm/lead-categories", draft);
        setNotice("Category created.");
      }
      setCategoryDraft(defaultCategoryDraft);
      setEditingCategoryId("");
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function deleteLeadCategory(category) {
    if (!category?.id) return;
    const attached = Number(category.leadCount || 0);
    const query = attached ? "?unassignLeads=true&force=true" : "";
    if (!window.confirm(attached ? `${attached} leads are attached to "${category.categoryName}". Delete category and unassign those leads?` : `Delete category "${category.categoryName}"?`)) return;
    try {
      await api.delete(`/marketing-crm/lead-categories/${category.id}${query}`);
      if (bulkCategoryId === category.id) setBulkCategoryId("");
      setNotice("Category deleted.");
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function createCategoryFromSearch() {
    const primaryLocation = targetRegion.includedLocations[0] || {};
    const suggestedName = [targetRegion.presetName, primaryLocation.name, targetRegion.city, targetRegion.state, searchForm.keyword || searchForm.category].filter(Boolean).join(" - ") || "New Contact List Category";
    const name = window.prompt("Enter category name", suggestedName)?.trim();
    if (!name) return;
    const type = primaryLocation.type === "city" ? "City Wise" : primaryLocation.type === "state" ? "State Wise" : primaryLocation.name ? "Area Wise" : searchForm.keyword ? "Business Type Wise" : "Custom";
    try {
      const { data } = await api.post("/marketing-crm/lead-categories", {
        categoryName: name,
        categoryType: type,
        country: targetRegion.country || searchForm.country,
        state: targetRegion.state || searchForm.state,
        city: targetRegion.city || (primaryLocation.type === "city" ? primaryLocation.name : ""),
        area: primaryLocation.type !== "city" ? primaryLocation.name || "" : "",
        pincode: primaryLocation.type === "pincode" ? primaryLocation.name : "",
        notes: "Created from Lead Finder",
        isActive: true,
      });
      setNotice(`Category "${data.categoryName}" created.`);
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  function exportSelectedCsv() {
    const rows = selectedLeadRows.length ? selectedLeadRows : sortedLeads;
    const columns = ["businessName", "categoryName", "categoryType", "city", "area", "mobile", "whatsappNumber", "email", "rating", "status", "whatsappConsent", "source", "createdAt"];
    const csv = [
      columns.join(","),
      ...rows.map((row) => columns.map((col) => `"${String(row[col] ?? "").replace(/"/g, '""')}"`).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "marketing-leads.csv";
    link.click();
    URL.revokeObjectURL(url);
  }

  function validateCampaignDraft(mode = "draft") {
    if (!campaignDraft.campaignName.trim()) return "Campaign name is required.";
    if (mode === "test" && !String(campaignDraft.testNumber || "").trim()) return "Test WhatsApp number is required.";
    if (mode !== "test" && ![...(campaignDraft.contactListIds || []), campaignDraft.contactListId].filter(Boolean).length) return "Select at least one recipient list.";
    if (!campaignDraft.messageCategory) return "Message category is required.";
    if (!campaignDraft.caption.trim()) return "Message / caption is required.";
    if (campaignDraft.messageCategory === "Marketing" && !campaignDraft.templateName.trim()) return "WhatsApp Approved Template is required for marketing campaigns.";
    if (campaignDraft.templateName.trim()) {
      const selectedTemplate = templates.find((tpl) => String(tpl.templateName || "").toLowerCase() === campaignDraft.templateName.trim().toLowerCase());
      if (!selectedTemplate || String(selectedTemplate.status || "").toUpperCase() !== "APPROVED") return "Select an APPROVED WhatsApp template before sending or scheduling.";
      const templateHeader = String(selectedTemplate.headerType || "None");
      const messageHeader = campaignDraft.messageType === "PDF" || campaignDraft.messageType === "Catalogue" ? "Document" : campaignDraft.messageType;
      if (["Image", "Video", "Document"].includes(messageHeader) && templateHeader !== messageHeader) {
        return `Selected template header is ${templateHeader}. For ${campaignDraft.messageType}, choose an approved template with ${messageHeader} header.`;
      }
    }
    if (["Image", "Video", "Document"].includes(campaignDraft.messageType) && !campaignDraft.mediaUrl.trim()) return "Upload media or paste a media link for this message type.";
    if (mode === "schedule" && campaignDraft.scheduleMode === "Send Now") return "Select One Time, Daily, Weekly, or Monthly before scheduling.";
    if (mode === "schedule" && !campaignDraft.scheduledDateTime) return "Schedule date/time is required.";
    return "";
  }

  async function saveCampaignDraft(status = "Draft") {
    const error = validateCampaignDraft(status === "Scheduled" ? "schedule" : "draft");
    if (error) {
      setCampaignActionMessage(error);
      return setNotice(error);
    }
    try {
      setCampaignActionMessage(status === "Scheduled" ? "Scheduling campaign..." : "Saving campaign draft...");
      const selectedListIds = [...new Set([...(campaignDraft.contactListIds || []), campaignDraft.contactListId].filter(Boolean))];
      const payload = { ...campaignDraft, contactListId: selectedListIds[0] || "", contactListIds: selectedListIds, status: status === "Draft" ? "Draft" : "Pending Approval" };
      const selectedTemplate = templates.find((tpl) => String(tpl.templateName || "").toLowerCase() === payload.templateName.trim().toLowerCase());
      if (selectedTemplate?.languageCode) payload.languageCode = selectedTemplate.languageCode;
      if (payload.messageType === "PDF" || payload.messageType === "Catalogue") payload.messageType = "Document";
      if (payload.scheduleMode === "Daily") payload.frequency = "Daily";
      else if (payload.scheduleMode === "Weekly") payload.frequency = "Weekly";
      else if (payload.scheduleMode === "Monthly") payload.frequency = "Monthly";
      else payload.frequency = "One time";
      const { id: campaignId, ...campaignPayload } = payload;
      const { data } = campaignId
        ? await api.put(`/marketing-crm/focused-campaigns/${campaignId}`, campaignPayload)
        : await api.post("/marketing/campaigns", campaignPayload);
      if (status === "Scheduled") {
        await api.post(`/marketing/campaigns/${data.id}/schedule`, { scheduledDateTime: campaignDraft.scheduledDateTime, scheduleMode: campaignDraft.scheduleMode });
        setNotice(`Campaign scheduled for ${formatDateTime(campaignDraft.scheduledDateTime)}.`);
        setCampaignActionMessage(`Campaign scheduled for ${formatDateTime(campaignDraft.scheduledDateTime)}.`);
      } else {
        setNotice("Campaign draft saved.");
        setCampaignActionMessage("Campaign draft saved. See it under Saved and Scheduled Campaigns.");
      }
      await loadAll();
      return data;
    } catch (err) {
      const message = formatErr(err);
      setNotice(message);
      setCampaignActionMessage(message);
      return null;
    }
  }

  function campaignSendResultMessage(data) {
    const extra = data.skippedNoConsentCount || data.missingWhatsappCount
      ? ` (${data.skippedNoConsentCount || 0} without opt-in, ${data.missingWhatsappCount || 0} missing WhatsApp number)`
      : "";
    const hint = data.sentCount === 0 && data.skippedNoConsentCount
      ? " Mark the selected recipient list as opted-in before sending."
      : "";
    return `Sent ${data.sentCount || 0}, failed ${data.failedCount || 0}, skipped ${data.skippedCount || 0}${extra}.${hint}`;
  }

  async function sendCampaignNow() {
    const error = validateCampaignDraft("send");
    if (error) {
      setCampaignActionMessage(error);
      return setNotice(error);
    }
    const campaign = await saveCampaignDraft("Pending Approval");
    if (!campaign?.id) return;
    try {
      const { data } = await api.post(`/marketing/campaigns/${campaign.id}/send`);
      const message = campaignSendResultMessage(data);
      setNotice(message);
      setCampaignActionMessage(message);
      await loadAll();
    } catch (err) {
      const message = campaignErrorMessage(err);
      setNotice(message);
      setCampaignActionMessage(message);
    }
  }

  async function sendCampaignTest() {
    const error = validateCampaignDraft("test");
    if (error) {
      setCampaignActionMessage(error);
      return setNotice(error);
    }
    try {
      setCampaignActionMessage(`Sending test to ${campaignDraft.testNumber}...`);
      const selectedListIds = [...new Set([...(campaignDraft.contactListIds || []), campaignDraft.contactListId].filter(Boolean))];
      const payload = { ...campaignDraft, contactListId: selectedListIds[0] || "", contactListIds: selectedListIds };
      const selectedTemplate = templates.find((tpl) => String(tpl.templateName || "").toLowerCase() === payload.templateName.trim().toLowerCase());
      if (selectedTemplate?.languageCode) payload.languageCode = selectedTemplate.languageCode;
      if (payload.messageType === "PDF" || payload.messageType === "Catalogue") payload.messageType = "Document";
      const { data } = await api.post("/marketing/campaigns/test", { ...payload, to: campaignDraft.testNumber });
      const metaId = data?.messageId ? ` Meta message ID: ${data.messageId}.` : "";
      setNotice(`Test accepted by Meta for ${campaignDraft.testNumber}.${metaId}`);
      setCampaignActionMessage(`Test accepted by Meta for ${campaignDraft.testNumber}.${metaId} Delivery status will update after webhook callback.`);
      await loadAll();
    } catch (err) {
      const message = campaignErrorMessage(err);
      setNotice(message);
      setCampaignActionMessage(message);
    }
  }

  async function uploadCampaignMedia(file) {
    if (!file) return;
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/marketing/media/upload", form);
      setCampaignDraft((cur) => ({
        ...cur,
        mediaUrl: data.url || "",
        messageType: data.mediaType === "Document" ? "PDF" : data.mediaType,
      }));
      setNotice("Media uploaded and attached.");
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function uploadTemplateMedia(file) {
    if (!file) return;
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/marketing/media/upload", form);
      const headerType = data.mediaType === "Document" ? "Document" : data.mediaType;
      setTemplateDraft((cur) => ({
        ...cur,
        headerType: ["Image", "Video", "Document"].includes(headerType) ? headerType : cur.headerType,
        mediaUrl: data.url || "",
        metaHeaderHandle: data.metaHeaderHandle || "",
        metaHeaderError: data.metaHeaderError || "",
      }));
      setNotice(data.metaHeaderHandle ? "Template header media uploaded and prepared for Meta." : `Template media uploaded, but Meta sample failed: ${data.metaHeaderError || "missing Meta handle"}`);
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function approveAndSend(campaignId, sendNow = false) {
    try {
      await api.post(`/marketing-crm/focused-campaigns/${campaignId}/approve`, { approved: true });
      if (sendNow) {
        const { data } = await api.post(`/marketing-crm/focused-campaigns/${campaignId}/send`);
        setNotice(campaignSendResultMessage(data));
      } else {
        setNotice("Campaign approved.");
      }
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  function editCampaign(campaign) {
    const listIds = [...new Set([...(campaign.contactListIds || []), campaign.contactListId].filter(Boolean))];
    setCampaignDraft({
      ...campaignDraft,
      ...campaign,
      contactListId: listIds[0] || "",
      contactListIds: listIds,
      testNumber: campaign.testNumber || campaignDraft.testNumber || "",
      templateVariables: { ...(campaignDraft.templateVariables || {}), ...(campaign.templateVariables || {}) },
    });
    setSection("campaigns");
    setCampaignActionMessage("Editing saved campaign. Save Draft or Send Now will update this campaign.");
  }

  async function deleteCampaign(campaign) {
    if (!campaign?.id) return setNotice("Campaign id missing.");
    if (!window.confirm(`Delete campaign "${campaign.campaignName}" and its delivery logs?`)) return;
    try {
      await api.delete(`/marketing-crm/focused-campaigns/${campaign.id}`);
      if (campaignDraft.id === campaign.id) setCampaignDraft((cur) => ({ ...cur, id: undefined }));
      setNotice("Campaign deleted.");
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function saveTemplate() {
    try {
      await api.post("/marketing/templates", { ...templateDraft, status: "DRAFT" });
      setTemplateDraft(defaultTemplateDraft);
      setNotice("Template draft saved.");
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function submitTemplateToMeta() {
    if (!templateDraft.templateName.trim()) return setNotice("Template name is required.");
    if (!(templateDraft.bodyText || templateDraft.bodyPreview || "").trim()) return setNotice("Template body text is required.");
    try {
      setTemplateSubmitting(true);
      await api.post("/marketing/templates/submit", templateDraft);
      setTemplateDraft(defaultTemplateDraft);
      setNotice("Template submitted to Meta for approval.");
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
      await loadAll();
    } finally {
      setTemplateSubmitting(false);
    }
  }

  async function syncTemplates() {
    try {
      setTemplateSubmitting(true);
      const { data } = await api.post("/marketing/templates/sync");
      setNotice(`${data.syncedCount || 0} WhatsApp templates synced from Meta. ${data.staleCount || 0} old templates marked stale.`);
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    } finally {
      setTemplateSubmitting(false);
    }
  }

  async function syncTemplateStatus(template) {
    if (!template?.id) return setNotice("Template id missing.");
    if (!template.metaTemplateId) return setNotice("This template does not have a Meta ID yet.");
    try {
      setTemplateSubmitting(true);
      const { data } = await api.post(`/marketing/templates/${template.id}/sync`);
      setNotice(`${data.templateName || template.templateName} is now ${String(data.status || "").toUpperCase() || "updated"}.`);
      await loadAll();
    } catch (err) {
      setNotice(formatErr(err));
    } finally {
      setTemplateSubmitting(false);
    }
  }

  async function deleteTemplate(template) {
    if (!template?.id) return setNotice("Template id missing.");
    const metaStatus = String(template.status || "").toUpperCase();
    const includesMeta = !!template.metaTemplateId || ["PENDING", "APPROVED", "PAUSED", "DISABLED"].includes(metaStatus);
    const message = includesMeta
      ? `Delete "${template.templateName}" from RP Signage and try to delete it from Meta WhatsApp too?`
      : `Delete local WhatsApp template "${template.templateName}" from RP Signage?`;
    if (!window.confirm(message)) return;
    try {
      const { data } = await api.delete(`/marketing/templates/${template.id}`);
      if (data?.warning) {
        setNotice(data.warning);
      } else if (data?.meta?.skipped) {
        setNotice("Template removed from RP Signage.");
      } else {
        setNotice("Template deleted from Meta and RP Signage.");
      }
      await loadAll();
    } catch (err) {
      const message = formatErr(err);
      if (includesMeta && window.confirm(`${message}\n\nRemove this template from RP Signage list only?`)) {
        try {
          const { data } = await api.delete(`/marketing/templates/${template.id}?localOnly=true`);
          setNotice(data?.warning || "Template removed from RP Signage only. It may still exist in Meta.");
          await loadAll();
          return;
        } catch (localErr) {
          setNotice(formatErr(localErr));
          return;
        }
      }
      setNotice(message);
    }
  }

  function editTemplate(template) {
    setTemplateDraft({
      ...defaultTemplateDraft,
      templateName: template.templateName || "",
      category: ["Marketing", "Utility", "Authentication"].includes(template.category) ? template.category : String(template.category || "Marketing").charAt(0).toUpperCase() + String(template.category || "Marketing").slice(1).toLowerCase(),
      languageCode: template.languageCode || "en_US",
      messageType: template.messageType || "Text",
      headerType: template.headerType || "None",
      headerText: template.headerText || "",
      mediaUrl: template.mediaUrl || "",
      metaHeaderHandle: template.metaHeaderHandle || "",
      metaHeaderError: template.metaHeaderError || "",
      bodyText: template.bodyText || template.bodyPreview || "",
      bodyPreview: template.bodyPreview || template.bodyText || "",
      footerText: template.footerText || "",
      buttons: template.buttons?.length ? template.buttons : defaultTemplateDraft.buttons,
      variables: template.variables?.length ? template.variables : defaultTemplateDraft.variables,
      variableExamples: template.variableExamples?.length ? template.variableExamples : defaultTemplateDraft.variableExamples,
      status: template.status || "DRAFT",
    });
    setSection("templates");
  }

  function duplicateTemplate(template) {
    editTemplate({ ...template, templateName: `${template.templateName || "template"}_copy`, status: "DRAFT" });
  }

  async function deleteWhatsappMessage(message) {
    if (!message?.id) return setNotice("Message id missing.");
    const outbound = message.direction === "out" || !!message.providerMessageId || !!message.campaignId || !!message.templateName;
    if (!window.confirm(`Delete this WhatsApp ${outbound ? "delivery status" : "reply"} record?`)) return;
    try {
      await api.delete(outbound ? `/marketing-crm/delivery-logs/${message.id}` : `/marketing-crm/inbox/${message.id}`);
      setNotice(outbound ? "WhatsApp delivery status deleted." : "WhatsApp reply deleted.");
      await refreshWhatsappActivity();
      await refreshDeliveryLogs();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function sendWhatsappReply(chat, message) {
    const text = String(message || "").trim();
    if (!text) return setNotice("Type a WhatsApp reply first.");
    if (!chat?.key && !chat?.phone) return setNotice("Select a WhatsApp conversation first.");
    try {
      const { data } = await api.post("/marketing-crm/inbox/reply", {
        leadId: chat?.lead?.id || "",
        phone: chat?.phone || chat?.key || "",
        message: text,
      });
      setWhatsappReplyText("");
      setNotice(data?.message?.providerMessageId ? "WhatsApp reply accepted by Meta." : "WhatsApp reply sent.");
      await refreshWhatsappActivity();
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function saveSettings() {
    try {
      const { data } = await api.put("/marketing-crm/settings", settingsDraft);
      setSettings(data || {});
      setSettingsDraft((cur) => ({ ...cur, ...(data || {}), accessToken: "", googlePlacesApiKey: "", googleMapsBrowserApiKey: "" }));
      setNotice("Integration settings saved securely.");
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  async function verifyWhatsappSettings() {
    try {
      setWhatsappVerify({ loading: true, message: "Checking WhatsApp IDs with Meta..." });
      const { data } = await api.get("/marketing-crm/settings/verify-whatsapp");
      setWhatsappVerify(data);
      setNotice(data.ok ? "WhatsApp IDs verified." : data.message || "WhatsApp IDs do not match.");
    } catch (err) {
      const message = formatErr(err);
      setWhatsappVerify({ ok: false, message });
      setNotice(message);
    }
  }

  async function subscribeWhatsappWebhook() {
    try {
      setWhatsappVerify({ loading: true, message: "Subscribing WABA to WhatsApp status webhooks..." });
      const { data } = await api.post("/marketing-crm/settings/subscribe-webhook");
      setWhatsappVerify(data);
      setNotice(data.message || "WhatsApp webhook subscription updated.");
      await refreshDeliveryLogs();
    } catch (err) {
      const message = formatErr(err);
      setWhatsappVerify({ ok: false, message });
      setNotice(message);
    }
  }

  const hotLeads = leads.filter((lead) => lead.status === "Interested").length;
  const today = new Date().toISOString().slice(0, 10);
  const todayFollowups = leads.filter((lead) => String(lead.nextFollowUpAt || "").startsWith(today)).length;
  const mobileSections = sections.filter((item) => ["dashboard", "leads", "whatsapp", "campaigns", "more"].includes(item.id));

  return (
    <div className="marketing-crm-page">
      <PageHeader
        overline="Admin / Marketing"
        title="Marketing CRM"
        subtitle="Find approved-provider leads, build opt-in lists, and send WhatsApp Cloud API campaigns."
      >
        <Button className="rounded-sm" onClick={() => setSection("finder")}>
          <Search className="h-4 w-4" /> Find Leads
        </Button>
      </PageHeader>

      {notice && (
        <div className="mb-4 rounded-sm border border-[#E5E7EB] bg-[#F9FAFB] p-3 text-sm text-[#374151]">
          {notice}
        </div>
      )}

      <div className="mb-5 hidden flex-wrap gap-2 lg:flex">
        {sections.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setSection(id)}
            className={`inline-flex items-center gap-2 rounded-sm border px-3 py-2 text-sm font-semibold ${section === id ? "border-[#111827] bg-[#111827] text-white" : "border-[#E5E7EB] bg-white text-[#374151]"}`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {section === "dashboard" && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard label="Today leads" value={leads.filter((lead) => String(lead.createdAt || "").startsWith(today)).length} Icon={Users} />
            <StatCard label="Follow-ups" value={todayFollowups} Icon={CalendarClock} />
            <StatCard label="WA replies" value={inbox.length} Icon={MessageCircle} />
            <StatCard label="Hot leads" value={hotLeads} Icon={Bell} />
            <StatCard label="Campaign sent" value={reports.campaignSent} Icon={Megaphone} />
            <StatCard label="Delivered" value={reports.delivered} Icon={CheckCircle2} />
            <StatCard label="Read" value={reports.read} Icon={BarChart3} />
            <StatCard label="Failed" value={reports.failed} Icon={MoreHorizontal} />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="font-display text-xl font-extrabold">Latest leads</h2>
                <Button variant="outline" size="sm" onClick={() => setSection("leads")}>Open</Button>
              </div>
              <div className="space-y-3">
                {leads.slice(0, 4).map((lead) => <LeadCard key={lead.id} lead={lead} updateLead={updateLead} compact />)}
                {!leads.length && <div className="text-sm text-[#6B7280]">No saved leads yet.</div>}
              </div>
            </div>
            <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
              <h2 className="font-display mb-3 text-xl font-extrabold">Campaign status</h2>
              <div className="space-y-3">
                {campaigns.slice(0, 4).map((campaign) => (
                  <CampaignCard key={campaign.id} campaign={campaign} approveAndSend={approveAndSend} />
                ))}
                {!campaigns.length && <div className="text-sm text-[#6B7280]">No campaigns yet.</div>}
              </div>
            </div>
          </div>
        </div>
      )}

      {section === "finder" && (
        <div className="grid gap-5 2xl:grid-cols-[minmax(760px,840px)_minmax(420px,1fr)]">
          <div className="min-w-0">
            <TargetRegionSelector
              searchForm={searchForm}
              setSearchForm={setSearchForm}
              targetRegion={targetRegion}
              setTargetRegion={setTargetRegion}
              targetRegions={targetRegions}
              selectedTargetRegionId={selectedTargetRegionId}
              applyTargetPreset={applyTargetPreset}
              locationSearch={locationSearch}
              setLocationSearch={setLocationSearch}
              locationMode={locationMode}
              setLocationMode={setLocationMode}
              addTargetLocation={addTargetLocation}
              removeTargetLocation={removeTargetLocation}
              updateTargetLocation={updateTargetLocation}
              saveTargetRegionPreset={saveTargetRegionPreset}
              deleteTargetRegionPreset={deleteTargetRegionPreset}
              searchPlaces={searchPlaces}
              loading={loading}
              googleMapsApiKey={settings.googleMapsBrowserApiKey || ENV_GOOGLE_MAPS_KEY}
            />
          </div>
          <div className="min-w-0 rounded-sm border border-[#E5E7EB] bg-white p-4">
            <div className="mb-3 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
              <div>
                <div className="text-sm font-semibold text-[#374151]">{searchResults.length} results</div>
                {searchSummary && <div className="text-xs text-[#6B7280]">{searchSummary.duplicatesSkipped || 0} duplicates skipped</div>}
                {searchResults.length > 0 && (
                  <label className="mt-1 flex items-center gap-2 text-xs font-semibold text-[#374151] sm:hidden">
                    <input type="checkbox" checked={allSearchResultsSelected} onChange={(e) => toggleAllSearchResults(e.target.checked)} />
                    Select all searched list
                  </label>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 xl:justify-end">
                {searchResults.length > 0 && (
                  <select className="h-10 min-w-[220px] rounded-md border border-[#E5E7EB] px-3 text-sm" value={saveListId} onChange={(e) => { setSaveListId(e.target.value); if (e.target.value) setSaveNewListName(""); }}>
                    <option value="">Create/select contact list</option>
                    {lists.map((list) => <option key={list.id} value={list.id}>{list.name} ({list.leadCount || list.leadIds?.length || 0})</option>)}
                  </select>
                )}
                {searchResults.length > 0 && !saveListId && (
                  <Input className="h-10 min-w-[220px] sm:w-64" placeholder="New contact list name" value={saveNewListName} onChange={(e) => setSaveNewListName(e.target.value)} />
                )}
                {searchResults.length > 0 && (
                  <label className="hidden items-center gap-2 text-sm font-semibold text-[#374151] sm:flex">
                    <input type="checkbox" checked={allSearchResultsSelected} onChange={(e) => toggleAllSearchResults(e.target.checked)} />
                    Select all
                  </label>
                )}
                <Button className="rounded-sm" onClick={saveSelectedLeads} disabled={!selectedSearchResults.length}>
                  <Download className="h-4 w-4" /> Save Selected{selectedSearchResults.length ? ` (${selectedSearchResults.length})` : ""}
                </Button>
              </div>
            </div>
            <div className="grid gap-3 xl:grid-cols-2 2xl:grid-cols-1 min-[1760px]:grid-cols-2">
              {searchResults.map((row, index) => {
                const key = leadFinderResultKey(row, index);
                const selected = !!selectedPlaces[key];
                return (
                  <div key={key} className={`rounded-sm border bg-white p-4 ${selected ? "border-[#111827]" : "border-[#E5E7EB]"}`}>
                    <div className="mb-3 flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold text-[#111827]">{row.businessName}</h3>
                        <div className="text-xs text-[#6B7280]">{row.category}</div>
                      </div>
                      <input className="mt-1 h-5 w-5" type="checkbox" checked={selected} onChange={(e) => setSelectedPlaces((current) => ({ ...current, [key]: e.target.checked }))} />
                    </div>
                    <div className="space-y-2 text-sm text-[#374151]">
                      <div className="flex gap-2"><MapPin className="mt-0.5 h-4 w-4 shrink-0" /> {row.address || "Address unavailable"}</div>
                      <div className="flex gap-2"><Phone className="mt-0.5 h-4 w-4 shrink-0" /> {row.phone || "No phone"}</div>
                      <div className="flex flex-wrap gap-2">
                        <Pill tone="amber">Rating {row.googleRating || "-"}</Pill>
                        {row.website && <a className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700" href={row.website} target="_blank" rel="noreferrer"><ExternalLink className="h-3 w-3" /> Website</a>}
                        {row.mapLink && <a className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700" href={row.mapLink} target="_blank" rel="noreferrer"><Navigation className="h-3 w-3" /> Map</a>}
                      </div>
                    </div>
                  </div>
                );
              })}
              {!searchResults.length && (
                <div className="rounded-sm border border-dashed border-[#D1D5DB] p-6 text-sm text-[#6B7280]">
                  {searchSummary
                    ? `${searchSummary.rawCandidates || 0} Google candidates checked. ${Array.isArray(searchSummary.queries) && searchSummary.queries[0] ? `Last query: ${searchSummary.queries[0]}` : "Try a broader keyword or region."}`
                    : "Search by keyword and location. Category assignment happens after results are selected."}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {section === "leads" && (
        <LeadManagementView
          leads={leads}
          loading={loading}
          filters={leadFilters}
          setFilters={(next) => { setLeadFilters(next); setLeadPage(1); }}
          cityOptions={cityOptions}
          sourceOptions={sourceOptions}
          sort={leadSort}
          setSort={setLeadSort}
          visibleLeads={visibleLeads}
          sortedCount={sortedLeads.length}
          selectedLeadIds={selectedLeadIds}
          setSelectedLeadIds={setSelectedLeadIds}
          page={leadPage}
          setPage={setLeadPage}
          totalPages={totalLeadPages}
          pageSize={leadPageSize}
          setPageSize={(size) => { setLeadPageSize(size); setLeadPage(1); }}
          setDetailLead={setDetailLead}
          updateLead={updateLead}
          deleteLead={deleteLead}
          createLead={createLead}
          leadDraft={leadDraft}
          setLeadDraft={setLeadDraft}
          selectedLeadRows={selectedLeadRows}
          setListModalOpen={setListModalOpen}
          setSection={setSection}
          bulkStatus={bulkStatus}
          setBulkStatus={setBulkStatus}
          changeSelectedStatus={changeSelectedStatus}
          deleteSelectedLeads={deleteSelectedLeads}
          exportSelectedCsv={exportSelectedCsv}
          setNotice={setNotice}
          bulkConsent={bulkConsent}
          setBulkConsent={setBulkConsent}
          changeSelectedConsent={changeSelectedConsent}
          archiveSelectedLeads={archiveSelectedLeads}
        />
      )}

      {section === "lists" && (
        <ContactListManager
          lists={lists}
          leads={leads}
          draft={listDraft}
          setDraft={setListDraft}
          editingId={editingListId}
          setEditingId={setEditingListId}
          saveList={saveContactList}
          deleteList={deleteList}
          deleteListsBulk={deleteContactListsBulk}
          removeContactsFromList={removeContactsFromList}
          moveContactsToList={moveContactsToList}
        />
      )}

      {section === "whatsapp" && (
        <WhatsAppActivityView
          conversations={whatsappConversations}
          activeChatKey={activeWhatsappChat}
          setActiveChatKey={setActiveWhatsappChat}
          refresh={refreshWhatsappActivity}
          deleteMessage={deleteWhatsappMessage}
          replyText={whatsappReplyText}
          setReplyText={setWhatsappReplyText}
          sendReply={sendWhatsappReply}
          updateLead={updateLead}
          setSection={setSection}
        />
      )}

      {section === "campaigns" && (
        <CampaignBuilder
          draft={campaignDraft}
          setDraft={setCampaignDraft}
          lists={lists}
          templates={templates}
          media={media}
          summary={campaignRecipientSummary}
          uploadCampaignMedia={uploadCampaignMedia}
          saveDraft={() => saveCampaignDraft("Draft")}
          sendTest={sendCampaignTest}
          scheduleCampaign={() => saveCampaignDraft("Scheduled")}
          sendNow={sendCampaignNow}
          markListOptIn={markCampaignListOptIn}
          actionMessage={campaignActionMessage}
          deliveryLogs={deliveryLogs}
          settings={settings}
          campaigns={campaigns}
          approveAndSend={approveAndSend}
          editCampaign={editCampaign}
          deleteCampaign={deleteCampaign}
          deleteWhatsappMessage={deleteWhatsappMessage}
        />
      )}

      {section === "templates" && (
        <WhatsAppTemplateManager
          draft={templateDraft}
          setDraft={setTemplateDraft}
          templates={templates}
          saveTemplate={saveTemplate}
          submitTemplateToMeta={submitTemplateToMeta}
          syncTemplates={syncTemplates}
          editTemplate={editTemplate}
          duplicateTemplate={duplicateTemplate}
          deleteTemplate={deleteTemplate}
          syncTemplateStatus={syncTemplateStatus}
          uploadTemplateMedia={uploadTemplateMedia}
          busy={templateSubmitting}
        />
      )}

      {section === "more" && (
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
            <h2 className="font-display mb-4 text-xl font-extrabold">Contact Lists</h2>
            <p className="mb-4 text-sm text-[#6B7280]">Create a list, then save Lead Finder results directly into that list.</p>
            <Button className="mb-4 w-full rounded-sm" onClick={() => setSection("lists")}><Mail className="h-4 w-4" /> Open Contact Lists</Button>
            <div className="space-y-2">{lists.slice(0, 5).map((item) => <div key={item.id} className="text-sm"><Pill tone="blue">List</Pill> {item.name}</div>)}</div>
          </div>
          <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
            <h2 className="font-display mb-4 text-xl font-extrabold">WhatsApp Templates</h2>
            <p className="mb-4 text-sm text-[#6B7280]">Create, submit, and sync approved Meta templates from the WhatsApp Templates tab.</p>
            <Button className="mb-4 w-full rounded-sm" onClick={() => setSection("templates")}><FileText className="h-4 w-4" /> Open Template Manager</Button>
            <div className="space-y-2">{templates.slice(0, 5).map((item) => <div key={item.id} className="text-sm"><Pill tone={String(item.status || "").toUpperCase() === "APPROVED" ? "green" : "amber"}>{item.status}</Pill> {item.templateName}</div>)}</div>
          </div>
          <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
            <h2 className="font-display mb-4 text-xl font-extrabold">Settings</h2>
            <div className="mb-3 grid grid-cols-2 gap-2 text-xs">
              <Pill tone={settings.accessTokenConfigured ? "green" : "red"}>Token {settings.accessTokenConfigured ? "set" : "missing"}</Pill>
              <Pill tone={settings.metaAppIdConfigured ? "green" : "red"}>Meta App {settings.metaAppIdConfigured ? "set" : "missing"}</Pill>
              <Pill tone={settings.googlePlacesApiKeyConfigured ? "green" : "red"}>Places key {settings.googlePlacesApiKeyConfigured ? "set" : "missing"}</Pill>
              <Pill tone={settings.googleMapsBrowserApiKeyConfigured ? "green" : "red"}>Map key {settings.googleMapsBrowserApiKeyConfigured ? "set" : "missing"}</Pill>
              <Pill tone={settings.webhookSubscribed ? "green" : "amber"}>Webhook {settings.webhookSubscribed ? "subscribed" : "pending"}</Pill>
            </div>
            <div className="mb-3 rounded-sm border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              Use the WABA ID that owns your active sending phone number. Templates created under another WhatsApp account will not work for this sender.
            </div>
            <Input className="mb-2" placeholder="Phone Number ID" value={settingsDraft.phoneNumberId || ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, phoneNumberId: e.target.value })} />
            <Input className="mb-2" placeholder="WABA ID" value={settingsDraft.wabaId || ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, wabaId: e.target.value })} />
            <Input className="mb-2" placeholder="Meta App ID" value={settingsDraft.metaAppId || ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, metaAppId: e.target.value })} />
            <Input className="mb-2" placeholder="Access token (leave blank to keep)" type="password" value={settingsDraft.accessToken || ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, accessToken: e.target.value })} />
            <Input className="mb-2" placeholder={`Google Places API key for backend search${settings.googlePlacesApiKeyConfigured ? " (saved - leave blank to keep)" : ""}`} type="password" value={settingsDraft.googlePlacesApiKey || ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, googlePlacesApiKey: e.target.value })} />
            <Input className="mb-2" placeholder={`Google Maps browser key for map UI${settings.googleMapsBrowserApiKeyConfigured ? " (saved - leave blank to keep)" : ""}`} type="password" value={settingsDraft.googleMapsBrowserApiKey || ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, googleMapsBrowserApiKey: e.target.value })} />
            <Input className="mb-2" placeholder="Webhook verify token" value={settingsDraft.webhookVerifyToken || ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, webhookVerifyToken: e.target.value })} />
            <Input className="mb-3" placeholder="Webhook URL" value={settingsDraft.webhookUrl || ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, webhookUrl: e.target.value })} />
            <div className="mb-3 rounded-sm border border-slate-200 bg-slate-50 p-3">
              <div className="mb-1 text-xs font-semibold text-slate-800">Salon WhatsApp OTP rate (₹ per delivered OTP)</div>
              <div className="mb-2 text-[11px] text-slate-500">This RP Signage platform tariff is applied only when WhatsApp reports Delivered or Read. Existing OTPs retain their original rate.</div>
              <Input type="number" min="0" step="0.01" value={settingsDraft.salonOtpUnitRate ?? 0.25} onChange={(e) => setSettingsDraft({ ...settingsDraft, salonOtpUnitRate: e.target.value === "" ? 0 : Number(e.target.value) })} />
              <div className="mt-3 text-xs font-semibold text-slate-800">Salon login OTP template (Meta)</div>
              <div className="mb-2 text-[11px] text-slate-500">Enter the exact approved Authentication template name, for example <span className="font-medium">web_user_login_otp</span>. Saving selects the Meta template; it does not create or approve one. The sender checks the connected WhatsApp account for the approved language if Meta rejects the saved language.</div>
              <Input className="mb-2" placeholder="Exact Meta template name, e.g. web_user_login_otp" value={settingsDraft.salonOtpTemplateName || ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, salonOtpTemplateName: e.target.value.trim().toLowerCase() })} />
              <Input placeholder="Meta template language, e.g. en_US" value={settingsDraft.salonOtpTemplateLanguage || "en_US"} onChange={(e) => setSettingsDraft({ ...settingsDraft, salonOtpTemplateLanguage: e.target.value.trim() || "en_US" })} />
              <div className="mt-3 text-xs font-semibold text-slate-800">Salon OTP payment-reminder template</div>
              <div className="mb-2 text-[11px] text-slate-500">Use an approved Meta Utility template with five body fields: Salon name, invoice number, month, amount, and due date. This is required to send reminders outside WhatsApp’s 24-hour conversation window.</div>
              <Input className="mb-2" placeholder="Meta Utility template name, e.g. salon_otp_payment_reminder" value={settingsDraft.salonOtpReminderTemplateName || ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, salonOtpReminderTemplateName: e.target.value.trim().toLowerCase() })} />
              <Input placeholder="Template language, e.g. en_US" value={settingsDraft.salonOtpReminderTemplateLanguage || "en_US"} onChange={(e) => setSettingsDraft({ ...settingsDraft, salonOtpReminderTemplateLanguage: e.target.value.trim() || "en_US" })} />
            </div>
            <div className="grid gap-2 sm:grid-cols-3">
              <Button variant="outline" className="w-full rounded-sm" onClick={verifyWhatsappSettings}>Verify WhatsApp IDs</Button>
              <Button variant="outline" className="w-full rounded-sm" onClick={subscribeWhatsappWebhook}>Subscribe Webhook</Button>
              <Button className="w-full rounded-sm" onClick={saveSettings}><Settings className="h-4 w-4" /> Save Settings</Button>
            </div>
            {settings.lastWebhookSubscribeAt && (
              <div className={`mt-3 rounded-sm border p-3 text-xs ${settings.lastWebhookSubscribeOk ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
                <div className="font-semibold">Webhook subscription: {settings.lastWebhookSubscribeOk ? "Active" : "Needs attention"}</div>
                <div className="mt-1">{settings.lastWebhookSubscribeMessage || "No subscription message available."}</div>
              </div>
            )}
            {whatsappVerify && (
              <div className={`mt-3 rounded-sm border p-3 text-xs ${whatsappVerify.ok ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-red-200 bg-red-50 text-red-700"}`}>
                <div className="font-semibold">{whatsappVerify.loading ? whatsappVerify.message : whatsappVerify.message || "Verification complete."}</div>
                {whatsappVerify.phone && (
                  <div className="mt-1">
                    Phone: {whatsappVerify.phone.display_phone_number || "-"} / {whatsappVerify.phone.verified_name || "-"}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {detailLead && (
        <LeadDetailDrawer
          lead={detailLead}
          inbox={inbox.filter((msg) => msg.leadId === detailLead.id || msg.phone_key === detailLead.phone_key)}
          logs={[]}
          updateLead={updateLead}
          onClose={() => setDetailLead(null)}
        />
      )}

      {listModalOpen && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/40 p-3 sm:items-center">
          <div className="w-full max-w-lg rounded-sm bg-white p-4 shadow-xl">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="font-display text-xl font-extrabold">Add to Contact List</h2>
                <p className="text-sm text-[#6B7280]">{selectedLeadIds.length} selected leads</p>
              </div>
              <button type="button" className="flex h-9 w-9 items-center justify-center rounded-sm border border-[#E5E7EB]" onClick={() => setListModalOpen(false)} aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid gap-3">
              <Field label="Existing list">
                <select className="h-10 w-full rounded-md border border-[#E5E7EB] px-3 text-sm" value={bulkListId} onChange={(e) => setBulkListId(e.target.value)}>
                  <option value="">Create new list</option>
                  {lists.map((list) => <option key={list.id} value={list.id}>{list.name} ({list.leadCount || list.leadIds?.length || 0})</option>)}
                </select>
              </Field>
              <Field label="Or create new">
                <Input placeholder="Restaurants Vasai" value={bulkNewListName} onChange={(e) => setBulkNewListName(e.target.value)} disabled={!!bulkListId} />
              </Field>
              <Button className="h-11 rounded-sm" onClick={addSelectedToContactList}>
                <Plus className="h-4 w-4" /> Save Leads to List
              </Button>
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setSection("leads")}
        className="fixed bottom-20 right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#111827] text-white shadow-xl lg:hidden"
        aria-label="Add Lead"
      >
        <Plus className="h-6 w-6" />
      </button>

      <nav className="fixed inset-x-0 bottom-0 z-50 border-t border-[#E5E7EB] bg-white/95 px-1 py-1 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] backdrop-blur lg:hidden">
        <div className="grid grid-cols-5">
          {mobileSections.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setSection(id)}
              className={`flex min-h-14 flex-col items-center justify-center gap-1 rounded-sm px-1 text-[10px] font-semibold ${section === id ? "bg-[#111827] text-white" : "text-[#4B5563]"}`}
            >
              <Icon className="h-4 w-4" />
              <span className="max-w-full truncate">{label.replace("Lead Finder", "Finder")}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}

function TargetRegionSelector({
  searchForm,
  setSearchForm,
  targetRegion,
  setTargetRegion,
  targetRegions,
  selectedTargetRegionId,
  applyTargetPreset,
  locationSearch,
  setLocationSearch,
  locationMode,
  setLocationMode,
  addTargetLocation,
  removeTargetLocation,
  updateTargetLocation,
  saveTargetRegionPreset,
  deleteTargetRegionPreset,
  searchPlaces,
  loading,
  googleMapsApiKey,
}) {
  const included = useMemo(() => targetRegion.includedLocations || [], [targetRegion.includedLocations]);
  const excluded = useMemo(() => targetRegion.excludedLocations || [], [targetRegion.excludedLocations]);
  const regionLabel = included.length ? `${included.map((item) => item.name).join(", ")}${excluded.length ? ` excluding ${excluded.map((item) => item.name).join(", ")}` : ""}` : "No target region selected";
  const approxArea = Math.max(0, Math.round((included.reduce((sum, item) => sum + locationCoverageAreaSqKm(item, targetRegion.defaultRadiusKm || 10), 0) - excluded.reduce((sum, item) => sum + locationCoverageAreaSqKm(item, targetRegion.defaultRadiusKm || 10), 0)) * 10) / 10);
  const placesNodeRef = useRef(null);
  const [googleReady, setGoogleReady] = useState(false);
  const [googleError, setGoogleError] = useState("");
  const [predictions, setPredictions] = useState([]);
  const [placesLoading, setPlacesLoading] = useState(false);

  useEffect(() => {
    if (!googleMapsApiKey) {
      setGoogleReady(false);
      setGoogleError("Google Maps API key missing. Please add key in settings or .env.");
      return undefined;
    }
    let cancelled = false;
    const onAuthError = (event) => {
      if (!cancelled) {
        setGoogleReady(false);
        setGoogleError(event.detail || "Google Maps rejected this browser key.");
      }
    };
    window.addEventListener("rp-google-maps-auth-error", onAuthError);
    loadGoogleMapsScript(googleMapsApiKey)
      .then(() => {
        if (!cancelled) {
          setGoogleReady(true);
          setGoogleError("");
        }
      })
      .catch((err) => {
        googleMapsLoaderPromise = null;
        if (!cancelled) {
          setGoogleReady(false);
          setGoogleError(err.message || "Google Maps failed to load.");
        }
      });
    return () => {
      cancelled = true;
      window.removeEventListener("rp-google-maps-auth-error", onAuthError);
    };
  }, [googleMapsApiKey]);

  useEffect(() => {
    if (!googleReady || !locationSearch.trim()) {
      setPredictions([]);
      return undefined;
    }
    const timer = setTimeout(() => {
      const service = new window.google.maps.places.AutocompleteService();
      const request = {
        input: locationSearch.trim(),
        componentRestrictions: { country: "in" },
      };
      service.getPlacePredictions(request, (items, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          const selectedKeys = new Set([...included, ...excluded].map((item) => item.placeId || item.name));
          setPredictions((items || [])
            .filter((item) => !selectedKeys.has(item.place_id))
            .sort((a, b) => placeSuggestionRank(a.types || []) - placeSuggestionRank(b.types || []))
            .slice(0, 8));
        } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          setPredictions([]);
        }
      });
    }, 250);
    return () => clearTimeout(timer);
  }, [googleReady, locationSearch, included, excluded]);

  function addPrediction(prediction) {
    if (!googleReady || !placesNodeRef.current) return;
    setPlacesLoading(true);
    const service = new window.google.maps.places.PlacesService(placesNodeRef.current);
    service.getDetails(
      {
        placeId: prediction.place_id,
        fields: ["place_id", "name", "formatted_address", "geometry", "address_components", "types"],
      },
      (place, status) => {
        setPlacesLoading(false);
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
          setGoogleError("Could not load this Google place. Please try another suggestion.");
          return;
        }
        addTargetLocation(normalizeGooglePlace(place, targetRegion.defaultRadiusKm || searchForm.radius_km || 10), locationMode);
        setPredictions([]);
      }
    );
  }

  function reverseGeocodeLocation(key, mode, coords) {
    if (!window.google?.maps?.Geocoder) {
      updateTargetLocation(key, mode, coords);
      return;
    }
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: coords }, (results, status) => {
      if (status !== "OK" || !results?.length) {
        updateTargetLocation(key, mode, coords);
        return;
      }
      const place = normalizeGooglePlace(results[0], targetRegion.defaultRadiusKm || searchForm.radius_km || 10);
      updateTargetLocation(key, mode, {
        latitude: coords.lat,
        longitude: coords.lng,
        formattedAddress: place.formattedAddress,
        city: place.city,
        state: place.state,
        country: place.country,
        pincode: place.pincode,
        placeId: place.placeId,
      });
    });
  }

  return (
    <div className="grid gap-4 min-[1180px]:grid-cols-[minmax(340px,0.92fr)_minmax(420px,1.08fr)]">
      <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
        <div className="mb-4">
          <h2 className="font-display text-xl font-extrabold">Lead Finder</h2>
          <p className="text-sm text-[#6B7280]">Search by business keyword and target regions. Category assignment happens after results are found.</p>
        </div>
        <div className="grid gap-3">
          <Field label="Business keyword">
            <Input placeholder="Restaurant, CCTV installer, dealer..." value={searchForm.keyword} onChange={(e) => setSearchForm({ ...searchForm, keyword: e.target.value })} />
          </Field>
          <Field label="Target region preset">
            <select className="h-10 w-full rounded-md border border-[#E5E7EB] px-3 text-sm" value={selectedTargetRegionId} onChange={(e) => applyTargetPreset(e.target.value)}>
              <option value="">Custom region</option>
              {targetRegions.filter((item) => item.isActive !== false).map((item) => <option key={item.id} value={item.id}>{item.presetName}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Default radius">
              <select className="h-10 w-full rounded-md border border-[#E5E7EB] px-3 text-sm" value={targetRegion.defaultRadiusKm} onChange={(e) => {
                const radius = Number(e.target.value);
                setTargetRegion({
                  ...targetRegion,
                  defaultRadiusKm: radius,
                  includedLocations: included.map((item) => ({ ...item, radiusKm: item.radiusKm || radius })),
                  excludedLocations: excluded.map((item) => ({ ...item, radiusKm: item.radiusKm || radius })),
                });
                setSearchForm({ ...searchForm, radius_km: radius });
              }}>
                {RADIUS_OPTIONS.map((value) => <option key={value} value={value}>{value} km</option>)}
              </select>
            </Field>
            <Field label="Max results">
              <select className="h-10 w-full rounded-md border border-[#E5E7EB] px-3 text-sm" value={searchForm.max_results} onChange={(e) => setSearchForm({ ...searchForm, max_results: Number(e.target.value) })}>
                <option value={20}>20 leads</option><option value={40}>40 leads</option><option value={60}>60 leads</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Min rating"><Input type="number" value={searchForm.minimum_rating} onChange={(e) => setSearchForm({ ...searchForm, minimum_rating: e.target.value })} /></Field>
            <Field label="Source">
              <select className="h-10 w-full rounded-md border border-[#E5E7EB] px-3 text-sm" value={searchForm.source} onChange={(e) => setSearchForm({ ...searchForm, source: e.target.value })}>
                <option value="google_places">Google Places</option>
              </select>
            </Field>
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={searchForm.has_phone} onChange={(e) => setSearchForm({ ...searchForm, has_phone: e.target.checked })} /> Has phone</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={searchForm.has_website} onChange={(e) => setSearchForm({ ...searchForm, has_website: e.target.checked })} /> Has website</label>
          </div>
        </div>

        <div className="mt-5 rounded-sm border border-[#E5E7EB] bg-[#F9FAFB] p-3">
          <div className="mb-3 flex rounded-sm border border-[#E5E7EB] bg-white p-1">
            <button type="button" className={`flex-1 rounded-sm px-3 py-2 text-sm font-semibold ${locationMode === "include" ? "bg-[#111827] text-white" : "text-[#374151]"}`} onClick={() => setLocationMode("include")}>Include</button>
            <button type="button" className={`flex-1 rounded-sm px-3 py-2 text-sm font-semibold ${locationMode === "exclude" ? "bg-red-600 text-white" : "text-[#374151]"}`} onClick={() => setLocationMode("exclude")}>Exclude</button>
          </div>
          <div className="relative">
            <Input
              placeholder="Search country, city, area, pincode, landmark"
              value={locationSearch}
              onChange={(e) => setLocationSearch(e.target.value)}
              disabled={!googleReady}
            />
            <div ref={placesNodeRef} className="hidden" />
            {googleError && <div className="mt-2 rounded-sm border border-amber-200 bg-amber-50 p-2 text-xs font-semibold text-amber-800">{googleError}</div>}
            {placesLoading && <div className="mt-2 rounded-sm border border-[#E5E7EB] bg-white p-2 text-xs text-[#6B7280]">Loading Google place...</div>}
            <div className="mt-2 grid gap-1">
              {predictions.map((item) => (
                <button key={item.place_id} type="button" className="flex items-center justify-between gap-3 rounded-sm border border-[#E5E7EB] bg-white px-3 py-2 text-left text-sm hover:border-[#111827]" onClick={() => addPrediction(item)}>
                  <span className="min-w-0">
                    <span className="block truncate font-semibold">{item.structured_formatting?.main_text || item.description}</span>
                    <span className="block truncate text-xs text-[#6B7280]">{item.structured_formatting?.secondary_text || item.description}</span>
                  </span>
                  <Plus className="h-4 w-4 shrink-0" />
                </button>
              ))}
            </div>
          </div>
          <LocationChipGroup title="Included locations" tone="include" items={included} remove={(name) => removeTargetLocation(name, "include")} update={(name, patch) => updateTargetLocation(name, "include", patch)} />
          <LocationChipGroup title="Excluded locations" tone="exclude" items={excluded} remove={(name) => removeTargetLocation(name, "exclude")} update={(name, patch) => updateTargetLocation(name, "exclude", patch)} />
        </div>

        <div className="mt-4 grid gap-2 sm:grid-cols-[1fr_auto_auto]">
          <Input placeholder="Preset name e.g. Vasai CCTV Leads" value={targetRegion.presetName} onChange={(e) => setTargetRegion({ ...targetRegion, presetName: e.target.value })} />
          <Button variant="outline" className="rounded-sm" onClick={saveTargetRegionPreset}>Save Preset</Button>
          <Button variant="outline" className="rounded-sm text-red-600" onClick={deleteTargetRegionPreset} disabled={!selectedTargetRegionId}>Delete</Button>
        </div>
        <Textarea className="mt-2" placeholder="Notes" value={targetRegion.notes} onChange={(e) => setTargetRegion({ ...targetRegion, notes: e.target.value })} />
        <Button className="mt-4 h-11 w-full rounded-sm" onClick={searchPlaces} disabled={loading || !included.length}>
          <Search className="h-4 w-4" /> Search {searchForm.keyword || "Leads"} in {included.length ? regionLabel : "target region"} ({approxArea} sq km)
        </Button>
      </div>

      <GoogleTargetMap
        included={included}
        excluded={excluded}
        approxArea={approxArea}
        regionLabel={regionLabel}
        mapKeyConfigured={Boolean(googleMapsApiKey)}
        googleReady={googleReady}
        googleError={googleError}
        reverseGeocodeLocation={reverseGeocodeLocation}
      />
    </div>
  );
}

function LocationChipGroup({ title, tone, items, remove, update }) {
  const isExclude = tone === "exclude";
  return (
    <div className="mt-4">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6B7280]">{title}</div>
      <div className="grid gap-2">
        {items.map((item) => (
          <div key={item.placeId || item.name} className={`rounded-sm border bg-white p-2 ${isExclude ? "border-red-200" : "border-emerald-200"}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold">{item.name}</div>
                <div className="truncate text-xs text-[#6B7280]">{item.formattedAddress || [item.city, item.state, item.country].filter(Boolean).join(", ") || item.type || "location"}</div>
                <div className="text-xs font-semibold text-[#6B7280]">{item.type || "location"} + {item.radiusKm || 10} km</div>
              </div>
              <button type="button" className="flex h-8 w-8 items-center justify-center rounded-sm border border-[#E5E7EB]" onClick={() => remove(item.placeId || item.name)} title="Remove location"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-2 grid grid-cols-[1fr_76px] items-center gap-2">
              <input type="range" min="1" max="50" step="1" value={item.radiusKm || 10} onChange={(e) => update(item.placeId || item.name, { radiusKm: Number(e.target.value) })} />
              <select className="h-8 rounded-md border border-[#E5E7EB] px-2 text-xs" value={item.radiusKm || 10} onChange={(e) => update(item.placeId || item.name, { radiusKm: Number(e.target.value) })}>
                {RADIUS_OPTIONS.map((value) => <option key={value} value={value}>{value} km</option>)}
              </select>
            </div>
          </div>
        ))}
        {!items.length && <div className="rounded-sm border border-dashed border-[#D1D5DB] bg-white p-3 text-sm text-[#6B7280]">No {isExclude ? "excluded" : "included"} locations added.</div>}
      </div>
    </div>
  );
}

function expandedViewportBounds(item) {
  const vb = item.viewportBounds || {};
  const north = Number(vb.north);
  const south = Number(vb.south);
  const east = Number(vb.east);
  const west = Number(vb.west);
  if (![north, south, east, west].every(Number.isFinite)) return null;
  const radiusKm = Number(item.radiusKm || 0);
  const centerLat = (north + south) / 2;
  const latDelta = radiusKm / 111;
  const lngDelta = radiusKm / Math.max(20, 111 * Math.cos((centerLat * Math.PI) / 180));
  return {
    north: north + latDelta,
    south: south - latDelta,
    east: east + lngDelta,
    west: west - lngDelta,
  };
}

function GoogleTargetMap({ included, excluded, approxArea, regionLabel, mapKeyConfigured, googleReady, googleError, reverseGeocodeLocation }) {
  const mapNodeRef = useRef(null);
  const mapRef = useRef(null);
  const overlaysRef = useRef([]);
  const all = useMemo(() => [...included.map((item) => ({ ...item, mode: "include" })), ...excluded.map((item) => ({ ...item, mode: "exclude" }))], [included, excluded]);

  useEffect(() => {
    if (!mapKeyConfigured || !googleReady || !mapNodeRef.current || mapRef.current) return;
    mapRef.current = new window.google.maps.Map(mapNodeRef.current, {
      center: { lat: 19.076, lng: 72.8777 },
      zoom: 10,
      mapTypeId: "roadmap",
      mapTypeControl: true,
      mapTypeControlOptions: {
        mapTypeIds: ["roadmap", "satellite", "hybrid", "terrain"],
      },
      zoomControl: true,
      streetViewControl: true,
      fullscreenControl: true,
      rotateControl: true,
      scaleControl: true,
      tilt: 45,
    });
  }, [googleReady, mapKeyConfigured]);

  useEffect(() => {
    if (!mapRef.current || !window.google?.maps) return;
    overlaysRef.current.forEach((overlay) => overlay.setMap(null));
    overlaysRef.current = [];
    const bounds = new window.google.maps.LatLngBounds();
    all.forEach((item) => {
      const lat = Number(item.latitude);
      const lng = Number(item.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const position = { lat, lng };
      const include = item.mode === "include";
      const expandedBounds = expandedViewportBounds(item);
      const marker = new window.google.maps.Marker({
        position,
        map: mapRef.current,
        draggable: !expandedBounds,
        title: item.name,
        label: include ? "I" : "E",
      });
      marker.addListener("dragend", () => {
        const next = marker.getPosition();
        const coords = { lat: next.lat(), lng: next.lng() };
        reverseGeocodeLocation(item.placeId || item.name, item.mode, coords);
      });
      let coverageOverlay;
      if (expandedBounds) {
        coverageOverlay = new window.google.maps.Rectangle({
          map: mapRef.current,
          bounds: expandedBounds,
          strokeColor: include ? "#059669" : "#DC2626",
          strokeOpacity: 0.9,
          strokeWeight: 2,
          fillColor: include ? "#10B981" : "#EF4444",
          fillOpacity: 0.18,
        });
      } else {
        coverageOverlay = new window.google.maps.Circle({
          map: mapRef.current,
          center: position,
          radius: Number(item.radiusKm || 10) * 1000,
          strokeColor: include ? "#059669" : "#DC2626",
          strokeOpacity: 0.9,
          strokeWeight: 2,
          fillColor: include ? "#10B981" : "#EF4444",
          fillOpacity: 0.18,
        });
      }
      overlaysRef.current.push(marker, coverageOverlay);
      bounds.extend(position);
      try {
        bounds.union(coverageOverlay.getBounds());
      } catch (err) {
        // Bounds can be unavailable for invalid geometry; marker bounds still keep the map usable.
      }
    });
    if (all.some((item) => Number.isFinite(Number(item.latitude)) && Number.isFinite(Number(item.longitude)))) {
      mapRef.current.fitBounds(bounds, 56);
    }
  }, [all, reverseGeocodeLocation]);

  return (
    <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-display text-xl font-extrabold">Target Region Map</h2>
          <p className="text-sm text-[#6B7280]">{regionLabel}</p>
        </div>
        <Pill tone="blue">{approxArea} sq km approx.</Pill>
      </div>
      {!mapKeyConfigured && (
        <div className="rounded-sm border border-amber-200 bg-amber-50 p-4 text-sm font-semibold text-amber-800">
          Google Maps API key missing. Please add key in settings or .env.
        </div>
      )}
      {mapKeyConfigured && googleError && (
        <div className="rounded-sm border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <div className="font-extrabold">Google Maps could not load.</div>
          <div className="mt-1 font-semibold">{googleError}</div>
          <div className="mt-2 text-xs">Use a browser key restricted to this domain, for example https://rpsignage.com/* and http://localhost:3000/*, with Maps JavaScript API and Places API enabled.</div>
        </div>
      )}
      {mapKeyConfigured && !googleError && <div ref={mapNodeRef} className="h-[460px] overflow-hidden rounded-sm border border-[#D1D5DB] bg-[#F3F4F6]" />}
      {mapKeyConfigured && !all.length && <div className="mt-3 rounded-sm border border-dashed border-[#D1D5DB] bg-[#F9FAFB] p-3 text-sm font-semibold text-[#6B7280]">Search Google Places and add include or exclude locations to draw radius circles.</div>}
      <div className="mt-3 grid gap-2 text-sm text-[#374151] sm:grid-cols-3">
        <div className="rounded-sm bg-[#F9FAFB] p-3"><span className="font-semibold">{included.length}</span> included</div>
        <div className="rounded-sm bg-[#F9FAFB] p-3"><span className="font-semibold">{excluded.length}</span> excluded</div>
        <div className="rounded-sm bg-[#F9FAFB] p-3">Markers are draggable</div>
      </div>
    </div>
  );
}

function WhatsAppActivityView({
  conversations,
  activeChatKey,
  setActiveChatKey,
  refresh,
  deleteMessage,
  replyText,
  setReplyText,
  sendReply,
  updateLead,
  setSection,
}) {
  const active = conversations.find((chat) => chat.key === activeChatKey) || conversations[0];
  const activeLead = active?.lead;
  const [chatSearch, setChatSearch] = useState("");
  const [mobileThreadOpen, setMobileThreadOpen] = useState(false);
  const filteredConversations = conversations.filter((chat) => {
    const q = chatSearch.trim().toLowerCase();
    if (!q) return true;
    return [chat.title, chat.phone, whatsappActivityText(chat.messages[chat.messages.length - 1] || {})].some((value) => String(value || "").toLowerCase().includes(q));
  });
  const suggestion = "Thanks for your reply. Our team will share details shortly. Would you like pricing, demo, or product catalogue?";
  const activePhone = active?.phone || active?.key || "";
  const telPhone = activePhone.replace(/\D/g, "");
  const unreadTotal = conversations.reduce((sum, chat) => sum + chat.messages.filter((msg) => msg.direction !== "out").length, 0);
  const outboundTotal = conversations.reduce((sum, chat) => sum + chat.messages.filter((msg) => msg.direction === "out").length, 0);
  const inboundTotal = Math.max(0, unreadTotal);
  const activeInitials = String(active?.title || active?.phone || "WA").slice(0, 2).toUpperCase();
  const openChat = (chat) => {
    setActiveChatKey(chat.key);
    setMobileThreadOpen(true);
  };
  return (
    <div className="overflow-hidden rounded-sm border border-[#D7DBD8] bg-[#F0F2F5] shadow-sm">
      {!conversations.length ? (
        <div className="grid min-h-[520px] place-items-center bg-[#F0F2F5] p-6 text-center">
          <div className="max-w-md rounded-sm border border-[#E9EDEF] bg-white p-6">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#00A884] text-white">
              <MessageCircle className="h-7 w-7" />
            </div>
            <h2 className="mt-4 text-xl font-extrabold text-[#111B21]">No WhatsApp chats yet</h2>
            <p className="mt-2 text-sm text-[#667781]">Replies and campaign/test messages will appear here after WhatsApp webhook or send activity.</p>
          </div>
        </div>
      ) : (
        <div className="grid h-[calc(100vh-210px)] min-h-[640px] bg-[#F0F2F5] lg:grid-cols-[380px_minmax(0,1fr)_340px]">
          <aside className={`${mobileThreadOpen ? "hidden lg:flex" : "flex"} min-h-0 flex-col border-[#DADDE1] bg-white lg:border-r`}>
            <div className="flex items-center justify-between gap-3 border-b border-[#E9EDEF] bg-[#00A884] px-4 py-3 text-white">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white/15">
                  <MessageCircle className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-lg font-extrabold leading-tight">WhatsApp Inbox</div>
                  <div className="text-xs text-white/80">{conversations.length} chats | {inboundTotal} received | {outboundTotal} sent</div>
                </div>
              </div>
              <button type="button" className="flex h-9 w-9 items-center justify-center rounded-full text-white hover:bg-white/15" onClick={refresh} title="Refresh chats">
                <Download className="h-4 w-4" />
              </button>
            </div>
            <div className="border-b border-[#E9EDEF] bg-white p-3">
              <div className="flex h-10 items-center gap-2 rounded-lg bg-[#F0F2F5] px-3">
                <Search className="h-4 w-4 text-[#667781]" />
                <input
                  className="min-w-0 flex-1 bg-transparent text-sm outline-none"
                  placeholder="Search chats or phone number"
                  value={chatSearch}
                  onChange={(e) => setChatSearch(e.target.value)}
                />
              </div>
              <div className="mt-3 flex gap-2 overflow-x-auto text-xs font-semibold">
                <span className="rounded-full bg-[#E7FCE3] px-3 py-1.5 text-[#008069]">All</span>
                <span className="rounded-full bg-[#F0F2F5] px-3 py-1.5 text-[#54656F]">Unread</span>
                <span className="rounded-full bg-[#F0F2F5] px-3 py-1.5 text-[#54656F]">Leads</span>
                <span className="rounded-full bg-[#F0F2F5] px-3 py-1.5 text-[#54656F]">Campaigns</span>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {filteredConversations.map((chat) => {
                const latest = chat.messages[chat.messages.length - 1] || {};
                const latestDate = latest.updatedAt || latest.createdAt || chat.latestAt;
                const latestTime = latestDate && !Number.isNaN(new Date(latestDate).getTime())
                  ? new Date(latestDate).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
                  : "";
                return (
                  <button
                    key={chat.key}
                    type="button"
                    onClick={() => openChat(chat)}
                    className={`block w-full border-b border-[#F0F2F5] px-3 py-3 text-left ${active?.key === chat.key ? "bg-[#E9F5F1]" : "hover:bg-[#F5F6F6]"}`}
                  >
                    <div className="flex gap-3">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#D9FDD3] text-sm font-extrabold text-[#075E54]">
                        {String(chat.title || chat.phone || "WA").slice(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-extrabold text-[#111B21]">{chat.title}</span>
                          <span className="shrink-0 text-[11px] text-[#667781]">{latestTime}</span>
                        </div>
                        <div className="mt-1 flex items-center gap-2">
                          <span className="truncate text-sm text-[#667781]">{whatsappActivityText(latest)}</span>
                          <span className="ml-auto rounded-full bg-[#25D366] px-1.5 py-0.5 text-[10px] font-bold text-white">{chat.messages.length}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
              {!filteredConversations.length && (
                <div className="p-6 text-center text-sm text-[#667781]">No chats matched your search.</div>
              )}
            </div>
          </aside>

          <main className={`${mobileThreadOpen ? "flex" : "hidden lg:flex"} min-h-0 flex-col bg-[#EFEAE2]`}>
            <div className="flex items-center justify-between gap-3 border-b border-[#DADDE1] bg-[#F0F2F5] px-3 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <button type="button" className="flex h-9 w-9 items-center justify-center rounded-full text-[#54656F] hover:bg-[#D9DBDF] lg:hidden" onClick={() => setMobileThreadOpen(false)}>
                  <ChevronRight className="h-5 w-5 rotate-180" />
                </button>
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#D9FDD3] text-sm font-extrabold text-[#075E54]">
                  {activeInitials}
                </div>
                <div className="min-w-0">
                  <div className="truncate font-extrabold text-[#111B21]">{active?.title || "WhatsApp chat"}</div>
                  <div className="truncate text-xs text-[#667781]">{active?.phone || "-"} | {active?.messages?.length || 0} messages</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {!!telPhone && (
                  <a className="flex h-9 w-9 items-center justify-center rounded-full text-[#54656F] hover:bg-[#D9DBDF]" href={`tel:${telPhone}`} title="Call">
                    <Phone className="h-4 w-4" />
                  </a>
                )}
                <button type="button" className="flex h-9 w-9 items-center justify-center rounded-full text-[#54656F] hover:bg-[#D9DBDF]" onClick={() => setSection("campaigns")} title="Open campaigns">
                  <Megaphone className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div
              className="flex-1 space-y-2 overflow-y-auto p-3 sm:p-5"
              style={{
                backgroundColor: "#efeae2",
                backgroundImage: "linear-gradient(45deg, rgba(255,255,255,0.18) 25%, transparent 25%), linear-gradient(-45deg, rgba(255,255,255,0.18) 25%, transparent 25%)",
                backgroundSize: "18px 18px",
              }}
            >
              {(active?.messages || []).map((msg, index) => {
                const outbound = msg.direction === "out";
                const text = whatsappActivityText(msg);
                const key = msg.id || msg.providerMessageId || `${msg.phone_key}-${index}`;
                return (
                  <div key={key} className={`flex ${outbound ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[86%] rounded-lg px-3 py-2 text-sm shadow-sm sm:max-w-[72%] ${outbound ? "bg-[#D9FDD3] text-[#111B21]" : "bg-white text-[#111B21]"}`}>
                      <div className="whitespace-pre-wrap break-words">{text}</div>
                      {msg.templateName && <div className="mt-2 rounded-md bg-black/5 px-2 py-1 text-[11px] text-[#667781]">Template: {msg.templateName}</div>}
                      {msg.failureReason && <div className="mt-2 text-xs text-red-700">{msg.failureReason}</div>}
                      <div className="mt-1 flex items-center justify-end gap-2 text-[11px] text-[#667781]">
                        {outbound && <span className={`rounded-sm border px-1.5 py-0.5 text-[10px] font-bold uppercase ${whatsappStatusTone(msg.status)}`}>{msg.status || "accepted"}</span>}
                        <span>{formatDateTime(msg.updatedAt || msg.createdAt)}</span>
                        <button type="button" className="text-red-500" title="Delete record" onClick={() => deleteMessage?.(msg)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              {!(active?.messages || []).length && (
                <div className="mx-auto mt-16 max-w-sm rounded-sm bg-white/80 p-4 text-center text-sm text-[#667781] shadow-sm">No messages in this conversation yet.</div>
              )}
            </div>
            <div className="border-t border-[#DADDE1] bg-[#F0F2F5] p-3">
              <div className="mb-2 flex gap-2 overflow-x-auto">
                <button type="button" className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-[#54656F] shadow-sm" onClick={() => setReplyText(suggestion)}>
                  Use suggested reply
                </button>
                <button type="button" className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-[#54656F] shadow-sm" onClick={() => setSection("templates")}>
                  Templates
                </button>
              </div>
              <div className="flex items-end gap-2">
                <textarea
                  className="max-h-32 min-h-11 flex-1 resize-none rounded-lg border-0 bg-white px-4 py-3 text-sm outline-none"
                  placeholder="Type a message"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendReply?.(active, replyText);
                    }
                  }}
                />
                <button type="button" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#00A884] text-white" onClick={() => sendReply?.(active, replyText)} title="Send reply">
                  <Send className="h-5 w-5" />
                </button>
              </div>
            </div>
          </main>

          <aside className="hidden min-h-0 flex-col border-l border-[#DADDE1] bg-white lg:flex">
            <div className="border-b border-[#E9EDEF] bg-[#F0F2F5] px-4 py-5">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#D9FDD3] text-xl font-bold text-[#075E54]">
                {activeInitials}
              </div>
              <div className="mt-3 text-center">
                <div className="truncate font-bold text-[#111B21]">{active?.title}</div>
                <div className="text-sm text-[#667781]">{activePhone}</div>
              </div>
            </div>
            <div className="space-y-3 overflow-y-auto p-4 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-sm bg-[#F0F2F5] p-3 text-center">
                  <div className="text-lg font-extrabold text-[#111B21]">{active?.messages?.filter((msg) => msg.direction !== "out").length || 0}</div>
                  <div className="text-xs text-[#667781]">Received</div>
                </div>
                <div className="rounded-sm bg-[#F0F2F5] p-3 text-center">
                  <div className="text-lg font-extrabold text-[#111B21]">{active?.messages?.filter((msg) => msg.direction === "out").length || 0}</div>
                  <div className="text-xs text-[#667781]">Sent</div>
                </div>
              </div>
              <div className="rounded-sm border border-[#E9EDEF] p-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-[#667781]">Lead Status</div>
                {activeLead ? (
                  <div className="grid gap-2">
                    <select className="h-9 rounded-md border border-[#E5E7EB] px-2 text-sm" value={activeLead.status || "New"} onChange={(e) => updateLead(activeLead.id, { status: e.target.value })}>
              <option>New</option><option>Contacted</option><option>Interested</option><option>Not Interested</option><option>Converted</option>
                    </select>
                    <select className="h-9 rounded-md border border-[#E5E7EB] px-2 text-sm" value={activeLead.whatsappConsent || "UNKNOWN"} onChange={(e) => updateLead(activeLead.id, { whatsappConsent: e.target.value, consentSource: "admin" })}>
                      <option>UNKNOWN</option><option>YES</option><option>NO</option>
                    </select>
                  </div>
                ) : (
                  <div className="text-[#667781]">No saved lead matched this number.</div>
                )}
              </div>
              <div className="rounded-sm border border-[#E9EDEF] p-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-[#667781]">Suggested Reply</div>
                <button type="button" className="w-full rounded-sm bg-[#F0F2F5] p-3 text-left text-[#111B21]" onClick={() => setReplyText(suggestion)}>
                  {suggestion}
                </button>
              </div>
              <div className="grid gap-2">
                <Button variant="outline" className="rounded-sm" onClick={() => setSection("campaigns")}><Megaphone className="h-4 w-4" /> Campaigns</Button>
                <Button variant="outline" className="rounded-sm" onClick={() => setSection("leads")}><Users className="h-4 w-4" /> Leads</Button>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function LeadCard({ lead, updateLead, compact = false }) {
  const consentTone = lead.whatsappConsent === "YES" ? "green" : lead.whatsappConsent === "NO" ? "red" : "amber";
  const phone = lead.whatsappNumber || lead.mobile || "";
  const mapUrl = lead.googlePlaceId ? `https://www.google.com/maps/place/?q=place_id:${lead.googlePlaceId}` : "";
  return (
    <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate font-semibold text-[#111827]">{lead.businessName}</h3>
          <div className="text-xs text-[#6B7280]">{lead.category || lead.city || lead.leadCode}</div>
        </div>
        <Pill tone={consentTone}>{lead.whatsappConsent || "UNKNOWN"}</Pill>
      </div>
      <div className="mb-3 flex flex-wrap gap-2">
        <Pill tone={lead.status === "Interested" ? "green" : "gray"}>{lead.status || "New"}</Pill>
        {lead.googleRating && <Pill tone="amber">Rating {lead.googleRating}</Pill>}
      </div>
      {!compact && (
        <div className="mb-3 grid grid-cols-2 gap-2">
          <a className="inline-flex h-10 items-center justify-center gap-2 rounded-sm border border-[#E5E7EB] text-sm font-semibold" href={`tel:${phone}`}><Phone className="h-4 w-4" /> Call</a>
          <a className="inline-flex h-10 items-center justify-center gap-2 rounded-sm border border-[#E5E7EB] text-sm font-semibold" href={`https://wa.me/${phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
          <a className="inline-flex h-10 items-center justify-center gap-2 rounded-sm border border-[#E5E7EB] text-sm font-semibold" href={lead.email ? `mailto:${lead.email}` : "#"}><Send className="h-4 w-4" /> Email</a>
          <a className="inline-flex h-10 items-center justify-center gap-2 rounded-sm border border-[#E5E7EB] text-sm font-semibold" href={mapUrl || "#"} target="_blank" rel="noreferrer"><Navigation className="h-4 w-4" /> Direction</a>
        </div>
      )}
      {!compact && (
        <div className="grid grid-cols-2 gap-2">
          <select className="h-9 rounded-md border border-[#E5E7EB] px-2 text-sm" value={lead.status || "New"} onChange={(e) => updateLead(lead.id, { status: e.target.value })}>
              <option>New</option><option>Contacted</option><option>Interested</option><option>Not Interested</option><option>Converted</option>
          </select>
          <select className="h-9 rounded-md border border-[#E5E7EB] px-2 text-sm" value={lead.whatsappConsent || "UNKNOWN"} onChange={(e) => updateLead(lead.id, { whatsappConsent: e.target.value, consentSource: "admin" })}>
            <option>UNKNOWN</option><option>YES</option><option>NO</option>
          </select>
        </div>
      )}
    </div>
  );
}

function LeadManagementView({
  leads,
  loading,
  filters,
  setFilters,
  cityOptions,
  sourceOptions,
  sort,
  setSort,
  visibleLeads,
  sortedCount,
  selectedLeadIds,
  setSelectedLeadIds,
  page,
  setPage,
  totalPages,
  pageSize,
  setPageSize,
  setDetailLead,
  updateLead,
  deleteLead,
  createLead,
  leadDraft,
  setLeadDraft,
  selectedLeadRows,
  setListModalOpen,
  setSection,
  bulkStatus,
  setBulkStatus,
  changeSelectedStatus,
  deleteSelectedLeads,
  exportSelectedCsv,
  setNotice,
  bulkConsent,
  setBulkConsent,
  changeSelectedConsent,
  archiveSelectedLeads,
}) {
  const visibleIds = visibleLeads.map((lead) => lead.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedLeadIds.includes(id));
  const toggleVisible = (checked) => {
    setSelectedLeadIds((ids) => {
      if (!checked) return ids.filter((id) => !visibleIds.includes(id));
      return [...new Set([...ids, ...visibleIds])];
    });
  };
  const toggleLead = (id, checked) => {
    setSelectedLeadIds((ids) => checked ? [...new Set([...ids, id])] : ids.filter((leadId) => leadId !== id));
  };
  const phoneFor = (lead) => lead.whatsappNumber || lead.whatsapp || lead.mobile || "";
  const mapUrlFor = (lead) => lead.googlePlaceId ? `https://www.google.com/maps/place/?q=place_id:${lead.googlePlaceId}` : "#";
  const selectedCount = selectedLeadIds.length;
  const categoryFor = (lead) => lead.categoryName || lead.category || "-";
  const ratingFor = (lead) => lead.rating || lead.googleRating || "";

  return (
    <div className="space-y-4">
      <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="font-display text-xl font-extrabold">Leads</h2>
            <p className="text-sm text-[#6B7280]">{sortedCount} leads found. {selectedCount ? `${selectedCount} selected.` : "Select rows for bulk actions."}</p>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:flex">
            <Input className="h-10 sm:w-60" placeholder="Business, mobile, category" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
            <select className="h-10 rounded-md border border-[#E5E7EB] px-3 text-sm" value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="createdAt_desc">Newest first</option>
              <option value="businessName_asc">Business A-Z</option>
              <option value="businessName_desc">Business Z-A</option>
              <option value="googleRating_desc">Rating high-low</option>
              <option value="city_asc">City A-Z</option>
              <option value="status_asc">Status A-Z</option>
            </select>
          </div>
        </div>

        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-5">
          <select className="h-10 rounded-md border border-[#E5E7EB] px-3 text-sm" value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })}>
            <option value="">All cities</option>
            {cityOptions.map((item) => <option key={item}>{item}</option>)}
          </select>
          <select className="h-10 rounded-md border border-[#E5E7EB] px-3 text-sm" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value="">All status</option>
            <option>New</option><option>Contacted</option><option>Interested</option><option>Not Interested</option><option>Converted</option>
          </select>
          <select className="h-10 rounded-md border border-[#E5E7EB] px-3 text-sm" value={filters.consent} onChange={(e) => setFilters({ ...filters, consent: e.target.value })}>
            <option value="">All consent</option><option>YES</option><option>NO</option><option>UNKNOWN</option>
          </select>
          <select className="h-10 rounded-md border border-[#E5E7EB] px-3 text-sm" value={filters.rating} onChange={(e) => setFilters({ ...filters, rating: e.target.value })}>
            <option value="">Any rating</option><option value="3">3+</option><option value="4">4+</option><option value="4.5">4.5+</option>
          </select>
          <select className="h-10 rounded-md border border-[#E5E7EB] px-3 text-sm" value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })}>
            <option value="">All sources</option>
            {sourceOptions.map((item) => <option key={item}>{item}</option>)}
          </select>
        </div>
      </div>

      {selectedCount > 0 && (
        <div className="sticky top-16 z-20 rounded-sm border border-[#111827] bg-white p-3 shadow-sm">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="font-semibold">{selectedCount} selected</div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="rounded-sm" onClick={() => toggleVisible(true)}>Select Visible</Button>
              <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setSelectedLeadIds([])}>Clear Selection</Button>
              <Button size="sm" className="rounded-sm" onClick={() => setListModalOpen(true)}><Plus className="h-4 w-4" /> Add to Contact List</Button>
              <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setSection("campaigns")}><MessageCircle className="h-4 w-4" /> Send WhatsApp</Button>
              <a className="inline-flex h-8 items-center justify-center gap-2 rounded-md border border-[#E5E7EB] px-3 text-xs font-medium" href={`mailto:?bcc=${selectedLeadRows.map((lead) => lead.email).filter(Boolean).join(",")}`}>
                <Mail className="h-4 w-4" /> Send Email
              </a>
              <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setNotice("Assign Dealer needs a dealer assignment endpoint. Selection is preserved.")}>Assign Dealer</Button>
              <select className="h-8 rounded-md border border-[#E5E7EB] px-2 text-xs" value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)}>
                <option>New</option><option>Contacted</option><option>Interested</option><option>Not Interested</option><option>Converted</option>
              </select>
              <Button size="sm" variant="outline" className="rounded-sm" onClick={changeSelectedStatus}>Change Status</Button>
              <Button size="sm" variant="outline" className="rounded-sm" onClick={exportSelectedCsv}><Download className="h-4 w-4" /> Export CSV</Button>
              <Button size="sm" variant="outline" className="rounded-sm" onClick={archiveSelectedLeads}><Archive className="h-4 w-4" /> Archive</Button>
              <Button size="sm" variant="destructive" className="rounded-sm" onClick={deleteSelectedLeads}><Trash2 className="h-4 w-4" /> Delete</Button>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
        <h3 className="font-display mb-3 text-lg font-extrabold">Quick Add</h3>
        <div className="grid gap-2 md:grid-cols-4">
          <Input placeholder="Business name" value={leadDraft.businessName} onChange={(e) => setLeadDraft({ ...leadDraft, businessName: e.target.value })} />
          <Input placeholder="Mobile" value={leadDraft.mobile} onChange={(e) => setLeadDraft({ ...leadDraft, mobile: e.target.value, whatsappNumber: e.target.value })} />
          <Input placeholder="City" value={leadDraft.city} onChange={(e) => setLeadDraft({ ...leadDraft, city: e.target.value })} />
          <Button className="rounded-sm" onClick={createLead}><Plus className="h-4 w-4" /> Add Lead</Button>
        </div>
      </div>

      <div className="hidden overflow-hidden rounded-sm border border-[#E5E7EB] bg-white lg:block">
        <div className="overflow-x-auto">
          <table className="min-w-[1280px] w-full text-left text-sm">
            <thead className="border-b border-[#E5E7EB] bg-[#F9FAFB] text-[11px] uppercase tracking-[0.12em] text-[#6B7280]">
              <tr>
                <th className="w-10 px-3 py-3"><input type="checkbox" checked={allVisibleSelected} onChange={(e) => toggleVisible(e.target.checked)} /></th>
                <th className="w-[280px] px-3 py-3 font-semibold">Business Name</th>
                <th className="w-[150px] px-3 py-3 font-semibold">Category</th>
                <th className="w-[120px] px-3 py-3 font-semibold">City</th>
                <th className="w-[140px] px-3 py-3 font-semibold">Mobile</th>
                <th className="w-[140px] px-3 py-3 font-semibold">WhatsApp</th>
                <th className="w-[190px] px-3 py-3 font-semibold">Email</th>
                <th className="w-[80px] px-3 py-3 font-semibold">Rating</th>
                <th className="w-[120px] px-3 py-3 font-semibold">Status</th>
                <th className="w-[120px] px-3 py-3 font-semibold">Consent</th>
                <th className="w-[130px] px-3 py-3 font-semibold">Source</th>
                <th className="w-[140px] px-3 py-3 font-semibold">Last Contact</th>
                <th className="w-[240px] px-3 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={13} className="px-3 py-10 text-center text-[#6B7280]">Loading leads...</td></tr>
              )}
              {!loading && visibleLeads.map((lead) => {
                const phone = phoneFor(lead);
                return (
                  <tr key={lead.id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]">
                    <td className="px-3 py-3"><input type="checkbox" checked={selectedLeadIds.includes(lead.id)} onChange={(e) => toggleLead(lead.id, e.target.checked)} /></td>
                    <td className="w-[280px] px-3 py-3 align-top">
                      <button type="button" title={lead.businessName} className="block max-w-[250px] whitespace-normal break-words text-left font-semibold leading-snug text-[#111827] hover:underline" onClick={() => setDetailLead(lead)}>{lead.businessName}</button>
                      <div className="text-xs text-[#6B7280]">{lead.leadCode}</div>
                    </td>
                    <td className="w-[150px] px-3 py-3 align-top">{categoryFor(lead)}</td>
                    <td className="w-[120px] px-3 py-3 align-top">{lead.city || "-"}</td>
                    <td className="w-[140px] whitespace-normal break-words px-3 py-3 align-top">{lead.mobile || "-"}</td>
                    <td className="w-[140px] whitespace-normal break-words px-3 py-3 align-top">{lead.whatsappNumber || lead.whatsapp || "-"}</td>
                    <td className="w-[190px] break-words px-3 py-3 align-top">{lead.email || "-"}</td>
                    <td className="w-[80px] px-3 py-3 align-top">{ratingFor(lead) || "-"}</td>
                    <td className="px-3 py-3"><Pill tone={lead.status === "Interested" ? "green" : "gray"}>{lead.status || "New"}</Pill></td>
                    <td className="px-3 py-3"><Pill tone={lead.whatsappConsent === "YES" ? "green" : lead.whatsappConsent === "NO" ? "red" : "amber"}>{lead.whatsappConsent || "UNKNOWN"}</Pill></td>
                    <td className="w-[130px] break-words px-3 py-3 align-top">{lead.source || "manual"}</td>
                    <td className="w-[140px] px-3 py-3 align-top">{lead.lastContact || lead.lastContactAt || lead.lastReplyAt || "-"}</td>
                    <td className="w-[240px] px-3 py-3 align-top">
                      <div className="flex items-center gap-1">
                        <button className="h-8 w-8 rounded-sm border border-[#E5E7EB]" title="View" onClick={() => setDetailLead(lead)}><Eye className="mx-auto h-4 w-4" /></button>
                        <a className="flex h-8 w-8 items-center justify-center rounded-sm border border-[#E5E7EB]" title="Call" href={`tel:${phone}`}><Phone className="h-4 w-4" /></a>
                        <a className="flex h-8 w-8 items-center justify-center rounded-sm border border-[#E5E7EB]" title="WhatsApp" href={`https://wa.me/${phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer"><MessageCircle className="h-4 w-4" /></a>
                        <a className="flex h-8 w-8 items-center justify-center rounded-sm border border-[#E5E7EB]" title="Direction" href={mapUrlFor(lead)} target="_blank" rel="noreferrer"><Navigation className="h-4 w-4" /></a>
                        <button className="h-8 w-8 rounded-sm border border-[#E5E7EB]" title="Send campaign/message" onClick={() => setSection("campaigns")}><Send className="mx-auto h-4 w-4" /></button>
                        <button className="h-8 w-8 rounded-sm border border-[#E5E7EB]" title="Edit" onClick={() => setDetailLead(lead)}><Edit className="mx-auto h-4 w-4" /></button>
                        <button className="h-8 w-8 rounded-sm border border-red-200 text-red-600" title="Delete" onClick={() => deleteLead(lead.id)}><Trash2 className="mx-auto h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && visibleLeads.length === 0 && (
                <tr><td colSpan={13} className="px-3 py-10 text-center text-[#6B7280]">No leads found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-3 lg:hidden">
        {loading && <div className="rounded-sm border border-[#E5E7EB] bg-white p-6 text-center text-sm text-[#6B7280]">Loading leads...</div>}
        {!loading && visibleLeads.map((lead) => {
          const phone = phoneFor(lead);
          return (
            <div key={lead.id} className="rounded-sm border border-[#E5E7EB] bg-white p-4">
              <div className="mb-3 flex items-start gap-3">
                <input className="mt-1 h-5 w-5" type="checkbox" checked={selectedLeadIds.includes(lead.id)} onChange={(e) => toggleLead(lead.id, e.target.checked)} />
                <button type="button" className="min-w-0 flex-1 text-left" onClick={() => setDetailLead(lead)}>
                  <div className="break-words font-semibold" title={lead.businessName}>{lead.businessName}</div>
                  <div className="text-xs text-[#6B7280]">{categoryFor(lead)} / {lead.city || "-"}</div>
                </button>
                <Pill tone={lead.status === "Interested" ? "green" : "gray"}>{lead.status || "New"}</Pill>
              </div>
              <div className="mb-3 flex flex-wrap gap-2">
                <Pill tone="amber">Rating {ratingFor(lead) || "-"}</Pill>
                <Pill tone={lead.whatsappConsent === "YES" ? "green" : lead.whatsappConsent === "NO" ? "red" : "amber"}>{lead.whatsappConsent || "UNKNOWN"}</Pill>
              </div>
              <select className="h-10 w-full rounded-md border border-[#E5E7EB] px-3 text-sm" onChange={(e) => {
                const action = e.target.value;
                e.target.value = "";
                if (action === "view") setDetailLead(lead);
                if (action === "call") window.location.href = `tel:${phone}`;
                if (action === "wa") window.open(`https://wa.me/${phone.replace(/\D/g, "")}`, "_blank", "noopener,noreferrer");
                if (action === "campaign") setSection("campaigns");
                if (action === "dir") window.open(mapUrlFor(lead), "_blank", "noopener,noreferrer");
                if (action === "delete") deleteLead(lead.id);
              }}>
                <option value="">Quick actions</option>
                <option value="view">View / Edit</option>
                <option value="call">Call</option>
                <option value="wa">WhatsApp</option>
                <option value="campaign">Send Campaign</option>
                <option value="dir">Direction</option>
                <option value="delete">Delete</option>
              </select>
            </div>
          );
        })}
        {!loading && visibleLeads.length === 0 && <div className="rounded-sm border border-[#E5E7EB] bg-white p-6 text-center text-sm text-[#6B7280]">No leads found</div>}
      </div>

      <div className="flex flex-col gap-3 rounded-sm border border-[#E5E7EB] bg-white p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-[#6B7280]">Page {Math.min(page, totalPages)} of {totalPages} / {sortedCount} leads</div>
        <div className="flex items-center gap-2">
          <select className="h-9 rounded-md border border-[#E5E7EB] px-2 text-sm" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
            <option value={25}>25 rows</option><option value={50}>50 rows</option><option value={100}>100 rows</option>
          </select>
          <Button variant="outline" className="rounded-sm" disabled={page <= 1} onClick={() => setPage(Math.max(1, page - 1))}>Previous</Button>
          <Button variant="outline" className="rounded-sm" disabled={page >= totalPages} onClick={() => setPage(Math.min(totalPages, page + 1))}>Next</Button>
        </div>
      </div>

      {selectedCount > 0 && (
        <div className="fixed inset-x-0 bottom-14 z-50 border-t border-[#E5E7EB] bg-white p-2 shadow-[0_-8px_24px_rgba(15,23,42,0.12)] lg:hidden">
          <div className="grid grid-cols-4 gap-2">
            <Button size="sm" className="rounded-sm" onClick={() => setListModalOpen(true)}>List</Button>
            <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setSection("campaigns")}>WhatsApp</Button>
            <Button size="sm" variant="outline" className="rounded-sm" onClick={exportSelectedCsv}>CSV</Button>
            <Button size="sm" variant="destructive" className="rounded-sm" onClick={deleteSelectedLeads}>Delete</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function LeadCategoryManager({ categories, draft, setDraft, editingId, setEditingId, saveCategory, deleteCategory }) {
  const editCategory = (category) => {
    setEditingId(category.id);
    setDraft({
      categoryName: category.categoryName || "",
      categoryType: category.categoryType || "Custom",
      country: category.country || "",
      state: category.state || "",
      city: category.city || "",
      area: category.area || "",
      pincode: category.pincode || "",
      notes: category.notes || "",
      isActive: category.isActive !== false,
    });
  };
  const reset = () => {
    setEditingId("");
    setDraft(defaultCategoryDraft);
  };
  return (
    <div className="grid gap-5 xl:grid-cols-[420px_1fr]">
      <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
        <h2 className="font-display mb-4 text-xl font-extrabold">{editingId ? "Edit Contact List Category" : "Add Contact List Category"}</h2>
        <div className="grid gap-3">
          <Field label="Contact List Category Name"><Input value={draft.categoryName} onChange={(e) => setDraft({ ...draft, categoryName: e.target.value })} /></Field>
          <Field label="Category Type">
            <select className="h-10 w-full rounded-md border border-[#E5E7EB] px-3 text-sm" value={draft.categoryType} onChange={(e) => setDraft({ ...draft, categoryType: e.target.value })}>
              <option>Area Wise</option><option>Region Wise</option><option>State Wise</option><option>City Wise</option><option>Business Type Wise</option><option>Custom</option>
            </select>
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Country"><Input value={draft.country} onChange={(e) => setDraft({ ...draft, country: e.target.value })} /></Field>
            <Field label="State"><Input value={draft.state} onChange={(e) => setDraft({ ...draft, state: e.target.value })} /></Field>
            <Field label="City"><Input value={draft.city} onChange={(e) => setDraft({ ...draft, city: e.target.value })} /></Field>
            <Field label="Area"><Input value={draft.area} onChange={(e) => setDraft({ ...draft, area: e.target.value })} /></Field>
            <Field label="Pincode"><Input value={draft.pincode} onChange={(e) => setDraft({ ...draft, pincode: e.target.value })} /></Field>
            <label className="mt-6 flex items-center gap-2 text-sm font-semibold text-[#374151]">
              <input type="checkbox" checked={draft.isActive} onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })} />
              Active
            </label>
          </div>
          <Field label="Notes"><Textarea value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} /></Field>
          <div className="grid gap-2 sm:grid-cols-2">
            <Button className="rounded-sm" onClick={() => saveCategory()}><Plus className="h-4 w-4" /> {editingId ? "Update Category" : "Add Category"}</Button>
            <Button variant="outline" className="rounded-sm" onClick={reset}>Clear</Button>
          </div>
        </div>
      </div>
      <div className="rounded-sm border border-[#E5E7EB] bg-white">
        <div className="border-b border-[#E5E7EB] p-4">
          <h2 className="font-display text-xl font-extrabold">Contact List Categories</h2>
          <p className="text-sm text-[#6B7280]">{categories.length} categories available for Lead Finder, saved leads, and campaigns.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-left text-sm">
            <thead className="border-b border-[#E5E7EB] bg-[#F9FAFB] text-[11px] uppercase tracking-[0.12em] text-[#6B7280]">
              <tr>
                <th className="px-3 py-3">Name</th>
                <th className="px-3 py-3">Type</th>
                <th className="px-3 py-3">Location</th>
                <th className="px-3 py-3">Leads</th>
                <th className="px-3 py-3">Status</th>
                <th className="w-32 px-3 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => (
                <tr key={category.id} className="border-b border-[#F3F4F6]">
                  <td className="px-3 py-3 font-semibold">{category.categoryName}</td>
                  <td className="px-3 py-3">{category.categoryType}</td>
                  <td className="px-3 py-3">{[category.area, category.city, category.state, category.pincode].filter(Boolean).join(", ") || "-"}</td>
                  <td className="px-3 py-3">{category.leadCount || 0}</td>
                  <td className="px-3 py-3"><Pill tone={category.isActive === false ? "gray" : "green"}>{category.isActive === false ? "Inactive" : "Active"}</Pill></td>
                  <td className="px-3 py-3">
                    <div className="flex gap-1">
                      <button className="h-8 w-8 rounded-sm border border-[#E5E7EB]" title="Edit category" onClick={() => editCategory(category)}><Edit className="mx-auto h-4 w-4" /></button>
                      <button className="h-8 w-8 rounded-sm border border-red-200 text-red-600" title="Delete category" onClick={() => deleteCategory(category)}><Trash2 className="mx-auto h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!categories.length && (
                <tr><td colSpan={6} className="px-3 py-10 text-center text-[#6B7280]">No contact list categories yet. Create Area Wise, City Wise, Business Type Wise, or Custom categories here.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ContactListManager({ lists, leads, draft, setDraft, editingId, setEditingId, saveList, deleteList, deleteListsBulk, removeContactsFromList, moveContactsToList }) {
  const realLists = lists.filter((list) => !String(list.id || "").startsWith("category:") && list.source !== "lead_category");
  const [formOpen, setFormOpen] = useState(false);
  const [viewingListId, setViewingListId] = useState("");
  const [selectedListIds, setSelectedListIds] = useState([]);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [moveTargetListId, setMoveTargetListId] = useState("");
  const [contactFilters, setContactFilters] = useState({ search: "", city: "", area: "", pincode: "", rating: "" });
  const leadById = useMemo(() => new Map(leads.map((lead) => [lead.id, lead])), [leads]);
  const selectedList = realLists.find((list) => list.id === viewingListId) || realLists.find((list) => list.id === editingId) || null;
  const selectedContacts = (selectedList?.leadIds || draft.leadIds || []).map((id) => leadById.get(id)).filter(Boolean);
  const filteredContacts = useMemo(() => {
    const q = contactFilters.search.trim().toLowerCase();
    return selectedContacts.filter((lead) => {
      const rating = Number(lead.rating || lead.googleRating || 0);
      const haystack = [lead.businessName, lead.mobile, lead.whatsappNumber, lead.email, lead.fullAddress, lead.address, lead.area, lead.city, lead.state, lead.pincode].join(" ").toLowerCase();
      if (q && !haystack.includes(q)) return false;
      if (contactFilters.city && lead.city !== contactFilters.city) return false;
      if (contactFilters.area && lead.area !== contactFilters.area) return false;
      if (contactFilters.pincode && lead.pincode !== contactFilters.pincode) return false;
      if (contactFilters.rating && rating < Number(contactFilters.rating)) return false;
      return true;
    });
  }, [selectedContacts, contactFilters]);
  const cityOptions = useMemo(() => [...new Set(selectedContacts.map((lead) => lead.city).filter(Boolean))].sort(), [selectedContacts]);
  const areaOptions = useMemo(() => [...new Set(selectedContacts.map((lead) => lead.area).filter(Boolean))].sort(), [selectedContacts]);
  const pincodeOptions = useMemo(() => [...new Set(selectedContacts.map((lead) => lead.pincode).filter(Boolean))].sort(), [selectedContacts]);
  const visibleListIds = realLists.map((list) => list.id);
  const allListsSelected = visibleListIds.length > 0 && visibleListIds.every((id) => selectedListIds.includes(id));
  const visibleContactIds = filteredContacts.map((lead) => lead.id);
  const allContactsSelected = visibleContactIds.length > 0 && visibleContactIds.every((id) => selectedContactIds.includes(id));
  useEffect(() => {
    setSelectedContactIds([]);
    setMoveTargetListId("");
  }, [selectedList?.id]);
  const openList = (list) => {
    setViewingListId(list.id);
    setFormOpen(false);
  };
  const editList = (list) => {
    setViewingListId(list.id);
    setFormOpen(true);
    setEditingId(list.id);
    setDraft({
      name: list.name || "",
      description: list.description || "",
      leadIds: list.leadIds || [],
      tags: list.tags || [],
    });
  };
  const reset = () => {
    setEditingId("");
    setDraft({ name: "", description: "", leadIds: [] });
    setFormOpen(false);
  };
  const startNewList = () => {
    setEditingId("");
    setDraft({ name: "", description: "", leadIds: [] });
    setFormOpen((open) => !open);
  };
  const submitList = async () => {
    await saveList();
    setFormOpen(false);
  };
  const toggleAllLists = (checked) => {
    setSelectedListIds(checked ? visibleListIds : []);
  };
  const toggleList = (id, checked) => {
    setSelectedListIds((ids) => checked ? [...new Set([...ids, id])] : ids.filter((item) => item !== id));
  };
  const bulkDeleteSelected = async () => {
    await deleteListsBulk(selectedListIds);
    if (selectedListIds.includes(viewingListId)) setViewingListId("");
    setSelectedListIds([]);
  };
  const toggleAllContacts = (checked) => {
    setSelectedContactIds(checked ? visibleContactIds : []);
  };
  const toggleContact = (id, checked) => {
    setSelectedContactIds((ids) => checked ? [...new Set([...ids, id])] : ids.filter((item) => item !== id));
  };
  const removeSelectedContacts = async () => {
    await removeContactsFromList(selectedList?.id, selectedContactIds);
    setSelectedContactIds([]);
  };
  const moveSelectedContacts = async () => {
    await moveContactsToList(selectedList?.id, moveTargetListId, selectedContactIds);
    setSelectedContactIds([]);
    setMoveTargetListId("");
  };
  return (
    <div className="space-y-5">
      <div className="rounded-sm border border-[#E5E7EB] bg-white">
        <div className="flex flex-col gap-3 border-b border-[#E5E7EB] p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-display text-xl font-extrabold">Contact Lists</h2>
            <p className="text-sm text-[#6B7280]">{realLists.length} editable recipient lists. Leads stay saved if a list is deleted.</p>
          </div>
          <Button className="rounded-sm" onClick={startNewList}><Plus className="h-4 w-4" /> Add Contact List</Button>
        </div>
        {selectedListIds.length > 0 && (
          <div className="flex flex-col gap-3 border-b border-[#E5E7EB] bg-[#F9FAFB] p-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm font-semibold text-[#111827]">{selectedListIds.length} contact list(s) selected</div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setSelectedListIds([])}>Clear</Button>
              <Button size="sm" variant="destructive" className="rounded-sm" onClick={bulkDeleteSelected}><Trash2 className="h-4 w-4" /> Delete Selected</Button>
            </div>
          </div>
        )}
        {formOpen && (
          <div className="border-b border-[#E5E7EB] bg-[#F9FAFB] p-4">
            <div className="grid gap-3 lg:grid-cols-[minmax(220px,320px)_1fr_auto] lg:items-end">
              <Field label="List Name">
                <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Borivali CCTV Installer" />
              </Field>
              <Field label="Description">
                <Input value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="Optional notes for this recipient list" />
              </Field>
              <div className="flex flex-wrap items-center gap-2">
                <Pill tone="blue">{draft.leadIds?.length || 0} contacts</Pill>
                <Button className="rounded-sm" onClick={submitList}><Plus className="h-4 w-4" /> {editingId ? "Update" : "Add"}</Button>
                <Button variant="outline" className="rounded-sm" onClick={reset}>Close</Button>
              </div>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="border-b border-[#E5E7EB] bg-[#F9FAFB] text-[11px] uppercase tracking-[0.12em] text-[#6B7280]">
              <tr>
                <th className="w-10 px-3 py-3"><input type="checkbox" checked={allListsSelected} onChange={(e) => toggleAllLists(e.target.checked)} /></th>
                <th className="px-3 py-3">Name</th>
                <th className="px-3 py-3">Contacts</th>
                <th className="px-3 py-3">Description</th>
                <th className="w-40 px-3 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {realLists.map((list) => (
                <tr key={list.id} className={`border-b border-[#F3F4F6] ${selectedList?.id === list.id ? "bg-blue-50/50" : ""}`}>
                  <td className="px-3 py-3"><input type="checkbox" checked={selectedListIds.includes(list.id)} onChange={(e) => toggleList(list.id, e.target.checked)} /></td>
                  <td className="px-3 py-3">
                    <button type="button" className="text-left font-semibold hover:underline" onClick={() => openList(list)}>{list.name}</button>
                  </td>
                  <td className="px-3 py-3">{list.leadCount || list.leadIds?.length || 0}</td>
                  <td className="px-3 py-3 text-[#6B7280]">{list.description || "-"}</td>
                  <td className="px-3 py-3">
                    <div className="flex gap-1">
                      <button className="h-8 w-8 rounded-sm border border-[#E5E7EB]" title="View contacts" onClick={() => openList(list)}><Eye className="mx-auto h-4 w-4" /></button>
                      <button className="h-8 w-8 rounded-sm border border-[#E5E7EB]" title="Edit contact list" onClick={() => editList(list)}><Edit className="mx-auto h-4 w-4" /></button>
                      <button className="h-8 w-8 rounded-sm border border-red-200 text-red-600" title="Delete contact list" onClick={() => deleteList(list)}><Trash2 className="mx-auto h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!realLists.length && (
                <tr><td colSpan={5} className="px-3 py-10 text-center text-[#6B7280]">No saved contact lists yet. Select leads, then use Add to Contact List.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="rounded-sm border border-[#E5E7EB] bg-white">
        <div className="border-b border-[#E5E7EB] p-4">
          <h2 className="font-display text-xl font-extrabold">{selectedList ? selectedList.name : "Open a Contact List"}</h2>
          <p className="text-sm text-[#6B7280]">{filteredContacts.length} of {selectedContacts.length} contact(s) shown with complete details.</p>
        </div>
        {selectedList && (
          <div className="space-y-3 border-b border-[#E5E7EB] bg-[#F9FAFB] p-3">
            <div className="grid gap-2 md:grid-cols-5">
              <Input className="h-10" placeholder="Search company, phone, address" value={contactFilters.search} onChange={(e) => setContactFilters({ ...contactFilters, search: e.target.value })} />
              <select className="h-10 rounded-md border border-[#E5E7EB] px-3 text-sm" value={contactFilters.city} onChange={(e) => setContactFilters({ ...contactFilters, city: e.target.value })}>
                <option value="">All cities</option>
                {cityOptions.map((item) => <option key={item}>{item}</option>)}
              </select>
              <select className="h-10 rounded-md border border-[#E5E7EB] px-3 text-sm" value={contactFilters.area} onChange={(e) => setContactFilters({ ...contactFilters, area: e.target.value })}>
                <option value="">All areas</option>
                {areaOptions.map((item) => <option key={item}>{item}</option>)}
              </select>
              <select className="h-10 rounded-md border border-[#E5E7EB] px-3 text-sm" value={contactFilters.pincode} onChange={(e) => setContactFilters({ ...contactFilters, pincode: e.target.value })}>
                <option value="">All pincodes</option>
                {pincodeOptions.map((item) => <option key={item}>{item}</option>)}
              </select>
              <select className="h-10 rounded-md border border-[#E5E7EB] px-3 text-sm" value={contactFilters.rating} onChange={(e) => setContactFilters({ ...contactFilters, rating: e.target.value })}>
                <option value="">Any rating</option><option value="3">3+</option><option value="4">4+</option><option value="4.5">4.5+</option>
              </select>
            </div>
            {selectedContactIds.length > 0 && (
              <div className="flex flex-col gap-2 rounded-sm border border-[#111827] bg-white p-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="font-semibold">{selectedContactIds.length} contact(s) selected</div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setSelectedContactIds([])}>Clear</Button>
                  <Button size="sm" variant="outline" className="rounded-sm" onClick={removeSelectedContacts}><Trash2 className="h-4 w-4" /> Delete From List</Button>
                  <select className="h-8 rounded-md border border-[#E5E7EB] px-2 text-xs" value={moveTargetListId} onChange={(e) => setMoveTargetListId(e.target.value)}>
                    <option value="">Move to list</option>
                    {realLists.filter((list) => list.id !== selectedList.id).map((list) => <option key={list.id} value={list.id}>{list.name}</option>)}
                  </select>
                  <Button size="sm" className="rounded-sm" onClick={moveSelectedContacts}>Move Selected</Button>
                </div>
              </div>
            )}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1280px] text-left text-sm">
            <thead className="border-b border-[#E5E7EB] bg-[#F9FAFB] text-[11px] uppercase tracking-[0.12em] text-[#6B7280]">
              <tr>
                <th className="w-10 px-3 py-3"><input type="checkbox" checked={allContactsSelected} onChange={(e) => toggleAllContacts(e.target.checked)} disabled={!filteredContacts.length} /></th>
                <th className="px-3 py-3">Company</th>
                <th className="px-3 py-3">Mobile</th>
                <th className="px-3 py-3">WhatsApp</th>
                <th className="px-3 py-3">Email</th>
                <th className="px-3 py-3">Address</th>
                <th className="px-3 py-3">Area</th>
                <th className="px-3 py-3">City</th>
                <th className="px-3 py-3">State</th>
                <th className="px-3 py-3">Pincode</th>
                <th className="px-3 py-3">Rating</th>
                <th className="px-3 py-3">Opt-in</th>
              </tr>
            </thead>
            <tbody>
              {filteredContacts.map((lead) => (
                <tr key={lead.id} className="border-b border-[#F3F4F6] align-top">
                  <td className="px-3 py-3"><input type="checkbox" checked={selectedContactIds.includes(lead.id)} onChange={(e) => toggleContact(lead.id, e.target.checked)} /></td>
                  <td className="max-w-[260px] break-words px-3 py-3 font-semibold">{lead.businessName || "-"}</td>
                  <td className="break-words px-3 py-3">{lead.mobile || "-"}</td>
                  <td className="break-words px-3 py-3">{lead.whatsappNumber || lead.whatsapp || "-"}</td>
                  <td className="max-w-[220px] break-words px-3 py-3">{lead.email || "-"}</td>
                  <td className="max-w-[340px] break-words px-3 py-3">{lead.fullAddress || lead.address || "-"}</td>
                  <td className="break-words px-3 py-3">{lead.area || "-"}</td>
                  <td className="break-words px-3 py-3">{lead.city || "-"}</td>
                  <td className="break-words px-3 py-3">{lead.state || "-"}</td>
                  <td className="break-words px-3 py-3">{lead.pincode || "-"}</td>
                  <td className="px-3 py-3">{lead.rating || lead.googleRating || "-"}</td>
                  <td className="px-3 py-3"><Pill tone={lead.whatsappConsent === "NO" ? "red" : "green"}>{lead.whatsappConsent === "NO" ? "NO" : "YES"}</Pill></td>
                </tr>
              ))}
              {!filteredContacts.length && (
                <tr><td colSpan={12} className="px-3 py-10 text-center text-[#6B7280]">{selectedList ? "No contacts match the current filters." : "Click the view button on any contact list to view all contact details."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function LeadDetailDrawer({ lead, inbox, updateLead, onClose }) {
  const phone = lead.whatsappNumber || lead.whatsapp || lead.mobile || "";
  const mapUrl = lead.googlePlaceId ? `https://www.google.com/maps/place/?q=place_id:${lead.googlePlaceId}` : "";
  return (
    <div className="fixed inset-0 z-[65] bg-black/30">
      <div className="ml-auto flex h-full w-full max-w-xl flex-col bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-[#E5E7EB] p-4">
          <div>
            <h2 className="font-display text-2xl font-extrabold">{lead.businessName}</h2>
            <p className="text-sm text-[#6B7280]">{lead.categoryName || lead.category || "-"} / {lead.city || "-"}</p>
          </div>
          <button type="button" className="flex h-9 w-9 items-center justify-center rounded-sm border border-[#E5E7EB]" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 space-y-5 overflow-y-auto p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Info label="Lead Code" value={lead.leadCode} />
            <Info label="Rating" value={lead.rating || lead.googleRating || "-"} />
            <Info label="Mobile" value={lead.mobile || "-"} />
            <Info label="WhatsApp" value={lead.whatsappNumber || lead.whatsapp || "-"} />
            <Info label="Email" value={lead.email || "-"} />
            <Info label="Website" value={lead.website || "-"} />
            <Info label="Source" value={lead.source || "manual"} />
            <Info label="Last Contact" value={lead.lastContactAt || lead.lastReplyAt || "-"} />
          </div>
          <div>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6B7280]">Address</div>
            <div className="rounded-sm bg-[#F9FAFB] p-3 text-sm">{lead.fullAddress || lead.address || "-"}</div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Status">
              <select className="h-10 w-full rounded-md border border-[#E5E7EB] px-3 text-sm" value={lead.status || "New"} onChange={(e) => updateLead(lead.id, { status: e.target.value })}>
                <option>New</option><option>Contacted</option><option>Interested</option><option>Not Interested</option><option>Converted</option>
              </select>
            </Field>
            <Field label="Consent">
              <select className="h-10 w-full rounded-md border border-[#E5E7EB] px-3 text-sm" value={lead.whatsappConsent || "UNKNOWN"} onChange={(e) => updateLead(lead.id, { whatsappConsent: e.target.value, consentSource: "admin" })}>
                <option>UNKNOWN</option><option>YES</option><option>NO</option>
              </select>
            </Field>
          </div>
          <Field label="Notes">
            <Textarea defaultValue={lead.notes || ""} onBlur={(e) => updateLead(lead.id, { notes: e.target.value })} />
          </Field>
          <Field label="Follow-up">
            <Input type="datetime-local" value={(lead.nextFollowUpAt || "").slice(0, 16)} onChange={(e) => updateLead(lead.id, { nextFollowUpAt: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <a className="inline-flex h-10 items-center justify-center gap-2 rounded-sm border border-[#E5E7EB] text-sm font-semibold" href={`tel:${phone}`}><Phone className="h-4 w-4" /> Call</a>
            <a className="inline-flex h-10 items-center justify-center gap-2 rounded-sm border border-[#E5E7EB] text-sm font-semibold" href={`https://wa.me/${phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
            <a className="inline-flex h-10 items-center justify-center gap-2 rounded-sm border border-[#E5E7EB] text-sm font-semibold" href={lead.website || "#"} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4" /> Website</a>
            <a className="inline-flex h-10 items-center justify-center gap-2 rounded-sm border border-[#E5E7EB] text-sm font-semibold" href={mapUrl || "#"} target="_blank" rel="noreferrer"><Navigation className="h-4 w-4" /> Map</a>
          </div>
          <div>
            <h3 className="font-display mb-2 text-lg font-extrabold">WhatsApp history</h3>
            <div className="space-y-2">
              {inbox.map((msg) => <div key={msg.id} className="rounded-sm bg-[#F3F4F6] p-3 text-sm">{msg.text || msg.messageType}</div>)}
              {!inbox.length && <div className="text-sm text-[#6B7280]">No WhatsApp replies yet.</div>}
            </div>
          </div>
          <div>
            <h3 className="font-display mb-2 text-lg font-extrabold">Campaign history</h3>
            <div className="text-sm text-[#6B7280]">Campaign logs will appear after WhatsApp campaigns are sent.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-sm border border-[#E5E7EB] p-3">
      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6B7280]">{label}</div>
      <div className="mt-1 break-words text-sm font-semibold text-[#111827]">{value}</div>
    </div>
  );
}

function WhatsAppTemplateManager({
  draft,
  setDraft,
  templates,
  saveTemplate,
  submitTemplateToMeta,
  syncTemplates,
  editTemplate,
  duplicateTemplate,
  deleteTemplate,
  syncTemplateStatus,
  uploadTemplateMedia,
  busy,
}) {
  const normalizedStatus = (status) => String(status || "DRAFT").toUpperCase();
  const statusTone = (status) => {
    const value = normalizedStatus(status);
    if (value === "APPROVED") return "green";
    if (value === "REJECTED" || value === "DISABLED") return "red";
    if (value === "PAUSED") return "blue";
    return "amber";
  };
  const updateButton = (index, patch) => {
    const next = [...(draft.buttons || [])];
    next[index] = { ...next[index], ...patch };
    setDraft({ ...draft, buttons: next });
  };
  const addButton = () => {
    if ((draft.buttons || []).length >= 3) return;
    setDraft({ ...draft, buttons: [...(draft.buttons || []), { type: "QUICK_REPLY", text: "Quick Reply", phoneNumber: "", url: "" }] });
  };
  const removeButton = (index) => setDraft({ ...draft, buttons: (draft.buttons || []).filter((_, i) => i !== index) });
  const insertVariable = (token) => {
    const nextBody = `${draft.bodyText || ""}${draft.bodyText ? " " : ""}${token}`;
    setDraft({ ...draft, bodyText: nextBody, bodyPreview: nextBody });
  };

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
      <div className="space-y-5">
        <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="font-display text-2xl font-extrabold">WhatsApp Template Manager</h2>
              <p className="text-sm text-[#6B7280]">Create templates here and submit them to Meta for approval without opening the Meta dashboard.</p>
            </div>
            <Button variant="outline" className="rounded-sm" onClick={syncTemplates} disabled={busy}>
              <Download className="h-4 w-4" /> Sync Templates
            </Button>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <Field label="Template Name">
              <Input value={draft.templateName} onChange={(e) => setDraft({ ...draft, templateName: e.target.value })} placeholder="restaurant_offer_demo" />
            </Field>
            <Field label="Category">
              <select className="h-10 w-full rounded-md border border-[#E5E7EB] px-3 text-sm" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })}>
                <option>Marketing</option><option>Utility</option><option>Authentication</option>
              </select>
            </Field>
            <Field label="Language">
              <Input value={draft.languageCode} onChange={(e) => setDraft({ ...draft, languageCode: e.target.value })} placeholder="en_US" />
            </Field>
            <Field label="Header Type">
              <select className="h-10 w-full rounded-md border border-[#E5E7EB] px-3 text-sm" value={draft.headerType} onChange={(e) => setDraft({ ...draft, headerType: e.target.value })}>
                <option>None</option><option>Text</option><option>Image</option><option>Video</option><option>Document</option>
              </select>
            </Field>
            {draft.headerType === "Text" && (
              <div className="lg:col-span-2">
                <Field label="Header Text">
                  <Input value={draft.headerText} onChange={(e) => setDraft({ ...draft, headerText: e.target.value })} placeholder="RP Digital Menu Board Offer" />
                </Field>
              </div>
            )}
            {["Image", "Video", "Document"].includes(draft.headerType) && (
              <div className="lg:col-span-3">
                <Field label="Header Media">
                  <div className="grid gap-3 lg:grid-cols-3">
                    <label className="flex h-11 cursor-pointer items-center justify-center gap-2 rounded-sm border border-[#E5E7EB] text-sm font-semibold">
                      <Upload className="h-4 w-4" /> Upload Image
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadTemplateMedia(e.target.files?.[0])} />
                    </label>
                    <label className="flex h-11 cursor-pointer items-center justify-center gap-2 rounded-sm border border-[#E5E7EB] text-sm font-semibold">
                      <Upload className="h-4 w-4" /> Upload Video
                      <input type="file" accept="video/*" className="hidden" onChange={(e) => uploadTemplateMedia(e.target.files?.[0])} />
                    </label>
                    <label className="flex h-11 cursor-pointer items-center justify-center gap-2 rounded-sm border border-[#E5E7EB] text-sm font-semibold">
                      <Upload className="h-4 w-4" /> Upload PDF
                      <input type="file" accept="application/pdf" className="hidden" onChange={(e) => uploadTemplateMedia(e.target.files?.[0])} />
                    </label>
                  </div>
                  <Input className="mt-3" value={draft.mediaUrl || ""} onChange={(e) => setDraft({ ...draft, mediaUrl: e.target.value })} placeholder="Or paste public header media URL" />
                  {draft.metaHeaderHandle ? (
                    <p className="mt-2 text-xs text-emerald-700">Meta sample media is ready for template submission.</p>
                  ) : (
                    <p className="mt-2 text-xs text-amber-700">{draft.metaHeaderError || "Upload media here before submitting Image/Video/Document templates to Meta."}</p>
                  )}
                </Field>
              </div>
            )}
            <div className="lg:col-span-3">
              <Field label="Body Text">
                <Textarea
                  className="min-h-36"
                  value={draft.bodyText}
                  onChange={(e) => setDraft({ ...draft, bodyText: e.target.value, bodyPreview: e.target.value })}
                  placeholder="Hello {{1}}, upgrade {{2}} with RP Digital Signage. Free demo available. Call {{3}}."
                />
              </Field>
              <div className="mt-2 flex flex-wrap gap-2">
                {(draft.variables || ["{{1}}", "{{2}}", "{{3}}"]).map((token) => (
                  <button key={token} type="button" className="rounded-sm border border-[#E5E7EB] px-2 py-1 text-xs font-semibold" onClick={() => insertVariable(token)}>
                    {token}
                  </button>
                ))}
              </div>
              {detectTemplateVariableCount(draft.bodyText) > 0 && (
                <div className="mt-4 rounded-sm border border-[#E5E7EB] bg-[#F9FAFB] p-4">
                  <p className="mb-2 text-sm font-semibold text-[#111827]">Example values for {"{{1}}"}, {"{{2}}"}, ...</p>
                  <p className="mb-3 text-xs text-[#6B7280]">Give Meta a realistic example for each placeholder - matching the real shape (a date, an amount with commas, a name) avoids sends failing after approval with a parameter-format-mismatch error. Leave blank to use a generic default.</p>
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {Array.from({ length: detectTemplateVariableCount(draft.bodyText) }, (_, i) => i).map((i) => (
                      <Input
                        key={i}
                        value={(draft.variableExamples || [])[i] || ""}
                        placeholder={`Example for {{${i + 1}}}`}
                        onChange={(e) => {
                          const next = [...(draft.variableExamples || [])];
                          next[i] = e.target.value;
                          setDraft({ ...draft, variableExamples: next });
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="lg:col-span-3">
              <Field label="Footer Text">
                <Input value={draft.footerText} onChange={(e) => setDraft({ ...draft, footerText: e.target.value })} placeholder="Reply STOP to opt out" />
              </Field>
            </div>
          </div>
        </div>

        <TemplateButtonEditor draft={draft} updateButton={updateButton} addButton={addButton} removeButton={removeButton} />

        <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="rounded-sm" onClick={saveTemplate} disabled={busy}>Save Draft</Button>
            <Button className="rounded-sm" onClick={submitTemplateToMeta} disabled={busy}>
              <Send className="h-4 w-4" /> Submit to Meta
            </Button>
          </div>
        </div>

        <TemplateList templates={templates} statusTone={statusTone} normalizedStatus={normalizedStatus} editTemplate={editTemplate} duplicateTemplate={duplicateTemplate} deleteTemplate={deleteTemplate} syncTemplateStatus={syncTemplateStatus} />
      </div>

      <TemplatePreview draft={draft} templates={templates} normalizedStatus={normalizedStatus} />
    </div>
  );
}

function TemplateButtonEditor({ draft, updateButton, addButton, removeButton }) {
  return (
    <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="font-display text-xl font-extrabold">Buttons</h3>
        <Button variant="outline" size="sm" className="rounded-sm" onClick={addButton} disabled={(draft.buttons || []).length >= 3}>
          <Plus className="h-4 w-4" /> Add Button
        </Button>
      </div>
      <div className="space-y-3">
        {(draft.buttons || []).map((button, index) => (
          <div key={`${button.type}-${index}`} className="grid gap-3 rounded-sm border border-[#E5E7EB] p-3 lg:grid-cols-[160px_1fr_1fr_auto]">
            <select className="h-10 rounded-md border border-[#E5E7EB] px-3 text-sm" value={button.type} onChange={(e) => updateButton(index, { type: e.target.value })}>
              <option value="PHONE_NUMBER">Call Now</option>
              <option value="URL">Visit Website</option>
              <option value="QUICK_REPLY">Quick Reply</option>
            </select>
            <Input placeholder="Button text" value={button.text} onChange={(e) => updateButton(index, { text: e.target.value })} />
            {button.type === "PHONE_NUMBER" ? (
              <Input placeholder="Phone number" value={button.phoneNumber || ""} onChange={(e) => updateButton(index, { phoneNumber: e.target.value })} />
            ) : button.type === "URL" ? (
              <Input placeholder="Website URL" value={button.url || ""} onChange={(e) => updateButton(index, { url: e.target.value })} />
            ) : (
              <Input value="Customer taps this quick reply" disabled />
            )}
            <button type="button" className="flex h-10 w-10 items-center justify-center rounded-sm border border-[#E5E7EB]" onClick={() => removeButton(index)} aria-label="Remove button">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {!(draft.buttons || []).length && <div className="rounded-sm bg-[#F9FAFB] p-3 text-sm text-[#6B7280]">No buttons added.</div>}
      </div>
    </div>
  );
}

function TemplateList({ templates, statusTone, normalizedStatus, editTemplate, duplicateTemplate, deleteTemplate, syncTemplateStatus }) {
  return (
    <div className="rounded-sm border border-[#E5E7EB] bg-white">
      <div className="border-b border-[#E5E7EB] p-4">
        <h3 className="font-display text-xl font-extrabold">Local and Meta Templates</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-[#F9FAFB] text-left text-xs uppercase tracking-[0.12em] text-[#6B7280]">
            <tr>
              <th className="px-4 py-3">Template</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Language</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Meta ID</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates.map((template) => (
              <tr key={template.id || `${template.templateName}-${template.languageCode}`} className="border-t border-[#E5E7EB]">
                <td className="px-4 py-3">
                  <button type="button" className="font-semibold text-[#111827] hover:underline" onClick={() => editTemplate(template)}>{template.templateName}</button>
                  {template.rejectionReason && <div className="mt-1 max-w-md text-xs text-red-700">{template.rejectionReason}</div>}
                </td>
                <td className="px-4 py-3">{template.category}</td>
                <td className="px-4 py-3">{template.languageCode}</td>
                <td className="px-4 py-3"><Pill tone={statusTone(template.status)}>{normalizedStatus(template.status)}</Pill></td>
                <td className="px-4 py-3 text-xs text-[#6B7280]">{template.metaTemplateId || "-"}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    {template.metaTemplateId && <Button variant="outline" size="sm" className="rounded-sm" onClick={() => syncTemplateStatus(template)}><Download className="h-4 w-4" /> Refresh</Button>}
                    <Button variant="outline" size="sm" className="rounded-sm" onClick={() => editTemplate(template)}><Edit className="h-4 w-4" /> Edit</Button>
                    <Button variant="outline" size="sm" className="rounded-sm" onClick={() => duplicateTemplate(template)}>Duplicate</Button>
                    <Button variant="outline" size="sm" className="rounded-sm text-red-600" onClick={() => deleteTemplate(template)}><Trash2 className="h-4 w-4" /> Delete</Button>
                  </div>
                </td>
              </tr>
            ))}
            {!templates.length && (
              <tr><td className="px-4 py-6 text-sm text-[#6B7280]" colSpan={6}>No templates found. Create one or sync from Meta.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TemplatePreview({ draft, templates, normalizedStatus }) {
  const previewBody = draft.bodyText || "Type the template body. Use variables like {{1}} for customer name or offer.";
  return (
    <aside className="space-y-4">
      <div className="rounded-sm border border-[#E5E7EB] bg-[#ECE5DD] p-4">
        <h3 className="font-display mb-3 text-xl font-extrabold text-[#111827]">Template Preview</h3>
        <div className="rounded-lg bg-white p-3 shadow-sm">
          {draft.headerType === "Text" && draft.headerText && <div className="mb-2 font-semibold text-[#111827]">{draft.headerText}</div>}
          {["Image", "Video", "Document"].includes(draft.headerType) && (
            draft.mediaUrl ? (
              <div className="mb-3 overflow-hidden rounded-sm bg-[#F3F4F6]">
                {draft.headerType === "Image" ? (
                  <img src={draft.mediaUrl} alt="Template header" className="h-36 w-full object-cover" />
                ) : draft.headerType === "Video" ? (
                  <video src={`${draft.mediaUrl}#t=0.1`} className="h-36 w-full object-cover" muted playsInline preload="metadata" />
                ) : (
                  <div className="flex h-28 items-center justify-center gap-2 text-sm font-semibold text-[#374151]">
                    <FileText className="h-5 w-5" /> PDF header attached
                  </div>
                )}
              </div>
            ) : (
              <div className="mb-3 flex h-28 items-center justify-center rounded-sm bg-[#F3F4F6] text-sm font-semibold text-[#374151]">
                <FileText className="mr-2 h-5 w-5" /> {draft.headerType} header
              </div>
            )
          )}
          <div className="whitespace-pre-wrap text-sm text-[#111827]">{previewBody}</div>
          {draft.footerText && <div className="mt-3 text-xs text-[#6B7280]">{draft.footerText}</div>}
          {(draft.buttons || []).filter((button) => button.text).map((button, index) => (
            <div key={`${button.text}-${index}`} className="mt-2 rounded-sm border border-[#D1E7DD] bg-[#E9F7EF] px-3 py-2 text-center text-sm font-semibold text-[#128C7E]">
              {button.text}
            </div>
          ))}
        </div>
        <div className="mt-3 rounded-sm border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          Marketing templates must be approved by Meta and sent only to opted-in recipients.
        </div>
      </div>

      <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
        <h3 className="font-display mb-3 text-xl font-extrabold">Status Summary</h3>
        <div className="grid grid-cols-2 gap-2 text-center text-xs">
          {["APPROVED", "PENDING", "REJECTED", "PAUSED", "DISABLED", "DRAFT"].map((status) => (
            <div key={status} className="rounded-sm bg-[#F9FAFB] p-3">
              <div className="text-lg font-extrabold">{templates.filter((tpl) => normalizedStatus(tpl.status) === status).length}</div>
              <div className="text-[#6B7280]">{status}</div>
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
}

function CampaignBuilder({
  draft,
  setDraft,
  lists,
  templates,
  media,
  summary,
  uploadCampaignMedia,
  saveDraft,
  sendTest,
  scheduleCampaign,
  sendNow,
  markListOptIn,
  actionMessage,
  deliveryLogs,
  settings,
  campaigns,
  approveAndSend,
  editCampaign,
  deleteCampaign,
  deleteWhatsappMessage,
}) {
  const setVar = (key, value) => setDraft({ ...draft, templateVariables: { ...(draft.templateVariables || {}), [key]: value } });
  const variables = draft.templateVariables || {};
  const previewMessage = draft.caption || "Type your WhatsApp campaign message here. Example: Upgrade your restaurant with RP Digital Menu Board. Free demo available. Call 9225140406.";
  const selectedListIds = [...new Set([...(draft.contactListIds || []), draft.contactListId].filter(Boolean))];
  const selectedListName = summary.lists?.length ? summary.lists.map((list) => list.name).join(", ") : "No list selected";
  const isMarketing = draft.messageCategory === "Marketing";
  const approvedTemplates = templates.filter((tpl) => String(tpl.status || "").toUpperCase() === "APPROVED");
  const selectedTemplate = approvedTemplates.find((tpl) => tpl.templateName === draft.templateName);
  const reportTotals = campaigns.reduce((totals, campaign) => ({
    sent: totals.sent + Number(campaign.sentCount || 0),
    delivered: totals.delivered + Number(campaign.deliveredCount || 0),
    read: totals.read + Number(campaign.readCount || 0),
    failed: totals.failed + Number(campaign.failedCount || 0),
    replied: totals.replied + Number(campaign.repliedCount || 0),
    skipped: totals.skipped + Number(campaign.skippedCount || 0),
  }), { sent: 0, delivered: 0, read: 0, failed: 0, replied: 0, skipped: 0 });
  const handleTemplateChange = (templateName) => {
    const template = approvedTemplates.find((tpl) => tpl.templateName === templateName);
    const headerType = template?.headerType || "None";
    const firstButton = (template?.buttons || []).find((button) => button?.text);
    setDraft({
      ...draft,
      templateName,
      messageCategory: template?.category || draft.messageCategory || "Marketing",
      languageCode: template?.languageCode || draft.languageCode || "en_US",
      messageType: headerType !== "None" ? (headerType === "Document" ? "PDF" : headerType) : "Text",
      mediaUrl: template?.mediaUrl || "",
      caption: template?.bodyText || template?.bodyPreview || draft.caption || "",
      ctaButton: firstButton?.text || draft.ctaButton || "",
    });
  };
  const toggleCampaignList = (listId, checked) => {
    const nextIds = checked
      ? [...new Set([...selectedListIds, listId])]
      : selectedListIds.filter((id) => id !== listId);
    setDraft({ ...draft, contactListIds: nextIds, contactListId: nextIds[0] || "" });
  };

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
      <div className="space-y-5">
        <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
          <div className="mb-4">
            <h2 className="font-display text-2xl font-extrabold">WhatsApp Campaign Builder</h2>
            <p className="text-sm text-[#6B7280]">Create a message, choose an opted-in contact list, preview it, then save or send.</p>
          </div>
          {isMarketing && (
            <div className="mb-4 rounded-sm border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              WhatsApp marketing campaigns require approved Meta templates and customer opt-in.
            </div>
          )}

          <div className="grid gap-4 lg:grid-cols-2">
            <Field label="Campaign Name">
              <Input value={draft.campaignName} onChange={(e) => setDraft({ ...draft, campaignName: e.target.value })} placeholder="June restaurant digital menu offer" />
            </Field>
            <Field label="Recipient Lists">
              <div className="max-h-44 overflow-y-auto rounded-md border border-[#E5E7EB] bg-white p-2">
                {lists.map((list) => (
                  <label key={list.id} className="flex items-center justify-between gap-3 rounded-sm px-2 py-2 text-sm hover:bg-[#F9FAFB]">
                    <span className="flex min-w-0 items-center gap-2">
                      <input type="checkbox" checked={selectedListIds.includes(list.id)} onChange={(e) => toggleCampaignList(list.id, e.target.checked)} />
                      <span className="truncate font-semibold">{list.name}</span>
                    </span>
                    <span className="shrink-0 text-xs text-[#6B7280]">{list.leadCount || list.leadIds?.length || 0}</span>
                  </label>
                ))}
                {!lists.length && <div className="p-3 text-sm text-[#6B7280]">No contact lists yet.</div>}
              </div>
              <p className="mt-1 text-xs text-[#6B7280]">{selectedListIds.length} list(s) selected. Duplicate contacts are sent only once.</p>
            </Field>
            <Field label="Message Category">
              <select className="h-10 w-full rounded-md border border-[#E5E7EB] px-3 text-sm" value={draft.messageCategory} onChange={(e) => setDraft({ ...draft, messageCategory: e.target.value })}>
                <option>Marketing</option><option>Utility</option><option>Authentication</option><option>Service Reply</option>
              </select>
            </Field>
            <Field label="Message Type">
              <select className="h-10 w-full rounded-md border border-[#E5E7EB] px-3 text-sm" value={draft.messageType} onChange={(e) => setDraft({ ...draft, messageType: e.target.value })}>
                <option>Text</option><option>Image</option><option>Video</option><option>PDF</option><option>Catalogue</option>
              </select>
            </Field>
            <div className="lg:col-span-2">
              <Field label="WhatsApp Approved Template">
                <select className="h-10 w-full rounded-md border border-[#E5E7EB] px-3 text-sm" value={draft.templateName} onChange={(e) => handleTemplateChange(e.target.value)}>
                  <option value="">Select approved template</option>
                  {approvedTemplates.map((tpl) => <option key={tpl.id || tpl.templateName} value={tpl.templateName}>{tpl.templateName} ({tpl.languageCode})</option>)}
                </select>
              </Field>
              <p className="mt-1 text-xs text-[#6B7280]">Select the approved Meta WhatsApp template. Promotional messages require approved Marketing templates.</p>
              {selectedTemplate && <p className="mt-1 text-xs text-[#6B7280]">Selected template header: <b>{selectedTemplate.headerType || "None"}</b>. Message type must match this header.</p>}
              {selectedTemplate && ["Image", "Video", "Document"].includes(selectedTemplate.headerType || "") && !selectedTemplate.mediaUrl && (
                <p className="mt-1 text-xs text-amber-700">This template has a media header, but no local sample media URL is saved. Upload or choose media before sending.</p>
              )}
              {!approvedTemplates.length && <p className="mt-1 text-xs text-amber-700">No approved templates found. Open WhatsApp Templates and sync or submit a template.</p>}
            </div>
          </div>
        </div>

        <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
          <h3 className="font-display mb-4 text-xl font-extrabold">Template Variables</h3>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            <Input placeholder="Customer Name" value={variables.customerName || ""} onChange={(e) => setVar("customerName", e.target.value)} />
            <Input placeholder="Business Name" value={variables.businessName || ""} onChange={(e) => setVar("businessName", e.target.value)} />
            <Input placeholder="Product Name" value={variables.productName || ""} onChange={(e) => setVar("productName", e.target.value)} />
            <Input placeholder="Offer" value={variables.offer || ""} onChange={(e) => setVar("offer", e.target.value)} />
            <Input placeholder="Website Link" value={variables.websiteLink || ""} onChange={(e) => setVar("websiteLink", e.target.value)} />
            <Input placeholder="Phone Number" value={variables.phoneNumber || ""} onChange={(e) => setVar("phoneNumber", e.target.value)} />
          </div>
        </div>

        <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
          <h3 className="font-display mb-4 text-xl font-extrabold">Upload Media / Media Link</h3>
          <div className="grid gap-3 lg:grid-cols-3">
            <label className="flex h-11 cursor-pointer items-center justify-center gap-2 rounded-sm border border-[#E5E7EB] text-sm font-semibold">
              <Upload className="h-4 w-4" /> Upload Image
              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadCampaignMedia(e.target.files?.[0])} />
            </label>
            <label className="flex h-11 cursor-pointer items-center justify-center gap-2 rounded-sm border border-[#E5E7EB] text-sm font-semibold">
              <Upload className="h-4 w-4" /> Upload Video
              <input type="file" accept="video/*" className="hidden" onChange={(e) => uploadCampaignMedia(e.target.files?.[0])} />
            </label>
            <label className="flex h-11 cursor-pointer items-center justify-center gap-2 rounded-sm border border-[#E5E7EB] text-sm font-semibold">
              <Upload className="h-4 w-4" /> Upload PDF
              <input type="file" accept="application/pdf" className="hidden" onChange={(e) => uploadCampaignMedia(e.target.files?.[0])} />
            </label>
          </div>
          <div className="mt-3 grid gap-3 lg:grid-cols-[1fr_220px]">
            <Input placeholder="Or paste media URL" value={draft.mediaUrl} onChange={(e) => setDraft({ ...draft, mediaUrl: e.target.value })} />
            <select className="h-10 rounded-md border border-[#E5E7EB] px-3 text-sm" value={draft.mediaUrl} onChange={(e) => setDraft({ ...draft, mediaUrl: e.target.value })}>
              <option value="">Choose from library</option>
              {media.map((item) => <option key={item.id} value={item.url}>{item.name}</option>)}
            </select>
          </div>
        </div>

        <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
          <Field label="Message / Caption">
            <Textarea
              className="min-h-36"
              value={draft.caption}
              onChange={(e) => setDraft({ ...draft, caption: e.target.value })}
              placeholder="Type your WhatsApp campaign message here. Example: Upgrade your restaurant with RP Digital Menu Board. Free demo available. Call 9225140406."
            />
          </Field>
          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            <Field label="CTA Buttons">
              <select className="h-10 w-full rounded-md border border-[#E5E7EB] px-3 text-sm" value={draft.ctaButton} onChange={(e) => setDraft({ ...draft, ctaButton: e.target.value })}>
                <option>Call Now</option><option>Visit Website</option><option>Request Demo</option><option>Download Catalogue</option>
              </select>
            </Field>
            <Field label="Schedule">
              <select className="h-10 w-full rounded-md border border-[#E5E7EB] px-3 text-sm" value={draft.scheduleMode} onChange={(e) => setDraft({ ...draft, scheduleMode: e.target.value })}>
                <option>Send Now</option><option>One Time</option><option>Daily</option><option>Weekly</option><option>Monthly</option>
              </select>
            </Field>
            {draft.scheduleMode !== "Send Now" && (
              <Field label="Scheduled Date Time">
                <Input type="datetime-local" value={draft.scheduledDateTime || ""} onChange={(e) => setDraft({ ...draft, scheduledDateTime: e.target.value })} />
              </Field>
            )}
          </div>
        </div>

        <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
          <div className="grid gap-3 lg:grid-cols-[minmax(220px,320px)_1fr]">
            <Field label="Test WhatsApp Number">
              <Input value={draft.testNumber || ""} onChange={(e) => setDraft({ ...draft, testNumber: e.target.value })} placeholder="919225149406" />
            </Field>
            <div className="flex flex-wrap items-end gap-2">
              <Button variant="outline" className="rounded-sm" onClick={saveDraft}>Save Draft</Button>
              <Button variant="outline" className="rounded-sm" onClick={sendTest}>Send Test</Button>
              <Button variant="outline" className="rounded-sm" onClick={scheduleCampaign}>Schedule Campaign</Button>
              <Button className="rounded-sm" onClick={sendNow}><Send className="h-4 w-4" /> Send Now</Button>
            </div>
          </div>
          {actionMessage && (
            <div className="mt-3 rounded-sm border border-blue-100 bg-blue-50 p-3 text-sm font-semibold text-blue-800">
              {actionMessage}
            </div>
          )}
        </div>

        <DeliveryStatusPanel logs={deliveryLogs} settings={settings} deleteLog={deleteWhatsappMessage} />

        <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-display text-xl font-extrabold">Saved and Scheduled Campaigns</h3>
              <p className="text-sm text-[#6B7280]">Drafts, scheduled campaigns, and sent campaign history appear here.</p>
            </div>
            <Pill tone="blue">{campaigns.length} total</Pill>
          </div>
          {campaigns.length ? (
            <div className="grid gap-3 md:grid-cols-2">
              {campaigns.map((campaign) => (
                <CampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  lists={lists}
                  approveAndSend={approveAndSend}
                  editCampaign={editCampaign}
                  deleteCampaign={deleteCampaign}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-sm border border-dashed border-[#CBD5E1] p-5 text-sm text-[#6B7280]">
              No campaigns saved yet. Save a draft or schedule a campaign to see it here.
            </div>
          )}
        </div>
      </div>

      <aside className="space-y-4">
        <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
          <h3 className="font-display mb-3 text-xl font-extrabold">Recipient Summary</h3>
          <div className="space-y-2 text-sm">
            <SummaryRow label="Selected list" value={selectedListName} />
            <SummaryRow label="Total contacts" value={summary.total} />
            <SummaryRow label="Opted-in contacts" value={summary.optedIn} />
            <SummaryRow label="Without opt-in" value={summary.noConsent || 0} />
            <SummaryRow label="Skipped contacts" value={summary.skipped} />
            <SummaryRow label="Missing WhatsApp" value={summary.missingWhatsapp} />
            <SummaryRow label="Estimated send count" value={summary.estimated} strong />
          </div>
          {summary.total > 0 && (summary.noConsent || 0) > 0 && (
            <div className="mt-3 rounded-sm border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              {summary.noConsent} contact(s) have WhatsApp number but are not opted-in, so campaign send will skip them.
              <Button variant="outline" className="mt-3 w-full rounded-sm bg-white text-[#111827]" onClick={markListOptIn}>
                Mark this list opted-in
              </Button>
            </div>
          )}
        </div>

        <div className="rounded-sm border border-[#E5E7EB] bg-[#ECE5DD] p-4">
          <h3 className="font-display mb-3 text-xl font-extrabold text-[#111827]">Preview</h3>
          <div className="rounded-lg bg-white p-3 shadow-sm">
            <div className="mb-2 text-xs font-semibold text-[#128C7E]">{variables.businessName || "RP Signage"}</div>
            {draft.mediaUrl && (
              <div className="mb-3 overflow-hidden rounded-sm bg-[#F3F4F6]">
                {draft.messageType === "Image" ? (
                  <img src={draft.mediaUrl} alt="Campaign media preview" className="h-40 w-full object-cover" />
                ) : draft.messageType === "Video" ? (
                  <video src={`${draft.mediaUrl}#t=0.1`} className="h-40 w-full object-cover" muted playsInline preload="metadata" controls />
                ) : (
                  <div className="flex h-32 items-center justify-center gap-2 text-sm font-semibold text-[#374151]">
                    <FileText className="h-5 w-5" /> {draft.messageType} attached
                  </div>
                )}
              </div>
            )}
            <div className="whitespace-pre-wrap text-sm text-[#111827]">{previewMessage}</div>
            {draft.ctaButton && <div className="mt-3 rounded-sm border border-[#D1E7DD] bg-[#E9F7EF] px-3 py-2 text-center text-sm font-semibold text-[#128C7E]">{draft.ctaButton}</div>}
            <div className="mt-2 text-right text-[10px] text-[#6B7280]">Recipient: {variables.customerName || "Customer Name"}</div>
          </div>
        </div>

        <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
          <h3 className="font-display mb-3 text-xl font-extrabold">Reports</h3>
          <div className="grid grid-cols-2 gap-2 text-center text-xs">
            {[
              ["Sent", reportTotals.sent],
              ["Delivered", reportTotals.delivered],
              ["Read", reportTotals.read],
              ["Failed", reportTotals.failed],
              ["Replied", reportTotals.replied],
              ["Skipped", reportTotals.skipped],
            ].map(([label, value]) => (
              <div key={label} className="rounded-sm bg-[#F9FAFB] p-3">
                <div className="text-lg font-extrabold">{value}</div>
                <div className="text-[#6B7280]">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </aside>
    </div>
  );
}

function DeliveryStatusPanel({ logs = [], settings = {}, deleteLog }) {
  const recentLogs = logs.slice(0, 8);
  const totalStatuses = Number(settings.totalWebhookStatusCount || 0);
  const totalReplies = Number(settings.totalWebhookMessageCount || 0);
  const statusWebhookMissing = Number(settings.lastWebhookMessageCount || 0) > 0 && totalStatuses === 0;
  const verifyFailed = settings.lastWebhookVerifyAt && !settings.lastWebhookVerifyOk;
  const ageMinutes = (log) => {
    const date = new Date(log.updatedAt || log.createdAt || "");
    if (Number.isNaN(date.getTime())) return 0;
    return Math.floor((Date.now() - date.getTime()) / 60000);
  };
  const isStaleAccepted = (log) => String(log.status || "").toLowerCase() === "accepted" && ageMinutes(log) >= 2;
  const toneFor = (status) => {
    const value = String(status || "").toLowerCase();
    if (value === "delivered" || value === "read" || value === "replied") return "green";
    if (value === "failed") return "red";
    if (value === "sent" || value === "accepted") return "blue";
    return "amber";
  };
  return (
    <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-xl font-extrabold">WhatsApp Delivery Status</h3>
          <p className="text-sm text-[#6B7280]">Recent test and campaign messages. This refreshes every 15 seconds while you stay on Campaigns.</p>
        </div>
        <Pill tone="blue">{logs.length} logs</Pill>
      </div>
      <div className="mb-3 rounded-sm border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
        Accepted means Meta accepted the API request. Delivered and read will update only after Meta sends status webhook events. If you received the message but it remains accepted, the send is working and the status webhook subscription needs attention.
      </div>
      {!settings.lastWebhookAt && (
        <div className="mb-3 rounded-sm border border-red-200 bg-red-50 p-3 text-xs text-red-700">
          No webhook callback has reached RP Signage yet. In Meta Developers, verify the callback URL, use the same verify token saved in Marketing Settings, and subscribe the WhatsApp Business Account to the messages webhook field.
        </div>
      )}
      {statusWebhookMissing && (
        <div className="mb-3 rounded-sm border border-red-200 bg-red-50 p-3 text-xs text-red-700">
          RP Signage has received WhatsApp replies, but no delivery/read status events. In Meta Webhooks, keep the messages field subscribed for the same RP Infotech WABA and phone number. Delivery status cannot change from accepted until Meta sends those status callbacks.
        </div>
      )}
      {verifyFailed && (
        <div className="mb-3 rounded-sm border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          The last webhook verification attempt failed. Save the exact verify token in Marketing Settings, then use that same token in Meta. Old successful callbacks can still appear, but new verification must pass for a clean setup.
        </div>
      )}
      <div className="mb-3 grid gap-2 text-xs md:grid-cols-3">
        <div className="rounded-sm bg-[#F9FAFB] p-3">
          <div className="font-semibold text-[#111827]">Webhook URL</div>
          <div className="truncate text-[#6B7280]" title={settings.webhookUrl || ""}>{settings.webhookUrl || "Not configured"}</div>
        </div>
        <div className="rounded-sm bg-[#F9FAFB] p-3">
          <div className="font-semibold text-[#111827]">Last Callback</div>
          <div className="text-[#6B7280]">{settings.lastWebhookAt ? formatDateTime(settings.lastWebhookAt) : "No callback received"}</div>
        </div>
        <div className="rounded-sm bg-[#F9FAFB] p-3">
          <div className="font-semibold text-[#111827]">Last Events</div>
          <div className="text-[#6B7280]">{settings.lastWebhookStatusCount || 0} status / {settings.lastWebhookMessageCount || 0} replies</div>
          <div className="mt-1 text-[11px] text-[#6B7280]">Total: {totalStatuses} status / {totalReplies} replies</div>
        </div>
      </div>
      <div className="mb-3 grid gap-2 text-xs md:grid-cols-2">
        <div className="rounded-sm bg-[#F9FAFB] p-3">
          <div className="font-semibold text-[#111827]">Last Verify Attempt</div>
          <div className="text-[#6B7280]">{settings.lastWebhookVerifyAt ? formatDateTime(settings.lastWebhookVerifyAt) : "No verify attempt recorded"}</div>
        </div>
        <div className="rounded-sm bg-[#F9FAFB] p-3">
          <div className="font-semibold text-[#111827]">Verify Result</div>
          <div className={settings.lastWebhookVerifyOk ? "text-emerald-700" : "text-red-700"}>{settings.lastWebhookVerifyOk ? "Verified" : "Not verified"}</div>
        </div>
      </div>
      {recentLogs.length ? (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#F9FAFB] text-left text-[11px] uppercase tracking-[0.16em] text-[#6B7280]">
              <tr>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Phone</th>
                <th className="px-3 py-2">Template</th>
                <th className="px-3 py-2">Meta ID</th>
                <th className="px-3 py-2">Reason</th>
                <th className="px-3 py-2">Time</th>
                {deleteLog && <th className="px-3 py-2 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {recentLogs.map((log) => (
                <tr key={log.id || log.providerMessageId} className="border-t border-[#E5E7EB]">
                  <td className="px-3 py-2">
                    <div className="flex flex-col gap-1">
                      <Pill tone={isStaleAccepted(log) ? "amber" : toneFor(log.status)}>{log.status || "accepted"}</Pill>
                      {isStaleAccepted(log) && <span className="text-[11px] font-semibold text-amber-700">Waiting {ageMinutes(log)} min</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2 font-semibold">{log.phone || log.phone_key || "-"}</td>
                  <td className="px-3 py-2">{log.templateName || "-"}</td>
                  <td className="max-w-[260px] truncate px-3 py-2 text-xs text-[#6B7280]" title={log.providerMessageId || ""}>{log.providerMessageId || "-"}</td>
                  <td className="max-w-[220px] truncate px-3 py-2 text-xs text-[#6B7280]" title={log.failureReason || ""}>{log.failureReason || "-"}</td>
                  <td className="px-3 py-2 text-xs text-[#6B7280]">{formatDateTime(log.updatedAt || log.createdAt)}</td>
                  {deleteLog && (
                    <td className="px-3 py-2 text-right">
                      <button type="button" className="inline-flex h-8 w-8 items-center justify-center rounded-sm border border-[#E5E7EB] text-red-600" onClick={() => deleteLog(log)} aria-label="Delete delivery status">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rounded-sm border border-dashed border-[#CBD5E1] p-4 text-sm text-[#6B7280]">
          No WhatsApp delivery logs yet. Send a test message to create the first log.
        </div>
      )}
    </div>
  );
}

function SummaryRow({ label, value, strong = false }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-[#F3F4F6] pb-2 last:border-0">
      <span className="text-[#6B7280]">{label}</span>
      <span className={strong ? "font-extrabold text-[#111827]" : "font-semibold text-[#111827]"}>{value ?? 0}</span>
    </div>
  );
}

function CampaignCard({ campaign, lists, approveAndSend, editCampaign, deleteCampaign }) {
  const tone = campaign.status === "Sent" ? "green" : campaign.status === "Failed" ? "red" : campaign.status === "Approved" ? "blue" : campaign.status === "Skipped" ? "amber" : "amber";
  const listIds = [...new Set([...(campaign.contactListIds || []), campaign.contactListId].filter(Boolean))];
  const selectedLists = (lists || []).filter((item) => listIds.includes(item.id));
  const listLabel = selectedLists.length ? selectedLists.map((item) => item.name).join(", ") : campaign.contactListId || "-";
  const scheduledText = campaign.scheduledDateTime ? formatDateTime(campaign.scheduledDateTime) : "-";
  return (
    <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-[#111827]">{campaign.campaignName}</h3>
          <div className="text-xs text-[#6B7280]">{campaign.templateName || "Template required"} / {campaign.languageCode || "en_US"}</div>
        </div>
        <Pill tone={tone}>{campaign.status}</Pill>
      </div>
      <div className="mb-3 grid gap-2 text-xs text-[#374151]">
        <div className="flex justify-between gap-3 border-b border-[#F3F4F6] pb-1">
          <span className="text-[#6B7280]">Recipient lists</span>
          <span className="text-right font-semibold">{listLabel}</span>
        </div>
        <div className="flex justify-between gap-3 border-b border-[#F3F4F6] pb-1">
          <span className="text-[#6B7280]">Schedule</span>
          <span className="text-right font-semibold">{campaign.scheduleMode || campaign.frequency || "One time"}</span>
        </div>
        <div className="flex justify-between gap-3 border-b border-[#F3F4F6] pb-1">
          <span className="text-[#6B7280]">Scheduled time</span>
          <span className="text-right font-semibold">{scheduledText}</span>
        </div>
        <div className="flex justify-between gap-3">
          <span className="text-[#6B7280]">Created</span>
          <span className="text-right font-semibold">{formatDateTime(campaign.createdAt)}</span>
        </div>
      </div>
      <div className="mb-3 flex flex-wrap gap-2">
        <Pill>{campaign.messageCategory || "Marketing"}</Pill>
        <Pill>{campaign.messageType || "Text"}</Pill>
        <Pill>{campaign.frequency || "One time"}</Pill>
      </div>
      <div className="mb-3 grid grid-cols-4 gap-2 text-center text-xs">
        <div className="rounded-sm bg-[#F9FAFB] p-2"><b>{campaign.sentCount || 0}</b><br />Sent</div>
        <div className="rounded-sm bg-[#F9FAFB] p-2"><b>{campaign.deliveredCount || 0}</b><br />Deliv.</div>
        <div className="rounded-sm bg-[#F9FAFB] p-2"><b>{campaign.readCount || 0}</b><br />Read</div>
        <div className="rounded-sm bg-[#F9FAFB] p-2"><b>{campaign.failedCount || 0}</b><br />Failed</div>
      </div>
      {!!campaign.skippedCount && (
        <div className="mb-3 rounded-sm border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
          Skipped {campaign.skippedCount} contact(s). {campaign.skippedNoConsentCount ? `${campaign.skippedNoConsentCount} without opt-in. ` : ""}{campaign.missingWhatsappCount ? `${campaign.missingWhatsappCount} missing WhatsApp number.` : ""}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" className="rounded-sm" onClick={() => approveAndSend(campaign.id, false)}><CheckCircle2 className="h-4 w-4" /> Approve</Button>
        <Button className="rounded-sm" onClick={() => approveAndSend(campaign.id, true)}><ChevronRight className="h-4 w-4" /> Send</Button>
        {editCampaign && <Button variant="outline" className="rounded-sm" onClick={() => editCampaign(campaign)}><Edit className="h-4 w-4" /> Edit</Button>}
        {deleteCampaign && <Button variant="outline" className="rounded-sm text-red-600" onClick={() => deleteCampaign(campaign)}><Trash2 className="h-4 w-4" /> Delete</Button>}
      </div>
    </div>
  );
}
